var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useContext, useEffect, useLayoutEffect, useMemo, useRef, useState, forwardRef, useImperativeHandle, } from 'react';
import { createPortal } from 'react-dom';
import '../styles/mathEditor.scss';
import { MathfieldElement } from 'mathlive';
import 'mathlive';
// Import memory optimization helpers
import { batchDisposeMathfields, scheduleSuggestGarbageCollection, ensureAriaLiveText, } from '../helper/mathliveOptimizations';
import { selectTool } from '../helper/helper';
import { announce, announceAssertive } from '../helper/announcer';
import { DataContext } from '../player/DataContext';
import customEventHandler from '../helper/customEvents';
import { ColorEnum } from './ColorPicker';
import ButtonComponent from './ButtonComponent';
import { saveState, consumeSkipNextFocusOutSave, } from '../helper/stateManagerHelper';
import { countVisibleCharsInLatex } from '../helper/latexCharCount';
import { collapseCaretAtPoint, isMathSelectAll, isPointOverMathField, isTapWithinSlop, hasPlaceholder, shouldArmLongPressWordSelect, } from '../helper/mathCaretHelper';
import { createWindowScrollHold } from '../helper/scrollHoldHelper';
import globalStore from '../store';
import { t } from 'i18next';
import { browserDetect } from '../helper/browserDetect';
import { nextFontSizeStep } from '../helper/fontSizeSteps';
import { LATEX_SIZE_COMMANDS, hasLatexStyle, stepLatexSize, toggleLatexStyle, unwrapLatexCommand, wrapLatexCommand, } from '../helper/mathLatexStyle';
import { allVisibleTextHasAncestor, boxHasNoVisibleText, isUsableSelection, } from '../helper/boxTextStyle';
var WHITESPACE_CHAR = '\u200B';
// MathLive's .ML__content (the shadow-DOM content wrapper) defaults to
// `overflow: hidden`, which clips a tall fraction's empty \placeholder{} box
// — its top extends a few px above the vertically-centered content box and
// gets cut (a filled digit sits lower, so it renders fine). Forcing it visible
// removes the clip so the placeholder shows fully.
// Trade-off: .ML__content's overflow:hidden also contains content wider/taller
// than the field, so `visible` lets a long expression paint outside the field
// box. Acceptable here because these editors hold short labels/cells; revisit
// if long expressions in narrow boxes start spilling.
var PLACEHOLDER_FIX_CSS = ' .ML__content { overflow: visible !important; }';
// ─── Window-scroll lock (singleton) ──────────────────────────────────────────
// iOS scrolls the whole document to keep a focused math-field visible. This app
// scrolls via an inner container, so that window scroll is an unwanted "jump".
// This lock pins window scroll to its saved position.
//
// MUST be a module-level singleton (not per-math-field): a per-field listener
// leaks when MathLive recreates the field without a blur, leaving multiple
// listeners with different saved positions that fight forever (window bounces
// 0 ↔ N on every frame — the iOS-26 typing-jump infinite loop). One listener
// can't fight itself (the `restoring` + already-at-target guards stop re-entry).
//
// Removal is debounced so the lock SURVIVES the keypad blur→refocus churn
// (tapping a keypad button blurs the math-field for a moment). Without this the
// lock is torn down mid-edit and reinstalled AFTER the jump, capturing the
// jumped position instead of the original.
var _winLockListener = null;
var _winLockPos = { x: 0, y: 0 };
var _winLockRestoring = false;
var _winLockRemoveTimer = null;
function installWinLock(savedX, savedY) {
    if (_winLockRemoveTimer) {
        clearTimeout(_winLockRemoveTimer);
        _winLockRemoveTimer = null;
    }
    // Already active → keep the original (pre-jump) saved position; don't recapture.
    if (_winLockListener)
        return;
    _winLockPos = { x: savedX, y: savedY };
    // Singleton scroll-restore for iOS 26 focus/input scroll. MUST stay a
    // singleton (one listener, debounced removal) — a per-field listener leaks
    // across MathLive field recreation and multiple locks fight each other
    // (window bounces 0↔N forever). Combined with holdWindowScrollBriefly() this
    // reduces the typing window-scroll to a non-visible 1-frame blip.
    // (Tried & rejected: overflow:hidden and position:fixed on <body> — iOS still
    // scrolled the document; position:fixed also risks layout breakage.)
    _winLockListener = function () {
        if (_winLockRestoring)
            return;
        if (window.scrollX === _winLockPos.x && window.scrollY === _winLockPos.y)
            return;
        _winLockRestoring = true;
        window.scrollTo(_winLockPos.x, _winLockPos.y);
        requestAnimationFrame(function () {
            _winLockRestoring = false;
        });
    };
    window.addEventListener('scroll', _winLockListener, { passive: true, capture: true });
}
function removeWinLock() {
    // Debounce: a keypad tap briefly blurs the math-field, then refocuses it.
    // Defer removal so a refocus within the window cancels it and the lock
    // stays put through the whole editing session.
    if (_winLockRemoveTimer)
        return;
    _winLockRemoveTimer = setTimeout(function () {
        _winLockRemoveTimer = null;
        if (_winLockListener) {
            window.removeEventListener('scroll', _winLockListener, { capture: true });
            _winLockListener = null;
        }
    }, 450);
}
// Proactively hold the window scroll for a short burst of animation frames.
// iOS 26 scrolls the *document* on each input (caret scroll-into-view,
// miscalculated under the canvas CSS transform). The scroll-lock listener only
// reacts to the `scroll` event, which iOS fires AFTER the frame paints — so the
// jump is briefly visible. rAF runs BEFORE paint, so re-asserting the saved
// position every frame reverts iOS's scroll before it's shown. Called from the
// input/keystroke handlers. Captures the position at call time (window is at its
// resting position then) and pins it for `frames` frames.
//
// A re-entrant call while a hold is already running KEEPS the original saved
// position and only restarts the frame counter — same contract as installWinLock
// ("already active → keep the original pre-jump position; don't recapture").
// This is not cosmetic: `preserveScroll()` arms a hold and then runs
// `mf.insert()`, which dispatches `input` SYNCHRONOUSLY, and the math-field's
// input listener calls this function again. Recapturing there would pin whatever
// position iOS had already scrolled to inside insert() — turning the hold from a
// jump-preventer into a jump-holder for the next ~24 frames. Rapid typing had
// the same flaw before the keypad path existed.
//
// The body lives in helper/scrollHoldHelper.ts so the re-entrancy contract is
// unit testable; this stays the module-level singleton instance it always was.
var holdWindowScrollBriefly = createWindowScrollHold({
    get scrollX() {
        return window.scrollX;
    },
    get scrollY() {
        return window.scrollY;
    },
    scrollTo: function (x, y) { return window.scrollTo(x, y); },
    requestAnimationFrame: function (cb) { return requestAnimationFrame(cb); },
    cancelAnimationFrame: function (h) { return cancelAnimationFrame(h); },
});
// Container-scroll lock — the iOS 26 counterpart of installWinLock for the
// *scroll container* rather than the window. On iPad iOS 26.5, typing into a
// focused math-field makes Safari scroll the nearest scrollable ancestor
// (`.customScrollbarContent`, the whiteboard canvas) to bring the caret into
// view — measured at dScrollTop=-364 / dScrollLeft=-565, which flings the
// editor off-screen. window.scrollY stays 0, so installWinLock never sees it;
// this pins the *element's* scrollTop/scrollLeft instead. Same singleton +
// debounced-removal contract (one listener; a keypad-tap blur/refocus must not
// tear it down and recapture the jumped position). Works for every context
// (bare canvas editor, JumpBar label, Table cell) because it locks whichever
// `.customScrollbarContent` is the editor's nearest scrollable ancestor.
var _elLockListener = null;
var _elLockEl = null;
var _elLockPos = { top: 0, left: 0 };
var _elLockRestoring = false;
var _elLockRemoveTimer = null;
// Deliberate-scroll escapes. The lock only exists to swallow iOS's *involuntary*
// caret scroll-into-view while a math-field is being edited. It used to swallow
// everything until blur, which broke two things a user actually asked for:
//   • wheel / touch-scrolling the canvas mid-edit was reverted to 0 px;
//   • clicking a second table cell scrolled 0 px, so the cell stayed off-screen
//     — the cell's own scrollIntoViewOnFocus landed inside the 450 ms debounced
//     teardown window and was undone.
// Both are answered by tearing the lock down immediately on a gesture or a
// pointerdown outside the editor context, rather than by weakening the lock.
var _elLockEscapes = null;
function tearDownElLock() {
    if (_elLockRemoveTimer) {
        clearTimeout(_elLockRemoveTimer);
        _elLockRemoveTimer = null;
    }
    if (_elLockListener && _elLockEl) {
        _elLockEl.removeEventListener('scroll', _elLockListener, { capture: true });
    }
    _elLockEscapes === null || _elLockEscapes === void 0 ? void 0 : _elLockEscapes();
    _elLockEscapes = null;
    _elLockListener = null;
    _elLockEl = null;
}
function installElLock(el) {
    if (!el)
        return;
    if (_elLockRemoveTimer) {
        clearTimeout(_elLockRemoveTimer);
        _elLockRemoveTimer = null;
    }
    // Already active → keep the original (pre-jump) saved position; don't recapture.
    if (_elLockListener)
        return;
    _elLockEl = el;
    _elLockPos = { top: el.scrollTop, left: el.scrollLeft };
    _elLockListener = function () {
        if (_elLockRestoring || !_elLockEl)
            return;
        if (_elLockEl.scrollTop === _elLockPos.top && _elLockEl.scrollLeft === _elLockPos.left)
            return;
        _elLockRestoring = true;
        _elLockEl.scrollTop = _elLockPos.top;
        _elLockEl.scrollLeft = _elLockPos.left;
        requestAnimationFrame(function () {
            _elLockRestoring = false;
        });
    };
    el.addEventListener('scroll', _elLockListener, { passive: true, capture: true });
    // A wheel or a touch drag on the locked container is unambiguously the user
    // asking to scroll; a pointerdown outside the current editing context (another
    // table cell, another text box, the canvas) means focus is about to move and
    // whatever scroll follows belongs to the new target. Release in both cases so
    // the requested scroll sticks. Taps on the math-field itself and on the keypad
    // are excluded — those are part of the same editing session, and releasing
    // there would reintroduce the iOS caret jump the lock is here to prevent.
    var onGesture = function () { return tearDownElLock(); };
    var onPointerDownOutside = function (ev) {
        var _a;
        var target = ev.target;
        if (!target)
            return;
        if ((_a = target.closest) === null || _a === void 0 ? void 0 : _a.call(target, 'math-field, .keypadWrapper'))
            return;
        tearDownElLock();
    };
    el.addEventListener('wheel', onGesture, { passive: true });
    el.addEventListener('touchmove', onGesture, { passive: true });
    document.addEventListener('pointerdown', onPointerDownOutside, true);
    _elLockEscapes = function () {
        el.removeEventListener('wheel', onGesture);
        el.removeEventListener('touchmove', onGesture);
        document.removeEventListener('pointerdown', onPointerDownOutside, true);
    };
}
function removeElLock() {
    if (_elLockRemoveTimer)
        return;
    _elLockRemoveTimer = setTimeout(function () {
        _elLockRemoveTimer = null;
        tearDownElLock();
    }, 450);
}
// The scroll-lock/hold pair above exists only to work around iOS 26 Safari's
// own caret-scroll-into-view bug (see comments above) — it was never meant to
// apply to desktop/Android, but nothing gated it, so focusing any math-field
// silently blocked all canvas scrolling everywhere. Gate every install site on
// this check. `isIpadOS()` covers modern iPadOS Safari, which reports a
// desktop "Macintosh" UA and no longer exposes `window.orientation`, so
// `platformIOS()` alone would miss it.
var needsIOSScrollLock = function () { return browserDetect.platformIOS() || browserDetect.isIpadOS(); };
// How long a structural keypad insert suppresses the focus-time
// scrollIntoView (see insertScrollGuardRef). Must outlast preserveScroll's own
// 500ms restore timeout — otherwise the scroll fires in the gap between the
// hold expiring and the position being restored, which is the jump itself.
var INSERT_SCROLL_GUARD_MS = 600;
// Extra delay on the deferred retry of a guard-suppressed scroll, so the retry
// lands strictly AFTER the guard lapses rather than racing its boundary.
var INSERT_SCROLL_RETRY_MARGIN_MS = 50;
// rAF-before-paint counterpart of holdWindowScrollBriefly for the container.
// iOS fires the container `scroll` event AFTER the frame paints, so the lock
// listener alone leaves a visible 1-frame fling; re-asserting the saved
// scrollTop/Left every frame for a short burst reverts it before paint.
var _elHoldRaf = 0;
function holdElScrollBriefly(el, frames) {
    if (frames === void 0) { frames = 24; }
    if (!el)
        return;
    var top = el.scrollTop;
    var left = el.scrollLeft;
    if (_elHoldRaf)
        cancelAnimationFrame(_elHoldRaf);
    var n = 0;
    var tick = function () {
        if (el.scrollTop !== top || el.scrollLeft !== left) {
            el.scrollTop = top;
            el.scrollLeft = left;
        }
        n += 1;
        _elHoldRaf = n < frames ? requestAnimationFrame(tick) : 0;
    };
    _elHoldRaf = requestAnimationFrame(tick);
}
// Accessible key metadata keyed by the keypad item's `id`, shared by BOTH
// halves of the keypad: the main grid and the `bottomBtns` strip (space,
// backspace). It used to live inside the grid's render IIFE, so the
// bottomBtns strip had no access to it and fell back to `item.ariaLabel`,
// which btnData.json does not set for those two keys. ButtonComponent then
// used `item.title` as the accessible name — and their titles are the raw
// i18n keys `text.spaceBar` / `text.back`, so a screen reader announced the
// literal string "text.spaceBar" while the other 30 keys were localized.
var KEYPAD_KP = 'text.mathEditor.keys.';
var KEYPAD_KEY_META = {
    zero: { labelKey: "".concat(KEYPAD_KP, "digit0"), cat: 0 },
    one: { labelKey: "".concat(KEYPAD_KP, "digit1"), cat: 0 },
    two: { labelKey: "".concat(KEYPAD_KP, "digit2"), cat: 0 },
    three: { labelKey: "".concat(KEYPAD_KP, "digit3"), cat: 0 },
    four: { labelKey: "".concat(KEYPAD_KP, "digit4"), cat: 0 },
    five: { labelKey: "".concat(KEYPAD_KP, "digit5"), cat: 0 },
    six: { labelKey: "".concat(KEYPAD_KP, "digit6"), cat: 0 },
    seven: { labelKey: "".concat(KEYPAD_KP, "digit7"), cat: 0 },
    eight: { labelKey: "".concat(KEYPAD_KP, "digit8"), cat: 0 },
    nine: { labelKey: "".concat(KEYPAD_KP, "digit9"), cat: 0 },
    plus: { labelKey: "".concat(KEYPAD_KP, "plus"), cat: 1 },
    minus: { labelKey: "".concat(KEYPAD_KP, "minus"), cat: 1 },
    multiply: { labelKey: "".concat(KEYPAD_KP, "multiply"), cat: 1 },
    divideSign: { labelKey: "".concat(KEYPAD_KP, "divide"), cat: 1 },
    equals: { labelKey: "".concat(KEYPAD_KP, "equals"), cat: 1 },
    lessThan: { labelKey: "".concat(KEYPAD_KP, "lessThan"), cat: 1 },
    greaterThan: { labelKey: "".concat(KEYPAD_KP, "greaterThan"), cat: 1 },
    lessThanOrEqual: { labelKey: "".concat(KEYPAD_KP, "lessThanOrEqual"), cat: 1 },
    greaterThanOrEqual: { labelKey: "".concat(KEYPAD_KP, "greaterThanOrEqual"), cat: 1 },
    percent: { labelKey: "".concat(KEYPAD_KP, "percent"), cat: 1 },
    comma: { labelKey: "".concat(KEYPAD_KP, "comma"), cat: 1 },
    dot: { labelKey: "".concat(KEYPAD_KP, "decimalPoint"), cat: 1 },
    dollar: { labelKey: "".concat(KEYPAD_KP, "dollar"), cat: 1 },
    cent: { labelKey: "".concat(KEYPAD_KP, "cent"), cat: 1 },
    leftParen: { labelKey: "".concat(KEYPAD_KP, "leftParen"), cat: 2 },
    rightParen: { labelKey: "".concat(KEYPAD_KP, "rightParen"), cat: 2 },
    slash: { labelKey: "".concat(KEYPAD_KP, "insertFraction"), cat: 2, shortcut: 'Alt+F' },
    fraction: { labelKey: "".concat(KEYPAD_KP, "insertFraction"), cat: 2, shortcut: 'Alt+F' },
    power: { labelKey: "".concat(KEYPAD_KP, "insertExponent"), cat: 2, shortcut: 'Alt+E' },
    exponent: { labelKey: "".concat(KEYPAD_KP, "insertExponent"), cat: 2, shortcut: 'Alt+E' },
    sqrt: { labelKey: "".concat(KEYPAD_KP, "insertSquareRoot"), cat: 2, shortcut: 'Alt+R' },
    squareRoot: { labelKey: "".concat(KEYPAD_KP, "insertSquareRoot"), cat: 2, shortcut: 'Alt+R' },
    questionMark: { labelKey: "".concat(KEYPAD_KP, "questionMark"), cat: 2 },
    clear: { labelKey: "".concat(KEYPAD_KP, "clear"), cat: 3 },
    spaceBar: { labelKey: "".concat(KEYPAD_KP, "space"), cat: 3, shortcut: 'Space' },
    back: { labelKey: "".concat(KEYPAD_KP, "backspace"), cat: 3, shortcut: 'Backspace' },
};
// Every focusable key in the keypad, in DOM order (grid first, then the
// bottomBtns strip). Used by the roving-tabindex arrow navigation.
var keypadKeyButtons = function (wrapper) {
    return wrapper
        ? Array.from(wrapper.querySelectorAll('button:not([disabled]):not([inert])'))
        : [];
};
// Columns per half of the keypad grid. The grid is 10 columns wide and DOM
// order sweeps the left half (cols 1-5) top-to-bottom before the right half,
// so stepping by 5 in DOM order moves exactly one visual row within a half —
// which is what ArrowUp/ArrowDown should do.
var KEYPAD_HALF_COLS = 5;
/**
 * Safely focus a MathfieldElement, catching MathLive internal errors.
 * Firefox: MathLive's onBlur handler crashes accessing ariaLiveText.textContent
 * when ariaLiveText hasn't been initialized in the shadow DOM yet.
 */
var safeFocusMathField = function (el) {
    try {
        if (el.isConnected) {
            // Ensure ariaLiveText exists before focus triggers blur/focus cycle
            if (el.tagName === 'MATH-FIELD') {
                ensureAriaLiveText(el);
            }
            el.focus({ preventScroll: true });
        }
    }
    catch (_) {
        // MathLive internal error (e.g., ariaLiveText undefined in Firefox)
    }
};
/**
 * Walk backwards from the current cursor position to find a math-field element.
 * Handles cases where spacer text nodes (WHITESPACE_CHAR) sit between the
 * cursor and the math-field. On iPad the cursor often lands inside these
 * spacers, so we need to look past them.
 */
var findMathFieldBeforeCursor = function () {
    var _a, _b, _c;
    var sel = window.getSelection();
    if (!sel || !sel.isCollapsed || sel.rangeCount === 0)
        return null;
    var range = sel.getRangeAt(0);
    var startContainer = range.startContainer, startOffset = range.startOffset;
    // Case 1: cursor is at the editor (element) level — check child before offset
    if (startContainer.nodeType === Node.ELEMENT_NODE) {
        var node = startContainer.childNodes[startOffset - 1];
        if ((node === null || node === void 0 ? void 0 : node.nodeName) === 'MATH-FIELD')
            return node;
        // If the node before is a spacer text node, look one further back
        if ((node === null || node === void 0 ? void 0 : node.nodeType) === Node.TEXT_NODE) {
            var prev = node.previousSibling;
            if ((prev === null || prev === void 0 ? void 0 : prev.nodeName) === 'MATH-FIELD')
                return prev;
        }
        return null;
    }
    // Case 2: cursor is inside a text node
    if (startContainer.nodeType === Node.TEXT_NODE) {
        var textNode = startContainer;
        var textBefore = (_b = (_a = textNode.textContent) === null || _a === void 0 ? void 0 : _a.slice(0, startOffset)) !== null && _b !== void 0 ? _b : '';
        // Only look past the text node if all characters before the cursor are
        // whitespace / zero-width spaces (i.e. cursor is effectively at the
        // boundary of a spacer node, not in the middle of real content)
        var isOnlySpacers = textBefore.replace(/[\u200B\s]/g, '').length === 0;
        if (startOffset === 0 || isOnlySpacers) {
            // Walk backwards through previous siblings, skipping spacer text nodes
            var prev = textNode.previousSibling;
            // If cursor is at offset 0, also check the current node's previous sibling
            // If cursor is inside a spacer, start from currentNode.previousSibling
            while (prev) {
                if (prev.nodeName === 'MATH-FIELD')
                    return prev;
                // Skip over whitespace/spacer text nodes
                if (prev.nodeType === Node.TEXT_NODE &&
                    ((_c = prev.textContent) !== null && _c !== void 0 ? _c : '').replace(/[\u200B\s]/g, '').length === 0) {
                    prev = prev.previousSibling;
                    continue;
                }
                break; // hit real content — stop looking
            }
        }
    }
    return null;
};
/**
 * Walk forward from the current cursor position to find a math-field element.
 * Symmetric counterpart to findMathFieldBeforeCursor — used so ArrowRight at
 * a position immediately to the LEFT of a math-field enters it at offset 0.
 */
var findMathFieldAfterCursor = function () {
    var _a, _b, _c;
    var sel = window.getSelection();
    if (!sel || !sel.isCollapsed || sel.rangeCount === 0)
        return null;
    var range = sel.getRangeAt(0);
    var startContainer = range.startContainer, startOffset = range.startOffset;
    // Case 1: cursor at element level — check the child at this offset
    if (startContainer.nodeType === Node.ELEMENT_NODE) {
        var node = startContainer.childNodes[startOffset];
        if ((node === null || node === void 0 ? void 0 : node.nodeName) === 'MATH-FIELD')
            return node;
        if ((node === null || node === void 0 ? void 0 : node.nodeType) === Node.TEXT_NODE) {
            var next = node.nextSibling;
            if ((next === null || next === void 0 ? void 0 : next.nodeName) === 'MATH-FIELD')
                return next;
        }
        return null;
    }
    // Case 2: cursor inside a text node — only walk forward if the chars
    // after the cursor are all whitespace / spacer (cursor is effectively
    // at the boundary, not in the middle of real content)
    if (startContainer.nodeType === Node.TEXT_NODE) {
        var textNode = startContainer;
        var textAfter = (_b = (_a = textNode.textContent) === null || _a === void 0 ? void 0 : _a.slice(startOffset)) !== null && _b !== void 0 ? _b : '';
        var isOnlySpacers = textAfter.replace(/[​\s]/g, '').length === 0;
        if (startOffset === textNode.length || isOnlySpacers) {
            var next = textNode.nextSibling;
            while (next) {
                if (next.nodeName === 'MATH-FIELD')
                    return next;
                if (next.nodeType === Node.TEXT_NODE &&
                    ((_c = next.textContent) !== null && _c !== void 0 ? _c : '').replace(/[​\s]/g, '').length === 0) {
                    next = next.nextSibling;
                    continue;
                }
                break;
            }
        }
    }
    return null;
};
/**
 * Parses a MathLive LaTeX string and returns whether it represents a single
 * valid number (integer, fraction, or mixed number) and its value.
 *
 * Examples:
 *   "3"              → { isValid: true,  value: 3 }
 *   "\frac{2}{3}"    → { isValid: true,  value: { num: 2, deno: 3 } }
 *   "1\frac{2}{3}"   → { isValid: true,  value: { num: 5, deno: 3 } }
 *   "-2\frac{1}{4}"  → { isValid: true,  value: { num: -9, deno: 4 } }
 *   "x+1"            → { isValid: false, value: null }
 */
/**
 * Convert a plain-text equation string (legacy "equation" type) to LaTeX.
 * Replaces `a/b` (integers or decimals) with `\frac{a}{b}`.
 * Example: "30/60 + 15/60 =" → "\frac{30}{60} + \frac{15}{60} ="
 */
export var equationStringToLatex = function (text) {
    return text.replace(/(\d+(?:\.\d+)?)\s*\/\s*(\d+(?:\.\d+)?)/g, '\\frac{$1}{$2}');
};
// MathLive renders `\placeholder{}` as a ▢ box, but only inside a real
// math-field. When a standalone placeholder box leaks into a plain text / HTML
// node (e.g. a manipulative label where MathLive's insert dispatches raw LaTeX
// into the contenteditable), it shows the literal characters "\placeholder{}"
// instead. Normalising it to the same symbol MathLive uses (U+25A2 ▢) keeps
// the visible box without needing a math-field. Fractions are unaffected —
// their placeholder lives inside a `type:'math'` item rendered by MathLive.
var PLACEHOLDER_BOX_SYMBOL = '▢'; // ▢ — matches MathLive placeholderSymbol
var normalizePlaceholderText = function (s) {
    return s ? s.replace(/\\placeholder\{[^}]*\}/g, PLACEHOLDER_BOX_SYMBOL) : s;
};
export var parseNumericLatex = function (latex) {
    var s = latex.replace(/[\s\u200B\u200C\u200D\uFEFF]/g, '');
    // MathLive's `mf.value` wraps content in `$ ... $` math delimiters.
    // Strip leading/trailing `$` (and `$$`) so the regexes below can match.
    s = s.replace(/^\$+|\$+$/g, '');
    // Normalise the LaTeX shorthand `\fracND` (no braces around single-token
    // arguments) to the canonical `\frac{N}{D}` form. MathLive emits the
    // shorthand whenever an argument is a single token, e.g. `\frac{1}{2}`
    // is serialized as `\frac12`. Three variants must be handled:
    // both-shorthand `\fracAB`, num-only `\fracA{B}`, deno-only `\frac{A}B`.
    // Run repeatedly until stable so combos like `2\frac34` and
    // `\frac1{12}` resolve fully.
    var prev = '';
    while (prev !== s) {
        prev = s;
        s = s.replace(/\\frac([0-9])([0-9])/g, '\\frac{$1}{$2}');
        s = s.replace(/\\frac([0-9])\{/g, '\\frac{$1}{');
        s = s.replace(/\\frac(\{[^{}]*\})([0-9])/g, '\\frac$1{$2}');
    }
    // Integer: e.g. "3", "-5"
    if (/^-?\d+$/.test(s)) {
        return { isValid: true, value: parseInt(s, 10) };
    }
    // Pure fraction: e.g. "\frac{2}{3}", "-\frac{2}{3}"
    var fracMatch = s.match(/^(-?)\\frac\{(\d+)\}\{(\d+)\}$/);
    if (fracMatch) {
        var sign = fracMatch[1] === '-' ? -1 : 1;
        var num = parseInt(fracMatch[2], 10) * sign;
        var deno = parseInt(fracMatch[3], 10);
        if (deno === 0)
            return { isValid: false, value: null };
        return { isValid: true, value: { num: num, deno: deno } };
    }
    // Mixed number: e.g. "1\frac{2}{3}", "-2\frac{1}{4}"
    var mixedMatch = s.match(/^(-?)(\d+)\\frac\{(\d+)\}\{(\d+)\}$/);
    if (mixedMatch) {
        var sign = mixedMatch[1] === '-' ? -1 : 1;
        var whole = parseInt(mixedMatch[2], 10);
        var numer = parseInt(mixedMatch[3], 10);
        var deno = parseInt(mixedMatch[4], 10);
        if (deno === 0)
            return { isValid: false, value: null };
        var num = (whole * deno + numer) * sign;
        return { isValid: true, value: { num: num, deno: deno } };
    }
    return { isValid: false, value: null };
};
var MathEditor = forwardRef(function (props, ref) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t;
    var editorRef = useRef(null);
    var keypadWrapperRef = useRef(null);
    var _u = useState(false), mlReady = _u[0], setMlReady = _u[1];
    var _v = useState(false), mathKeyboardVisible = _v[0], setmathKeyboardVisible = _v[1];
    var keypadTriggerRef = useRef(null);
    var _w = useState({ top: 0, left: 0 }), keypadPos = _w[0], setKeypadPos = _w[1];
    // Roving tabindex for the keypad toolbar: the id of the one key that is in
    // the document Tab order. null → fall back to the first key, so the keypad is
    // always enterable even before the user has moved within it.
    var _x = useState(null), rovingKeyId = _x[0], setRovingKeyId = _x[1];
    var _y = globalStore.useStoreState(function (st) { return st.playerStore; }), canvasHeight = _y.canvasHeight, zoomLevel = _y.zoomLevel;
    var _z = useContext(DataContext), setText = _z.setText, selectedTool = _z.selectedTool, setSelectedTool = _z.setSelectedTool, color = _z.color, setContextMenu = _z.setContextMenu;
    var _0 = props.setActiveLink, setActiveLink = _0 === void 0 ? function () { } : _0, _1 = props.idPreffix, idPreffix = _1 === void 0 ? 'newMath' : _1, index = props.index, data = props.data, _2 = props.isTextUpdate, isTextUpdate = _2 === void 0 ? true : _2, cb = props.cb, keypadButtons = props.keypadButtons, bottomBtns = props.bottomBtns, _3 = props.viewonly, viewonly = _3 === void 0 ? false : _3, _4 = props.selectOnViewOnly, selectOnViewOnly = _4 === void 0 ? false : _4, className = props.className, _5 = props.disableTransform, disableTransform = _5 === void 0 ? false : _5, setActiveMathText = props.setActiveMathText, _6 = props.isManipulative, isManipulative = _6 === void 0 ? false : _6, _7 = props.style, style = _7 === void 0 ? {} : _7, _8 = props.throwChangeOnBlur, throwChangeOnBlur = _8 === void 0 ? false : _8, _9 = props.maxCharacters, maxCharacters = _9 === void 0 ? undefined : _9, colorProp = props.color, onChange = props.onChange, _10 = props.onFocusEvent, onFocusEvent = _10 === void 0 ? false : _10, _11 = props.placeHolder, placeHolder = _11 === void 0 ? 'Type' : _11, onKeyDown = props.onKeyDown, _12 = props.dataPlaceholder, dataPlaceholder = _12 === void 0 ? undefined : _12, _13 = props.offsetKeypadByEditorHeight, offsetKeypadByEditorHeight = _13 === void 0 ? false : _13, _14 = props.blockNewLine, blockNewLine = _14 === void 0 ? false : _14, onRemove = props.onRemove, onClickWhenLocked = props.onClickWhenLocked, _15 = props.scrollIntoViewOnFocus, scrollIntoViewOnFocus = _15 === void 0 ? false : _15, _16 = props.keepSelectedOnInternalBlur, keepSelectedOnInternalBlur = _16 === void 0 ? false : _16, _17 = props.enableFocusPanel, enableFocusPanel = _17 === void 0 ? false : _17, _18 = props.stickyKeypadAfterInsertOnTouch, stickyKeypadAfterInsertOnTouch = _18 === void 0 ? false : _18, keypadAnchorRef = props.keypadAnchorRef, _19 = props.suppressAutoFocusWhenToolbarActive, suppressAutoFocusWhenToolbarActive = _19 === void 0 ? false : _19, onTab = props.onTab, _20 = props.outerAriaHidden, outerAriaHidden = _20 === void 0 ? false : _20, _21 = props.suppressOuterGroup, suppressOuterGroup = _21 === void 0 ? false : _21, editorAriaLabel = props.editorAriaLabel, onTouchFocusSelf = props.onTouchFocusSelf;
    var selectionType = "".concat(idPreffix, "_").concat(index);
    // Equation items use a display font size, but that number is typically sourced
    // from a production format where values like 130 are authoring-scale.
    // Render at 40 % of the stored size so the text fits the canvas naturally.
    var effectiveFontSize = (data === null || data === void 0 ? void 0 : data.type) === 'equation' ? Math.round((((_a = data === null || data === void 0 ? void 0 : data.data) === null || _a === void 0 ? void 0 : _a.size) || 20) * 0.4) : ((_b = data === null || data === void 0 ? void 0 : data.data) === null || _b === void 0 ? void 0 : _b.size) || 20;
    var onTabRef = useRef(onTab);
    useEffect(function () {
        onTabRef.current = onTab;
    }, [onTab]);
    var lastFocusedMathField = useRef(null);
    var lastSelection = useRef(null);
    var lastCursorPosition = useRef(null);
    var timeoutRef = useRef(null);
    var onChangeSaveTimerRef = useRef(null);
    var textBeforeEditRef = useRef(null);
    var textEditHistoryIndexRef = useRef(-1);
    var wasFocusedRef = useRef(false);
    var getMathDataRef = useRef(null);
    var mathFieldInstancesRef = useRef(new Set());
    // Per-math-field cache of the last announced caret-in-fraction position
    // ('above' = numerator, 'below' = denominator, 'outside' = elsewhere).
    // Used by the selectionchange listener to announce only when the caret
    // transitions between numerator / denominator / outside — not on every
    // keystroke, which would flood the SR. WeakMap so the entries are
    // garbage-collected with the math-field they key on.
    var lastFracBranchRef = useRef(new WeakMap());
    var isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    var insertMathGuardRef = useRef(0);
    // Timestamp of the last setMathData DOM rebuild (initial mount, duplication,
    // or a full content reset). A duplicated text item is auto-selected
    // (isFocused=true) as soon as it mounts, and re-inserting its math-fields
    // into the DOM can trigger a stray native focus on one of them before the
    // user has clicked anything — without this guard that stray focus would
    // satisfy the isFocused check below and pop the math keypad open unprompted.
    var justHydratedRef = useRef(0);
    var keypadGuardRef = useRef(0);
    // Timestamp of the last structural keypad insert (see preserveScroll). A
    // keypad press never needs the focus-time scrollIntoView below: the cell is
    // already in view — the user is looking at it and just tapped a key. Without
    // this guard the two fight each other on iPad, which is what made inserting
    // a fraction into a table cell jump the whole table and snap back ~1s later.
    // Deliberately per-MathEditor-instance (each table cell renders its own), so
    // focusing a DIFFERENT cell reads that cell's untouched ref and still scrolls
    // into view normally — the MLC#253 behaviour this must not regress.
    var insertScrollGuardRef = useRef(0);
    // Set when the user opens the keypad via the Math Keypad toolbar button.
    // The next time the keypad becomes visible (after React renders it and
    // keypadWrapperRef is attached), we'll move focus to the first keypad
    // button ('1') instead of leaving it on the math-field. One-shot — the
    // useEffect that consumes it clears it back to false.
    var focusKeypadOnOpenRef = useRef(false);
    var isColorChangeFocusRef = useRef(false);
    // Timestamp of last touchstart on keypad. Used to skip the synthetic
    // mousedown that iPad Safari fires ~300ms after a touch — the 80ms
    // debounce in keypadBtnClickHandler is too short to catch it.
    var keypadTouchRef = useRef(0);
    // Long-press timer for programmatic text selection on iPad.
    // If native selection doesn't trigger within 500ms of a touch, we
    // select the word under the touch point using caretRangeFromPoint.
    var longPressTouchRef = useRef(null);
    var longPressTouchPosRef = useRef(null);
    // Mirror mathKeyboardVisible and isFocused in refs so the data-sync useEffect
    // can read the latest values without adding them to its dependency array.
    var mathKeyboardVisibleRef = useRef(false);
    var isFocusedRef = useRef(false);
    // Visually-hidden aria-live region for announcing fraction position (numerator/denominator)
    var fracPositionLiveRef = useRef(null);
    // Focus-options popover: when the editorDiv is reached via keyboard (Tab),
    // don't drop the caret straight into contentEditable. Show a small menu
    // with "Enter editing mode" + "Open options" so SR / keyboard users have
    // a clear choice (WCAG 4.1.2 + iPad VoiceOver best practice).
    var focusOptionsRef = useRef(null);
    // When the text box sits near the top of the canvas, the popover (which
    // renders above the box) can have nowhere to scroll to — the scroll
    // container's scrollTop can't go negative. In that case render it below
    // the box instead of leaving it clipped.
    var _22 = useState(false), focusPanelRight = _22[0], setFocusPanelRight = _22[1];
    // true when the next focus on editorDiv came from a keyboard event.
    // Reset after each focus is handled. Mouse clicks skip the popover.
    var focusFromKeyboardRef = useRef(false);
    // true once the user has chosen "edit" — suppresses the popover until blur.
    var editingAllowedRef = useRef(false);
    var viewOnlyRef = useRef(viewonly);
    var clickedAnchorRef = useRef(null);
    // Compute isFocused early so its ref sync fires BEFORE the data?.data?.text
    // useEffect (React fires effects in declaration order).
    var isFocused = selectedTool.length === 1 && selectedTool[0] === selectionType;
    // TEMP DIAGNOSTIC (iOS 26 jump). __jumpSnap(label, el, id) samples the element
    // + window + scroll-container for N frames and logs any frame that moves >2px
    // plus a DONE summary; a capture 'scroll' listener logs what scrolls. This
    // pinned the cause to `.customScrollbarContent` being scrolled by iOS on
    // input (fixed via installElLock/holdElScrollBriefly). Left commented out so
    // it can be re-enabled quickly if the jump ever regresses.
    /* eslint-disable */
    /*
    useEffect(() => {
      if ((window as any).__jumpInstalled) return;
      (window as any).__jumpInstalled = true;
      // Snapshot every scrollable ancestor of `el` (scrollTop/scrollLeft) so we
      // can detect WHICH container scrolls — not just the two we guessed before.
      const ancestorScrolls = (el: HTMLElement | null) => {
        const out: { node: HTMLElement; top: number; left: number; tag: string }[] = [];
        let n: HTMLElement | null = el;
        while (n && n !== document.body && n !== document.documentElement) {
          if (n.scrollHeight > n.clientHeight + 1 || n.scrollWidth > n.clientWidth + 1) {
            const cls =
              typeof n.className === 'string' && n.className
                ? `.${n.className.trim().split(/\s+/).slice(0, 2).join('.')}`
                : n.tagName;
            out.push({ node: n, top: Math.round(n.scrollTop), left: Math.round(n.scrollLeft), tag: cls });
          }
          n = n.parentElement;
        }
        out.push({ node: document.scrollingElement as HTMLElement, top: Math.round(window.scrollY), left: Math.round(window.scrollX), tag: 'WINDOW' });
        return out;
      };
      const offs = (el: HTMLElement | null) => {
        const r = el?.getBoundingClientRect();
        // The whole JumpBar widget (not just the inner editor) — if this moves,
        // the widget itself is being repositioned, not scrolled.
        const wrap = el?.closest('.jumpBarManipulative') as HTMLElement | null;
        const wr = wrap?.getBoundingClientRect();
        const label = el?.closest('.jumpBarLabelContent') as HTMLElement | null;
        const vv = (window as any).visualViewport;
        return {
          elTop: r ? Math.round(r.top) : -1,
          elLeft: r ? Math.round(r.left) : -1,
          wrapTop: wr ? Math.round(wr.top) : -1,
          // iOS shifts the *visual* viewport (not window.scrollY) when it tries to
          // open the native keyboard on focus → elements move with scrollY=0.
          vvTop: vv ? Math.round(vv.offsetTop) : -1,
          vvH: vv ? Math.round(vv.height) : -1,
          labelH: label ? Math.round(label.offsetHeight) : -1,
        };
      };
      (window as any).__jumpSnap = (label: string, el: HTMLElement | null, id: string) => {
        const base = offs(el);
        const baseScrolls = ancestorScrolls(el);
        const FRAMES = 120;
        let frame = 0;
        let maxEl = 0;
        const sample = () => {
          frame++;
          const o = offs(el);
          const dEl = o.elTop - base.elTop;
          const dElL = o.elLeft - base.elLeft;
          const dWrap = o.wrapTop - base.wrapTop;
          const dVv = o.vvTop - base.vvTop;
          const dVvH = o.vvH - base.vvH;
          const dLh = o.labelH - base.labelH;
          // Which ancestor scrolled since t0?
          const nowScrolls = ancestorScrolls(el);
          const moved = baseScrolls
            .map((b, i) => ({ tag: b.tag, dT: (nowScrolls[i]?.top ?? b.top) - b.top, dL: (nowScrolls[i]?.left ?? b.left) - b.left }))
            .filter(s => Math.abs(s.dT) > 2 || Math.abs(s.dL) > 2)
            .map(s => `${s.tag}[dT=${s.dT},dL=${s.dL}]`)
            .join(' ');
          if (Math.abs(dEl) > Math.abs(maxEl)) maxEl = dEl;
          if (Math.abs(dEl) > 2 || Math.abs(dElL) > 2 || Math.abs(dWrap) > 2 || Math.abs(dVv) > 2 || Math.abs(dVvH) > 2 || Math.abs(dLh) > 2 || moved || frame === FRAMES) {
            console.log(`[JUMP:${label}] ${id} f${frame} dElTop=${dEl} dElLeft=${dElL} dWrapTop=${dWrap} dVvTop=${dVv} dVvH=${dVvH} dLabelH=${dLh} scrolled={${moved}}`);
          }
          if (frame < FRAMES) requestAnimationFrame(sample);
          else console.log(`[JUMP:${label}] ${id} DONE maxElTopMove=${maxEl}`);
        };
        console.log(`[JUMP:${label}] ${id} @t0 elTop=${base.elTop} wrapTop=${base.wrapTop} vvTop=${base.vvTop} vvH=${base.vvH} labelH=${base.labelH} scrollers=[${baseScrolls.map(s => `${s.tag}:${s.top}/${s.left}`).join(', ')}]`);
        requestAnimationFrame(sample);
      };
      const onScroll = (e: Event) => {
        const t = e.target as any;
        const node = t === document ? document.scrollingElement : t;
        const cls =
          node && typeof node.className === 'string' && node.className
            ? `.${node.className.trim().split(/\s+/).join('.')}`
            : node?.tagName;
        console.log('[JUMP:scroll]', cls, { top: Math.round(node?.scrollTop ?? 0), winY: Math.round(window.scrollY) });
      };
      document.addEventListener('scroll', onScroll, true);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    */
    /* eslint-enable */
    useEffect(function () {
        mathKeyboardVisibleRef.current = mathKeyboardVisible;
        customEventHandler.dispatchEvent('mathKeyboardToggle', {
            visible: mathKeyboardVisible,
        });
        // If the keypad just opened via the Math Keypad toolbar button, move
        // focus from the freshly-inserted mf to the first keypad button ('1')
        // so the user can drive math input from the keyboard immediately.
        if (mathKeyboardVisible && focusKeypadOnOpenRef.current) {
            focusKeypadOnOpenRef.current = false;
            // rAF lets React paint the keypad and attach keypadWrapperRef before
            // we query for the first button.
            requestAnimationFrame(function () {
                var _a;
                var firstBtn = (_a = keypadWrapperRef.current) === null || _a === void 0 ? void 0 : _a.querySelector('button:not([disabled]):not([tabindex="-1"])');
                if (firstBtn)
                    firstBtn.focus();
            });
        }
    }, [mathKeyboardVisible]);
    // Track whether the last user-originated input was keyboard or pointer.
    // We listen globally (capture phase) so we set the flag BEFORE the
    // editorDiv's focus handler runs.
    useEffect(function () {
        var onKey = function (e) {
            if (e.key === 'Tab')
                focusFromKeyboardRef.current = true;
        };
        var onPointer = function (e) {
            var _a;
            focusFromKeyboardRef.current = false;
            // Only count as editing consent if the click is on the editor itself,
            // not on popover buttons (pencil, kebab) — those are inside focusOptionsRef.
            var target = e.target;
            if ((_a = focusOptionsRef.current) === null || _a === void 0 ? void 0 : _a.contains(target))
                return;
            editingAllowedRef.current = true;
        };
        document.addEventListener('keydown', onKey, true);
        document.addEventListener('mousedown', onPointer, true);
        document.addEventListener('touchstart', onPointer, true);
        return function () {
            document.removeEventListener('keydown', onKey, true);
            document.removeEventListener('mousedown', onPointer, true);
            document.removeEventListener('touchstart', onPointer, true);
        };
    }, []);
    // Auto-focus the first button in the focusOptions dialog when it opens via keyboard.
    useEffect(function () {
        if (!isFocused || !enableFocusPanel || mathKeyboardVisible)
            return;
        setFocusPanelRight(false); // re-measure fresh each time the panel opens
        var raf2 = 0;
        // Double rAF: a single setTimeout(0)/rAF can fire before the browser has
        // finished laying out and painting the just-shown panel at the current
        // zoom scale, so the first getBoundingClientRect() read is stale — this
        // was why the correct scroll only kicked in after a few extra clicks.
        // Waiting two frames guarantees layout/paint has settled before measuring.
        var raf1 = requestAnimationFrame(function () {
            raf2 = requestAnimationFrame(function () {
                var panel = focusOptionsRef.current;
                var firstBtn = panel === null || panel === void 0 ? void 0 : panel.querySelector('button');
                if (!panel)
                    return;
                // Only steal focus into the panel when it was actually opened via
                // keyboard (Tab) — matching this effect's own intent. Without this
                // check the effect ran unconditionally on every mathKeyboardVisible
                // → false transition (e.g. clicking from a math-field into plain
                // text in the same box), stealing focus to the pencil button and
                // triggering MathLive's internal focus-recovery to reclaim it right
                // back into the math-field, reopening the keypad the user just left.
                if (firstBtn && focusFromKeyboardRef.current) {
                    // preventScroll: native focus() triggers scroll-into-view that miscalculates
                    // offsets under .mainHolder's transform: scale(zoomLevel), same root cause as
                    // the mf.scrollIntoView no-op above. We still want the panel brought into view,
                    // so do it manually below with zoom-corrected math instead of skipping it.
                    firstBtn.focus({ preventScroll: true });
                }
                var scrollParent = panel.closest('[class*="scrollbar"], [class*="Scrollbar"]');
                if (!scrollParent)
                    return;
                var panelRect = panel.getBoundingClientRect();
                var parentRect = scrollParent.getBoundingClientRect();
                // The scroll container's own box can extend visually behind the sidebar
                // overlay, so comparing against parentRect.left alone under-reports
                // clipping when the box sits at the canvas's left edge — the sidebar
                // still covers the panel even though parentRect says there's room.
                // Clamp the effective left bound to whichever is further right.
                var sidebarEl = document.querySelector('.sidebar');
                var visibleLeft = sidebarEl
                    ? Math.max(parentRect.left, sidebarEl.getBoundingClientRect().right)
                    : parentRect.left;
                // The transform: scale(zoomLevel) lives on .mainHolder, a DESCENDANT of this
                // scroll container — per the CSS transform spec, the scrollable-overflow
                // region is computed from the descendant's post-transform (visual) box, so
                // scrollTop/scrollLeft already move in the same visual-pixel units as
                // getBoundingClientRect. Dividing by zoomLevel here under-corrects: empirically
                // it left ~(1 - 1/scale) of the offset every time, requiring ~7 clicks to
                // converge at 150% zoom.
                // behavior: 'auto' (instant), not 'smooth' — a smooth scroll is still
                // mid-animation when the user's very next click re-measures against
                // that in-flight scrollTop/scrollLeft, so only part of the correction
                // lands and it takes several clicks to fully converge.
                //
                // The panel renders directly to the left of the text box (vertically
                // centered on it), so it can still clip vertically (a tall box, or one
                // near the top/bottom canvas edge) as well as horizontally (near the
                // left edge) — correct both axes in one scrollTo call.
                var deltaLeft = 0;
                var deltaTop = 0;
                if (panelRect.left < visibleLeft) {
                    // Panel renders left of the text box. If the box sits near the left edge
                    // of the canvas (or under the sidebar), scrollLeft can't go negative
                    // enough to fully reveal it — in that case flip the panel to the box's
                    // right instead of leaving it clipped.
                    var neededLeft = visibleLeft - panelRect.left;
                    if (neededLeft > scrollParent.scrollLeft) {
                        setFocusPanelRight(true);
                    }
                    else {
                        deltaLeft = -neededLeft;
                    }
                }
                else if (panelRect.right > parentRect.right) {
                    deltaLeft = panelRect.right - parentRect.right;
                }
                if (panelRect.top < parentRect.top) {
                    deltaTop = -(parentRect.top - panelRect.top);
                }
                else if (panelRect.bottom > parentRect.bottom) {
                    deltaTop = panelRect.bottom - parentRect.bottom;
                }
                if (deltaLeft !== 0 || deltaTop !== 0) {
                    scrollParent.scrollTo({
                        left: scrollParent.scrollLeft + deltaLeft,
                        top: scrollParent.scrollTop + deltaTop,
                        behavior: 'auto',
                    });
                }
            });
        });
        return function () {
            cancelAnimationFrame(raf1);
            cancelAnimationFrame(raf2);
        };
    }, [isFocused, enableFocusPanel, zoomLevel, mathKeyboardVisible]);
    useEffect(function () {
        var _a, _b;
        isFocusedRef.current = isFocused;
        var touchFocusRafId = 0;
        // Reset editing-allowed gate when the editor loses selection.
        if (!isFocused) {
            editingAllowedRef.current = false;
        }
        if (!isFocused) {
            // Only run cleanup when transitioning from focused→unfocused,
            // not on initial mount. Mounting an unfocused MathEditor should
            // not clear the global selection (which would destroy the cursor
            // in a sibling editor that the user is actively typing in).
            if (wasFocusedRef.current) {
                setmathKeyboardVisible(false);
                setActiveMathText && setActiveMathText(-1);
                // Clear any lingering text selection when editor loses focus
                (_a = window.getSelection()) === null || _a === void 0 ? void 0 : _a.removeAllRanges();
                var editor = editorRef.current;
                if (editor) {
                    var emptyMfs_1 = [];
                    editor.querySelectorAll('math-field').forEach(function (node) {
                        var mf = node;
                        if ((mf.value || '').trim() === '') {
                            emptyMfs_1.push(mf);
                        }
                    });
                    if (emptyMfs_1.length > 0) {
                        emptyMfs_1.forEach(function (mf) {
                            mathFieldInstancesRef.current.delete(mf);
                            mf.remove();
                        });
                        batchDisposeMathfields(emptyMfs_1);
                        editor.normalize();
                    }
                }
                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                }
                timeoutRef.current = setTimeout(function () {
                    timeoutRef.current = null;
                    var content = calcMathData();
                    if (content.length === 0 && onRemove) {
                        onRemove();
                    }
                    else {
                        getMathData();
                    }
                }, 100);
            }
        }
        else if (isFocused && !wasFocusedRef.current) {
            // Transitioning to focused: capture text state before any editing starts
            if (isTextUpdate && typeof index === 'number' && !isManipulative) {
                var rawText = (_b = data === null || data === void 0 ? void 0 : data.data) === null || _b === void 0 ? void 0 : _b.text;
                var textArr = Array.isArray(rawText)
                    ? rawText
                    : rawText
                        ? [{ type: 'html', value: rawText }]
                        : [];
                textBeforeEditRef.current = JSON.stringify(textArr);
            }
            // On touch devices (iPad), the first tap selects the item (blue border)
            // but iPad Safari doesn't reliably activate the editing session — even
            // when the DIV is document.activeElement there may be no cursor and
            // keystrokes won't register. Always focus + place a collapsed selection
            // at the end to force the editing session open.
            if (isTouchDevice && editorRef.current && !viewonly) {
                touchFocusRafId = requestAnimationFrame(function () {
                    var editor = editorRef.current;
                    if (!editor)
                        return;
                    editor.focus({ preventScroll: true });
                    // Place a collapsed selection at the end so iPad shows the cursor
                    var sel = window.getSelection();
                    if (sel && (sel.rangeCount === 0 || !editor.contains(sel.anchorNode))) {
                        var range = document.createRange();
                        range.selectNodeContents(editor);
                        range.collapse(false); // collapse to end
                        sel.removeAllRanges();
                        sel.addRange(range);
                    }
                });
            }
        }
        if (isFocused && !wasFocusedRef.current) {
            // Emit alignment state immediately when this editor gains focus so
            // BottomPanel alignment buttons reflect the current editor's alignment.
            emitFormattingState(null);
        }
        if (!isFocused && wasFocusedRef.current) {
            customEventHandler.dispatchEvent('formattingStateChanged', {
                bold: false,
                italic: false,
                underline: false,
                leftAlign: false,
                centerAlign: false,
                rightAlign: false,
            });
            // Save text state on focus-out for whiteboard MathEditors (text state in GameController).
            // Only save when there is actual content and the text changed since focus-in.
            if (isTextUpdate &&
                typeof index === 'number' &&
                !isManipulative &&
                textBeforeEditRef.current !== null) {
                // Undo/redo deselects the focused editor as part of applying the history
                // step. Saving this uncommitted edit here would push a fresh entry that
                // truncates the redo/undo stack the action just produced — consume the
                // flag unconditionally so it never leaks into a later, unrelated blur.
                var skipSave = consumeSkipNextFocusOutSave();
                var output = calcMathData();
                var hasContent = output.length > 0 &&
                    output.some(function (item) {
                        if (item.type === 'math')
                            return !!(item.latex && item.latex.trim() !== '');
                        return ((item.value || '')
                            .replace(/<[^>]*>/g, '')
                            .replace(/\u200B/g, '')
                            .trim() !== '');
                    });
                var newSerialized = JSON.stringify(output);
                if (hasContent && textBeforeEditRef.current !== newSerialized && !skipSave) {
                    var newFullItem = __assign(__assign({}, data), { data: __assign(__assign({}, data.data), { text: output }) });
                    saveState({
                        type: "".concat(idPreffix, "_").concat(index),
                        id: index,
                        propertyName: 'textContent',
                        old: textBeforeEditRef.current,
                        new: JSON.stringify(newFullItem),
                    });
                }
                textBeforeEditRef.current = null;
            }
        }
        wasFocusedRef.current = isFocused;
        return function () {
            cancelAnimationFrame(touchFocusRafId);
        };
    }, [isFocused]);
    // Re-emit when alignment prop changes (after clicking an alignment button) so
    // BottomPanel reflects the new alignment while this editor is focused.
    useEffect(function () {
        if (!isFocusedRef.current)
            return;
        emitFormattingState(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [(_c = data === null || data === void 0 ? void 0 : data.data) === null || _c === void 0 ? void 0 : _c.alignment]);
    useEffect(function () {
        viewOnlyRef.current = viewonly;
        // Disable/enable all existing mathfield elements when viewonly changes
        var editor = editorRef.current;
        if (editor) {
            editor.querySelectorAll('math-field').forEach(function (mf) {
                mf.readonly = !!viewonly;
            });
        }
    }, [viewonly]);
    // Get total character count in the editor (text nodes + math field values)
    var getTotalCharacterCount = function (ignoreSpaces) {
        if (ignoreSpaces === void 0) { ignoreSpaces = false; }
        var editor = editorRef.current;
        if (!editor)
            return 0;
        var count = 0;
        editor.childNodes.forEach(function (node) {
            var _a;
            if (node.nodeType === Node.TEXT_NODE) {
                var text = (((_a = node.textContent) === null || _a === void 0 ? void 0 : _a.replace(/\u200B/g, '')) || '').trim();
                if (ignoreSpaces)
                    text = text.replace(/\s+/g, '');
                count += text.length;
            }
            else if (node.nodeName === 'MATH-FIELD') {
                var mf = node;
                var latexValue = (mf.value || '').trim();
                // Skip truly empty mathfields (placeholder counts as 1 visible box)
                if (latexValue !== '') {
                    // No ignoreSpaces argument: the counter never counts whitespace, so
                    // the flag only ever mattered for this function's own text nodes.
                    count += countVisibleCharsInLatex(latexValue);
                }
            }
            else if (node.nodeType === Node.ELEMENT_NODE) {
                // For HTML elements, count text content at any depth (excluding child
                // math fields, which are counted separately). Formatting nests — a
                // resized run is a font-size span, and colour enforcement puts a
                // colour span inside it — so counting only DIRECT text children made
                // every character under two or more wrappers invisible to the counter
                // and to the maxCharacters gates built on it.
                var countText_1 = function (parent) {
                    parent.childNodes.forEach(function (child) {
                        var _a;
                        if (child.nodeType === Node.TEXT_NODE) {
                            var text = ((_a = child.textContent) === null || _a === void 0 ? void 0 : _a.replace(/\u200B/g, '')) || '';
                            if (ignoreSpaces)
                                text = text.trim();
                            count += text.length;
                        }
                        else if (child.nodeType === Node.ELEMENT_NODE && child.nodeName !== 'MATH-FIELD') {
                            countText_1(child);
                        }
                    });
                };
                countText_1(node);
            }
        });
        return count;
    };
    // Returns true when the cursor in `mf` is inside a \frac argument.
    // Used to bypass the >= maxCharacters block so each fraction side can
    // independently reach the limit (the input-event revert handler enforces
    // the per-side limit using > rather than >=).
    // Uses MathLive's internal atom model to walk up the parent chain and
    // check for a 'genfrac' ancestor — getValue(0, pos) is unreliable when
    // the cursor is nested inside a fraction group.
    var isInFrac = function (mf) {
        var _a;
        try {
            var model = (_a = mf._mathfield) === null || _a === void 0 ? void 0 : _a.model;
            if (!model)
                return false;
            var atom = model.at(model.position);
            while (atom && atom.type !== 'root') {
                if (atom.type === 'genfrac')
                    return true;
                atom = atom.parent;
            }
        }
        catch (_b) {
            // model API not accessible — fall through
        }
        return false;
    };
    // Returns 'numerator' | 'denominator' | null based on where the cursor sits
    // inside a \frac. MathLive stores the numerator in the 'above' branch and
    // the denominator in the 'below' branch of a genfrac atom.
    var getFracBranch = function (mf) {
        var _a, _b, _c;
        try {
            var model = (_a = mf._mathfield) === null || _a === void 0 ? void 0 : _a.model;
            if (!model)
                return null;
            var atom = model.at(model.position);
            while (atom && atom.type !== 'root') {
                if (((_b = atom.parent) === null || _b === void 0 ? void 0 : _b.type) === 'genfrac') {
                    var branch = (_c = atom.treeBranch) !== null && _c !== void 0 ? _c : atom.branch;
                    if (branch === 'above')
                        return 'numerator';
                    if (branch === 'below')
                        return 'denominator';
                }
                atom = atom.parent;
            }
        }
        catch (_d) {
            // model API not accessible
        }
        return null;
    };
    // Announce the fraction position (numerator / denominator) to screen readers
    // via the polite live region. Called on selectionchange and focusin.
    var announceFracPosition = function (mf) {
        var branch = getFracBranch(mf);
        var liveEl = fracPositionLiveRef.current;
        if (!liveEl)
            return;
        if (branch === 'numerator') {
            liveEl.textContent = t('text.mathEditor.fracNumerator');
        }
        else if (branch === 'denominator') {
            liveEl.textContent = t('text.mathEditor.fracDenominator');
        }
        else {
            liveEl.textContent = '';
        }
    };
    // Thin aliases over src/helper/mathLatexStyle.ts. The brace-depth walker and
    // the size ladder moved there so they can be unit-tested without a browser
    // (see tests/unit/mathLatexStyle.test.ts); the names are kept so the rest of
    // this file reads unchanged.
    var unwrap = unwrapLatexCommand;
    var wrap = wrapLatexCommand;
    /**
     * The largest font size (px) actually rendered inside `range`.
     *
     * The size a selection displays at lives on whatever `<span
     * style="font-size">` happens to wrap it, and a selection can cover several
     * of them plus unwrapped text — hence a range, not one number. Falls back to
     * the editor's own base size when nothing inside carries an explicit size.
     */
    var measureSelectionFontSize = function (range, existingSpan) {
        var _a;
        var editor = editorRef.current;
        var readPx = function (node) {
            var _a;
            var el = node && node.nodeType === Node.ELEMENT_NODE
                ? node
                : ((_a = node === null || node === void 0 ? void 0 : node.parentElement) !== null && _a !== void 0 ? _a : null);
            if (!el || !el.isConnected)
                return 0;
            var px = parseFloat(window.getComputedStyle(el).fontSize);
            return Number.isFinite(px) ? px : 0;
        };
        var sizes = [];
        if (existingSpan)
            sizes.push(readPx(existingSpan));
        // Measure the text runs the range actually covers, each at its own parent's
        // size. Reading the range's boundary CONTAINERS instead is only an
        // approximation, and it breaks on an element-anchored range: with
        // `selectNodeContents(editorDiv)` the container is the editor, contributing
        // its base size as though base-size text were selected, so "Smaller" over a
        // uniformly enlarged line undershoots by a rung.
        var zws = new RegExp(WHITESPACE_CHAR, 'g');
        if (editor) {
            var walker = document.createTreeWalker(editor, NodeFilter.SHOW_TEXT);
            for (var n = walker.nextNode(); n; n = walker.nextNode()) {
                var visible = ((_a = n.textContent) !== null && _a !== void 0 ? _a : '').replace(zws, '').length > 0;
                if (visible && range.intersectsNode(n))
                    sizes.push(readPx(n));
            }
        }
        // Sized spans that render without contributing any text: a math-field draws
        // through its shadow DOM and a <br> draws as height, so both leave
        // `textContent` empty. The walker above cannot see them, and skipping them
        // is not harmless — a selection running from plain text through an enlarged
        // fraction would measure the plain text only, and "Larger" would shrink the
        // fraction: the original bug again. Husk spans left behind by the
        // extract-and-wrap path below have neither, and are correctly ignored.
        editor === null || editor === void 0 ? void 0 : editor.querySelectorAll('span[style*="font-size"]').forEach(function (el) {
            var _a;
            var rendersWithoutText = ((_a = el.textContent) !== null && _a !== void 0 ? _a : '').replace(zws, '').length === 0 &&
                !!el.querySelector('math-field, br');
            if (rendersWithoutText && range.intersectsNode(el))
                sizes.push(readPx(el));
        });
        var seen = sizes.filter(function (px) { return px > 0; });
        if (seen.length === 0)
            return { min: effectiveFontSize, max: effectiveFontSize };
        return { min: Math.min.apply(Math, seen), max: Math.max.apply(Math, seen) };
    };
    /**
     * The range a formatting action should apply to.
     *
     * **No selection means "the whole box".** Selecting the box and pressing Bold
     * (or Italic / Underline / Larger / Smaller) styles everything in it, so the
     * user never has to select every character first. `changeColour` has always
     * read an empty selection that way; the other tools used to bail out instead,
     * which is why those buttons appeared to do nothing until text was manually
     * selected.
     *
     * Three sources, in order:
     *
     * 1. **The live selection**, when it is non-empty and inside THIS editor. The
     *    containment test matters — a caret left behind in a different text box
     *    must not be read as this box's selection.
     * 2. **`lastSelection`**, the recovery path for browsers that collapse the
     *    selection on toolbar mousedown despite `preventDefault` (Edge). It is
     *    guarded hard, because that ref is written on every non-collapsed
     *    `onSelect` and **never cleared** — left unguarded it would resurrect a
     *    selection from minutes ago and quietly style THAT instead of the box, in
     *    the commonest flow there is (select a word, style it, click elsewhere,
     *    press Bold). It counts only while the editor still holds focus (i.e. the
     *    user really is mid-edit and the selection was just destroyed under them)
     *    and its nodes are still connected — an earlier `extractContents` detaches
     *    them.
     * 3. **The whole box**, which is the point of this helper.
     *
     * Returns `null` only when there is nothing to style at all.
     */
    var usableSelectionInBox = function () {
        var editor = editorRef.current;
        if (!editor)
            return null;
        var sel = window.getSelection();
        var live = sel && sel.rangeCount > 0 ? sel.getRangeAt(0) : null;
        // The live selection is current by definition, so it needs no focus check.
        if (isUsableSelection(live, editor, document.activeElement))
            return live;
        // The saved one does — see isUsableSelection for why that ref goes stale.
        var saved = lastSelection.current;
        if (isUsableSelection(saved, editor, document.activeElement, { requireFocus: true })) {
            return saved;
        }
        return null;
    };
    var resolveFormattingRange = function () {
        var editor = editorRef.current;
        if (!editor)
            return null;
        var sel = window.getSelection();
        var usable = usableSelectionInBox();
        if (usable) {
            // Re-assert it: the saved-selection path has to put the recovered range
            // back on the document before the caller mutates it.
            if (sel && (sel.rangeCount === 0 || sel.getRangeAt(0) !== usable)) {
                var restored = usable.cloneRange();
                sel.removeAllRanges();
                sel.addRange(restored);
                return { range: restored, wholeBox: false };
            }
            return { range: usable, wholeBox: false };
        }
        var all = document.createRange();
        all.selectNodeContents(editor);
        // An empty box has nothing to style. `collapsed` is the right test: a box
        // holding only a blank-line caret spacer still offers a range, and styling
        // it is harmless.
        if (all.collapsed)
            return null;
        sel === null || sel === void 0 ? void 0 : sel.removeAllRanges();
        sel === null || sel === void 0 ? void 0 : sel.addRange(all);
        return { range: all, wholeBox: true };
    };
    // Apply font size to selected regular text — or, with nothing selected, to
    // the whole box (see resolveFormattingRange).
    var applyFontSize = function (direction) {
        if (viewOnlyRef.current)
            return;
        var sel = window.getSelection();
        if (!sel)
            return;
        var target = resolveFormattingRange();
        if (!target)
            return;
        var range = target.range;
        // Helper to find font-size span that fully contains the selection
        var findContainingFontSpan = function () {
            var _a, _b;
            var startContainer = range.startContainer;
            var endContainer = range.endContainer;
            // Find font-size span from start
            var startSpan = startContainer.nodeType === Node.ELEMENT_NODE
                ? startContainer.closest('span[style*="font-size"]')
                : (_a = startContainer.parentElement) === null || _a === void 0 ? void 0 : _a.closest('span[style*="font-size"]');
            // Find font-size span from end
            var endSpan = endContainer.nodeType === Node.ELEMENT_NODE
                ? endContainer.closest('span[style*="font-size"]')
                : (_b = endContainer.parentElement) === null || _b === void 0 ? void 0 : _b.closest('span[style*="font-size"]');
            // If both are in the same span, and selection covers the entire span content, return it
            if (startSpan && endSpan && startSpan === endSpan && startSpan instanceof HTMLSpanElement) {
                // Check if selection covers the entire span content
                var spanRange = document.createRange();
                spanRange.selectNodeContents(startSpan);
                // If the selection fully covers the span, we can update it
                if (range.compareBoundaryPoints(Range.START_TO_START, spanRange) <= 0 &&
                    range.compareBoundaryPoints(Range.END_TO_END, spanRange) >= 0) {
                    return startSpan;
                }
                // Also return if the selection is entirely within this span
                return startSpan;
            }
            return null;
        };
        // Try to find existing font-size span
        var existingSpan = findContainingFontSpan();
        // The sizes actually rendered in the selection, measured from the screen.
        //
        // Reading the size off `effectiveFontSize` (the box's base size) whenever
        // the selection wasn't wholly inside one font-size span was what made
        // "Larger" shrink text: a selection spanning a 48px span plus plain text
        // measured as the 20px base, so the next step up was 24px and the whole
        // run visibly dropped from 48px to 24px.
        var _a = measureSelectionFontSize(range, existingSpan), minSize = _a.min, maxSize = _a.max;
        // A mixed selection unifies to a single size, and the button's direction
        // must hold for EVERY run in it — so grow from the largest and shrink from
        // the smallest. Stepping "one below the largest" would have made Smaller
        // enlarge the small runs (12px + 96px → 72px), the same bug in mirror.
        // The rule itself lives in `src/helper/fontSizeSteps.ts` so it can be
        // unit-tested without a browser (see tests/unit/fontSizeSteps.test.ts).
        var newSize = nextFontSizeStep(direction, { min: minSize, max: maxSize });
        // Resizing is a raw DOM mutation, so the browser fires no `input` event and
        // the canvas-text auto-resize effect (which listens for exactly that) never
        // sees the box's content grow — the same gap insertMath had. Announce it,
        // or enlarged text overflows and clips instead of growing the box.
        var notifyResize = function () { var _a; return (_a = editorRef.current) === null || _a === void 0 ? void 0 : _a.dispatchEvent(new Event('input', { bubbles: true })); };
        // If selection is entirely within an existing font-size span, just update it
        if (existingSpan) {
            existingSpan.style.fontSize = "".concat(newSize, "px");
            // Restore selection to the span
            var newRange_1 = document.createRange();
            newRange_1.selectNodeContents(existingSpan);
            sel.removeAllRanges();
            sel.addRange(newRange_1);
            notifyResize();
            return;
        }
        // Otherwise, extract and wrap selection in a new span
        var extractedContent = range.extractContents();
        // Flatten any nested font-size spans in the extracted content
        var tempDiv = document.createElement('div');
        tempDiv.appendChild(extractedContent);
        var nestedSpans = tempDiv.querySelectorAll('span[style*="font-size"]');
        nestedSpans.forEach(function (span) {
            // Unwrap the span but keep its contents
            var parent = span.parentNode;
            while (span.firstChild) {
                parent === null || parent === void 0 ? void 0 : parent.insertBefore(span.firstChild, span);
            }
            span.remove();
        });
        var sizeSpan = document.createElement('span');
        sizeSpan.style.fontSize = "".concat(newSize, "px");
        while (tempDiv.firstChild) {
            sizeSpan.appendChild(tempDiv.firstChild);
        }
        range.insertNode(sizeSpan);
        // Restore selection
        var newRange = document.createRange();
        newRange.selectNodeContents(sizeSpan);
        sel.removeAllRanges();
        sel.addRange(newRange);
        notifyResize();
    };
    // Apply font size to selected math or text
    var execFontSize = function (direction) {
        var _a, _b, _c;
        if (viewOnlyRef.current)
            return;
        var mf = lastFocusedMathField.current;
        // Box-level: nothing selected anywhere → resize every text run AND every
        // math-field. See the sibling branch in `exec` for why this precedes the
        // focused-math-field path.
        if (boxLevelStylingAllowed && mathSelectionIsEmpty(mf) && !usableSelectionInBox()) {
            applyFontSize(direction);
            applyFontSizeToMathFields(direction);
            (_a = editorRef.current) === null || _a === void 0 ? void 0 : _a.focus({ preventScroll: true });
            clearBoxLevelSelection();
            (_b = getMathDataRef.current) === null || _b === void 0 ? void 0 : _b.call(getMathDataRef);
            return;
        }
        if (!mf) {
            applyFontSize(direction);
            (_c = editorRef.current) === null || _c === void 0 ? void 0 : _c.focus({ preventScroll: true });
            return;
        }
        // Re-focus the mathfield BEFORE reading selection.
        safeFocusMathField(mf);
        var sel = mf.selection;
        var startOffset = sel.ranges[0][0];
        // Handle math field selection
        var latex = mf.getValue(sel);
        if (!latex || latex.trim().length === 0) {
            // No selection in math field, try regular text
            applyFontSize(direction);
            return;
        }
        // Detect current size from LaTeX wrapping and strip it
        var currentSizeIndex = LATEX_SIZE_COMMANDS.indexOf('normalsize'); // default 4
        for (var i = 0; i < LATEX_SIZE_COMMANDS.length; i++) {
            var cmd = LATEX_SIZE_COMMANDS[i];
            if (latex.includes("\\".concat(cmd, "{")) ||
                latex.includes("\\".concat(cmd, " ")) ||
                latex.includes("\\".concat(cmd))) {
                currentSizeIndex = i;
                latex = unwrap(cmd, latex);
                break;
            }
        }
        // Step up or down
        var newSizeIndex;
        if (direction === 'larger') {
            newSizeIndex = Math.min(currentSizeIndex + 1, LATEX_SIZE_COMMANDS.length - 1);
        }
        else {
            newSizeIndex = Math.max(currentSizeIndex - 1, 0);
        }
        // Wrap with new size command (skip if normalsize to keep LaTeX clean)
        if (latex.includes("\\".concat(LATEX_SIZE_COMMANDS[currentSizeIndex]))) {
            // remove the previous size command
            latex = latex.replace("\\".concat(LATEX_SIZE_COMMANDS[currentSizeIndex]), '');
        }
        if (LATEX_SIZE_COMMANDS[newSizeIndex]) {
            latex = wrap(LATEX_SIZE_COMMANDS[newSizeIndex], latex);
        }
        mf.insert(latex, {
            insertionMode: 'replaceSelection',
        });
        // Re-select the inserted content so repeated clicks can step further
        // (same pattern as applyFontSize does for regular text)
        var endOffset = mf.position;
        mf.selection = { ranges: [[startOffset, endOffset]] };
    };
    /**
     * Wrap or unwrap `cmd` over the current selection.
     *
     * This is the SELECTION path. Styling a whole box goes through
     * `applyStyleToWholeBoxText` instead, because this function infers
     * "already styled, so unwrap" from a single tag enclosing the range — see the
     * note there for why that inference does not survive a save/reload rebuild.
     *
     * It still falls back to the whole box when there is no selection at all
     * (`resolveFormattingRange`), so it degrades sensibly rather than doing
     * nothing if it is ever reached that way.
     */
    var applyFormatting = function (cmd) {
        var _a, _b, _c, _d, _e;
        if (viewOnlyRef.current)
            return;
        var sel = window.getSelection();
        if (!sel)
            return;
        var target = resolveFormattingRange();
        if (!target)
            return;
        var range = target.range;
        var tagName = cmd === 'bold' ? 'strong' : cmd === 'italic' ? 'em' : 'u';
        // Check if the entire selection is within a tag of the same type
        var container = range.commonAncestorContainer;
        var existingTag = null;
        if (container.nodeType === Node.ELEMENT_NODE) {
            existingTag = container.closest(tagName);
        }
        else {
            existingTag = ((_a = container.parentElement) === null || _a === void 0 ? void 0 : _a.closest(tagName)) || null;
        }
        // Check if selection boundaries are within the same formatting tag
        var startContainer = range.startContainer;
        var endContainer = range.endContainer;
        var startParent = startContainer.nodeType === Node.ELEMENT_NODE
            ? startContainer
            : startContainer.parentElement;
        var endParent = endContainer.nodeType === Node.ELEMENT_NODE
            ? endContainer
            : endContainer.parentElement;
        var startTag = (_b = startParent === null || startParent === void 0 ? void 0 : startParent.closest(tagName)) !== null && _b !== void 0 ? _b : null;
        var endTag = (_c = endParent === null || endParent === void 0 ? void 0 : endParent.closest(tagName)) !== null && _c !== void 0 ? _c : null;
        // Edge fallback: when the range container is the editor element itself,
        // closest() won't find the tag. Check if the child node at the offset
        // is the formatting tag directly.
        if (!startTag && startContainer.nodeType === Node.ELEMENT_NODE) {
            var childAtOffset = startContainer.childNodes[range.startOffset];
            if (((_d = childAtOffset === null || childAtOffset === void 0 ? void 0 : childAtOffset.nodeName) === null || _d === void 0 ? void 0 : _d.toLowerCase()) === tagName) {
                startTag = childAtOffset;
            }
        }
        if (!endTag && endContainer.nodeType === Node.ELEMENT_NODE) {
            var childBefore = endContainer.childNodes[range.endOffset - 1];
            if (((_e = childBefore === null || childBefore === void 0 ? void 0 : childBefore.nodeName) === null || _e === void 0 ? void 0 : _e.toLowerCase()) === tagName) {
                endTag = childBefore;
            }
        }
        // Also check: if existingTag was found via commonAncestor, use it as fallback
        if (!startTag && !endTag && existingTag) {
            startTag = existingTag;
            endTag = existingTag;
        }
        if (startTag && endTag && startTag === endTag) {
            // Unwrap: selection is entirely within the tag, so remove formatting
            var parent_1 = startTag.parentNode;
            if (parent_1) {
                // Store the first and last nodes before unwrapping
                var firstNode = startTag.firstChild;
                var lastNode = startTag.lastChild;
                // Unwrap the tag
                while (startTag.firstChild) {
                    parent_1.insertBefore(startTag.firstChild, startTag);
                }
                parent_1.removeChild(startTag);
                // Keep the unwrapped content selected
                if (firstNode && lastNode) {
                    var newRange = document.createRange();
                    newRange.setStartBefore(firstNode);
                    newRange.setEndAfter(lastNode);
                    sel.removeAllRanges();
                    sel.addRange(newRange);
                    lastSelection.current = newRange.cloneRange();
                }
            }
        }
        else {
            // Wrap: apply formatting
            var selectedContent = range.extractContents();
            var formatElement = document.createElement(tagName);
            formatElement.appendChild(selectedContent);
            range.insertNode(formatElement);
            // Keep the wrapped content selected
            var newRange = document.createRange();
            newRange.selectNodeContents(formatElement);
            sel.removeAllRanges();
            sel.addRange(newRange);
            lastSelection.current = newRange.cloneRange();
        }
    };
    /**
     * Every `<math-field>` in this box, in document order.
     *
     * `querySelectorAll` rather than `mathFieldInstancesRef` (which is a cleanup
     * ledger and can hold fields already detached) and rather than
     * `editor.childNodes` (which sees only DIRECT children, so a field inside a
     * colour or size span is missed — the bug the existing whole-box recolour
     * still has).
     */
    var mathFieldsInBox = function () {
        return editorRef.current
            ? Array.from(editorRef.current.querySelectorAll('math-field'))
            : [];
    };
    /**
     * Apply a style to EVERY math-field in the box, in the direction the text
     * half already took.
     *
     * Required because the HTML wrap cannot reach math: a `<math-field>` paints
     * through its shadow DOM, so an enclosing `<strong>` leaves the expression
     * visually untouched. Without this, box-level Bold on a box holding "1/2 of
     * the cake" would bold the words and leave the fraction light.
     */
    var applyStyleToMathFields = function (style, direction) {
        mathFieldsInBox().forEach(function (mf) {
            var _a;
            var latex = (_a = mf.value) !== null && _a !== void 0 ? _a : '';
            var next = toggleLatexStyle(latex, style, direction);
            if (next !== latex)
                mf.value = next;
        });
    };
    /** Step every math-field in the box one rung along the LaTeX size ladder. */
    var applyFontSizeToMathFields = function (direction) {
        mathFieldsInBox().forEach(function (mf) {
            var _a;
            var latex = (_a = mf.value) !== null && _a !== void 0 ? _a : '';
            var next = stepLatexSize(latex, direction);
            if (next !== latex)
                mf.value = next;
        });
    };
    var STYLE_TAGS = {
        bold: 'strong',
        italic: 'em',
        underline: 'u',
    };
    /**
     * Is the WHOLE box already carrying `style`?
     *
     * Drives the toolbar highlight when there is no selection, where
     * `document.queryCommandState` cannot help — it reports the state at the
     * caret, and a box-level action has no caret. A button showing "off" while a
     * click would turn the style *off* is worse than no highlight at all.
     *
     * "Whole box" means every run that renders a character: an ancestor of the
     * right tag, found INSIDE this editor (`closest` alone would happily match a
     * `<strong>` wrapping the widget). Blank-line caret spacers and other
     * zero-width text are skipped — they carry no formatting a user can see. A
     * box holding only math falls through to the math-fields' own state, so a
     * fraction-only box still highlights correctly.
     */
    var boxHasStyle = function (style) {
        var editor = editorRef.current;
        if (!editor)
            return false;
        var tagName = STYLE_TAGS[style];
        if (boxHasNoVisibleText(editor, WHITESPACE_CHAR))
            return mathFieldsAllStyled(style);
        if (!allVisibleTextHasAncestor(editor, tagName, WHITESPACE_CHAR))
            return false;
        // Text is uniformly styled; math must agree, or the box is mixed.
        var fields = mathFieldsInBox().filter(function (mf) { var _a; return ((_a = mf.value) !== null && _a !== void 0 ? _a : '').trim().length > 0; });
        return fields.length === 0 || fields.every(function (mf) { var _a; return hasLatexStyle((_a = mf.value) !== null && _a !== void 0 ? _a : '', style); });
    };
    /**
     * Leave no selection behind after a box-level action.
     *
     * A box-level action means "nothing was selected", and that has to still be
     * true for the NEXT click, because `usableSelectionInBox()` is what routes
     * the click. Two things conspired to break it and made a second consecutive
     * Larger click style the text while the math stalled:
     *
     *  - `applyFontSize` re-selects what it just resized — deliberately, so
     *    repeated clicks can step a *real* selection further — which leaves a
     *    live, non-collapsed, in-editor range that reads as a user selection.
     *  - the branch re-focuses the editor, which satisfies `isUsableSelection`'s
     *    focus clause and so revives `lastSelection` as well.
     *
     * Clearing the live range alone is not enough for that second reason:
     * `lastSelection` is written on every non-collapsed `onSelect` and never
     * cleared, so a word the user selected earlier would take over the next
     * click. Both have to go.
     */
    var clearBoxLevelSelection = function () {
        var _a;
        (_a = window.getSelection()) === null || _a === void 0 ? void 0 : _a.removeAllRanges();
        lastSelection.current = null;
    };
    /**
     * Apply a style to ALL the text in the box, in a given direction.
     *
     * The box path deliberately does NOT go through `applyFormatting`, and the
     * reason is a measured failure rather than a preference. `applyFormatting`
     * infers "already styled, so unwrap" from `startTag === endTag` — one tag
     * enclosing the whole range. That holds right after a wrap, but the save →
     * `calcMathData` → `setMathData` rebuild re-emits a uniformly bold box as
     * SEVERAL sibling `<strong>`s (each decomposed text run gets its own, with
     * the math-field a bare sibling between them). Two different tags then read
     * as "not styled", so a second click wrapped the box again instead of
     * clearing it — the text appeared to toggle while the math stayed bold.
     *
     * Deciding the direction from `boxHasStyle` and applying it explicitly is
     * indifferent to how many tags the rebuild produced.
     *
     * Both directions strip the existing tags first, so a wrap cannot nest.
     */
    var applyStyleToWholeBoxText = function (style, direction) {
        var editor = editorRef.current;
        if (!editor)
            return;
        var tagName = STYLE_TAGS[style];
        editor
            .querySelectorAll(tagName)
            .forEach(function (el) { return unwrapElement(el); });
        if (direction === 'unwrapped')
            return;
        var range = document.createRange();
        range.selectNodeContents(editor);
        if (range.collapsed)
            return;
        var fragment = range.extractContents();
        var wrapper = document.createElement(tagName);
        wrapper.appendChild(fragment);
        range.insertNode(wrapper);
    };
    /**
     * Table cells are the one editor that must never take a box-level style from
     * here. `TableManipulativeComp` owns whole-cell bold/italic/underline/size
     * (its `bulkOps`, applied to every cell on a table-level selection) and its
     * own `colourChange` listener, all on the cell DATA model rendered as CSS on
     * the `<td>`. A span override written here would both double-apply and
     * desync from that model.
     */
    var boxLevelStylingAllowed = idPreffix !== 'tableText';
    /**
     * Recolour the entire box.
     *
     * Math-fields are set directly: they paint through their shadow DOM, so the
     * enclosing colour span does not reach them. Setting `style.color` alone is
     * not enough, though — an earlier individual colour change (picking a colour
     * while part of the math expression was selected) can leave the run wrapped
     * in an explicit `\textcolor{...}` LaTeX macro, and that macro's own colour
     * wins at render time over the field-level CSS. An existing macro has to be
     * rewritten directly; `applyStyle` is only a valid fallback for a field that
     * doesn't already have one, since it won't rewrite a macro that's already
     * there. Either write also leaves that range as the field's own live
     * selection, so it's collapsed afterwards — otherwise the *next* colour
     * change would misread the field as having a real user selection and take
     * the `changeColour` mf-focused branch instead of this one.
     */
    var recolourWholeBox = function (localColor) {
        var _a;
        var editor = editorRef.current;
        if (!editor)
            return;
        var fields = mathFieldsInBox();
        fields.forEach(function (mf) {
            var _a;
            mf.style.color = localColor;
            if (mf.lastOffset > 0) {
                var latex = (_a = mf.value) !== null && _a !== void 0 ? _a : '';
                // An earlier individual colour change embeds an explicit `\textcolor{...}`
                // macro in the LaTeX itself. `applyStyle` on a range that's already inside
                // one of those macros does not rewrite it — the macro's own colour still
                // wins at render time over the field-level CSS `style.color` set above, so
                // that run stays visually stuck on its old colour. Rewrite the macro's
                // colour argument directly rather than relying on `applyStyle` for it.
                if (/\\textcolor\{/.test(latex)) {
                    var recoloured = latex.replace(/\\textcolor\{[^}]*\}/g, "\\textcolor{".concat(localColor, "}"));
                    if (recoloured !== latex)
                        mf.value = recoloured;
                }
                else {
                    mf.applyStyle({ color: localColor }, { range: [0, mf.lastOffset] });
                }
                // `applyStyle`/`value` writes with an explicit range can leave that range
                // as the field's own live selection, so the NEXT colour change reads
                // `mathSelectionIsEmpty` as false and wrongly takes the mf-focused branch
                // again (recolouring only the math, skipping the box's plain text).
                // Collapse it back to a caret so a subsequent box-level colour change is
                // correctly recognised as one.
                mf.selection = 0;
            }
        });
        var allRange = document.createRange();
        allRange.selectNodeContents(editor);
        if (!allRange.collapsed) {
            var fragment = allRange.extractContents();
            unwrapNestedSpans(fragment);
            var span = document.createElement('span');
            span.style.color = localColor;
            span.appendChild(fragment);
            allRange.insertNode(span);
        }
        (_a = getMathDataRef.current) === null || _a === void 0 ? void 0 : _a.call(getMathDataRef);
    };
    /**
     * Is every math-field in the box already carrying `style`?
     *
     * Only consulted when the box holds no stylable text, so the text half has no
     * opinion to follow — a math-only box still has to toggle. An empty box
     * reports false so the first click adds rather than removes.
     */
    var mathFieldsAllStyled = function (style) {
        var fields = mathFieldsInBox().filter(function (mf) { var _a; return ((_a = mf.value) !== null && _a !== void 0 ? _a : '').trim().length > 0; });
        return fields.length > 0 && fields.every(function (mf) { var _a; return hasLatexStyle((_a = mf.value) !== null && _a !== void 0 ? _a : '', style); });
    };
    // Detect current formatting state from the active selection (regular text or mathfield)
    // and dispatch it so BottomPanel can highlight the active formatting buttons.
    var emitFormattingState = function (mf) {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        var activeMf = mf !== null && mf !== void 0 ? mf : lastFocusedMathField.current;
        var bold = false;
        var italic = false;
        var underline = false;
        if (activeMf) {
            var latex = activeMf.getValue(activeMf.selection);
            bold = latex.includes('\\mathbf{') || latex.includes('\\bm{');
            italic = latex.includes('\\mathit{');
            underline = latex.includes('\\underline{');
        }
        else if (idPreffix === 'tableText') {
            // Table cells use data-model bold/italic/underline (CSS on <td>), not DOM <strong>/<em>/<u> tags.
            bold = (_b = (_a = data === null || data === void 0 ? void 0 : data.data) === null || _a === void 0 ? void 0 : _a.bold) !== null && _b !== void 0 ? _b : false;
            italic = (_d = (_c = data === null || data === void 0 ? void 0 : data.data) === null || _c === void 0 ? void 0 : _c.italic) !== null && _d !== void 0 ? _d : false;
            underline = (_f = (_e = data === null || data === void 0 ? void 0 : data.data) === null || _e === void 0 ? void 0 : _e.underline) !== null && _f !== void 0 ? _f : false;
        }
        else if (boxLevelStylingAllowed && !usableSelectionInBox()) {
            // No selection: the buttons describe what a click would do to the WHOLE
            // box, so read the box rather than the caret.
            bold = boxHasStyle('bold');
            italic = boxHasStyle('italic');
            underline = boxHasStyle('underline');
        }
        else {
            try {
                bold = document.queryCommandState('bold');
                italic = document.queryCommandState('italic');
                underline = document.queryCommandState('underline');
            }
            catch (_) { }
        }
        var alignment = (_h = (_g = data === null || data === void 0 ? void 0 : data.data) === null || _g === void 0 ? void 0 : _g.alignment) !== null && _h !== void 0 ? _h : 'left';
        customEventHandler.dispatchEvent('formattingStateChanged', {
            bold: bold,
            italic: italic,
            underline: underline,
            leftAlign: alignment === 'left',
            centerAlign: alignment === 'center',
            rightAlign: alignment === 'right',
        });
    };
    /**
     * Is a math-field holding a real (non-empty) selection right now?
     *
     * `lastFocusedMathField` is a *last*-focused ref, not a "currently selected"
     * one, so its mere presence says nothing about whether the user has anything
     * selected in it. Reading its selection can throw once the field has been
     * detached, hence the guard.
     */
    var mathSelectionIsEmpty = function (mf) {
        var _a;
        if (!mf || !mf.isConnected)
            return true;
        try {
            return ((_a = mf.getValue(mf.selection)) !== null && _a !== void 0 ? _a : '').trim().length === 0;
        }
        catch (_b) {
            return true;
        }
    };
    var exec = function (cmd) {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o;
        if (viewOnlyRef.current)
            return;
        var mf = lastFocusedMathField.current;
        // ── Box-level: nothing selected anywhere, so style the WHOLE box.
        //
        // Checked before the focused-math-field branch below, which would
        // otherwise style whichever field happened to be focused last — and, with
        // no selection in it, `wrap` an empty string into `\mathbf{}` (that branch
        // has no empty-latex guard, unlike execFontSize's).
        if (boxLevelStylingAllowed && mathSelectionIsEmpty(mf) && !usableSelectionInBox()) {
            var scrollEl_1 = (_a = editorRef.current) === null || _a === void 0 ? void 0 : _a.closest('.customScrollbarContent');
            var savedScrollTop_1 = (_b = scrollEl_1 === null || scrollEl_1 === void 0 ? void 0 : scrollEl_1.scrollTop) !== null && _b !== void 0 ? _b : 0;
            var savedScrollLeft_1 = (_c = scrollEl_1 === null || scrollEl_1 === void 0 ? void 0 : scrollEl_1.scrollLeft) !== null && _c !== void 0 ? _c : 0;
            // ONE direction for the whole box, read from the box's current state, so
            // the text and the math can never toggle opposite ways on a single click.
            var direction = boxHasStyle(cmd) ? 'unwrapped' : 'wrapped';
            // Text first, math second, and the order is load-bearing: the text pass
            // wraps the box with `extractContents()`, which re-parents every
            // math-field. `mathFieldsInBox()` re-queries the live DOM afterwards, so
            // it addresses the fields where they ended up. Doing math first would
            // write values into nodes about to be moved.
            applyStyleToWholeBoxText(cmd, direction);
            applyStyleToMathFields(cmd, direction);
            (_d = editorRef.current) === null || _d === void 0 ? void 0 : _d.focus({ preventScroll: true });
            clearBoxLevelSelection();
            if (scrollEl_1) {
                requestAnimationFrame(function () {
                    scrollEl_1.scrollTop = savedScrollTop_1;
                    scrollEl_1.scrollLeft = savedScrollLeft_1;
                });
            }
            // A raw DOM/value mutation fires no native `input`, so the canvas-text
            // auto-resize never sees the box's content change — same gap, and same
            // remedy, as applyFontSize and insertMath.
            (_e = editorRef.current) === null || _e === void 0 ? void 0 : _e.dispatchEvent(new Event('input', { bubbles: true }));
            emitFormattingState(null);
            (_f = getMathDataRef.current) === null || _f === void 0 ? void 0 : _f.call(getMathDataRef);
            return;
        }
        if (!mf) {
            // Save scroll position — formatting DOM manipulation can trigger
            // browser auto-scroll due to transform: scale() offsets.
            var scrollEl_2 = (_g = editorRef.current) === null || _g === void 0 ? void 0 : _g.closest('.customScrollbarContent');
            var savedScrollTop_2 = (_h = scrollEl_2 === null || scrollEl_2 === void 0 ? void 0 : scrollEl_2.scrollTop) !== null && _h !== void 0 ? _h : 0;
            var savedScrollLeft_2 = (_j = scrollEl_2 === null || scrollEl_2 === void 0 ? void 0 : scrollEl_2.scrollLeft) !== null && _j !== void 0 ? _j : 0;
            applyFormatting(cmd);
            (_k = editorRef.current) === null || _k === void 0 ? void 0 : _k.focus({ preventScroll: true });
            if (scrollEl_2) {
                requestAnimationFrame(function () {
                    scrollEl_2.scrollTop = savedScrollTop_2;
                    scrollEl_2.scrollLeft = savedScrollLeft_2;
                });
            }
            return;
        }
        // Save scroll position — MathLive insert/applyStyle can trigger
        // browser auto-scroll due to transform: scale() offsets.
        var scrollEl = (_l = editorRef.current) === null || _l === void 0 ? void 0 : _l.closest('.customScrollbarContent');
        var savedScrollTop = (_m = scrollEl === null || scrollEl === void 0 ? void 0 : scrollEl.scrollTop) !== null && _m !== void 0 ? _m : 0;
        var savedScrollLeft = (_o = scrollEl === null || scrollEl === void 0 ? void 0 : scrollEl.scrollLeft) !== null && _o !== void 0 ? _o : 0;
        // Re-focus the mathfield BEFORE reading selection.
        // Clicking a toolbar button blurs the mathfield, which may clear
        // MathLive's internal selection. Re-focus restores it.
        safeFocusMathField(mf);
        if (cmd === 'bold') {
            var latex = mf.getValue(mf.selection);
            var hasBold = latex.includes('\\mathbf{') || latex.includes('\\bm{');
            var newLatex = hasBold ? unwrap('bm', unwrap('mathbf', latex)) : wrap('mathbf', latex);
            mf.insert(newLatex, {
                insertionMode: 'replaceSelection',
                selectionMode: 'item',
            });
        }
        else if (cmd === 'italic') {
            var latex = mf.getValue(mf.selection);
            var hasItalic = latex.includes('\\mathit{');
            var newLatex = hasItalic ? unwrap('mathit', latex) : wrap('mathit', latex);
            mf.insert(newLatex, {
                insertionMode: 'replaceSelection',
                selectionMode: 'item',
            });
        }
        else if (cmd === 'underline') {
            var sel = mf.selection;
            var latex = mf.getValue(sel);
            var isUnderline = latex.includes('\\underline{');
            var newLatex = isUnderline ? unwrap('underline', latex) : wrap('underline', latex);
            mf.insert(newLatex, {
                insertionMode: 'replaceSelection',
                selectionMode: 'item',
            });
        }
        // Restore scroll position after MathLive DOM changes
        if (scrollEl) {
            requestAnimationFrame(function () {
                scrollEl.scrollTop = savedScrollTop;
                scrollEl.scrollLeft = savedScrollLeft;
            });
        }
    };
    var ensureTrailingTextNode = function (editor) {
        var last = editor.lastChild;
        if (!last || last.nodeType !== Node.TEXT_NODE) {
            editor.appendChild(document.createTextNode(''));
        }
    };
    var placeCaretAfter = function (node) {
        var _a;
        // Focus editor first so the browser registers it as the active input context.
        // Without this, the first keypress after moving from a math-field is swallowed.
        (_a = editorRef.current) === null || _a === void 0 ? void 0 : _a.focus({ preventScroll: true });
        var range = document.createRange();
        var sel = window.getSelection();
        range.setStartAfter(node);
        range.collapse(true);
        sel === null || sel === void 0 ? void 0 : sel.removeAllRanges();
        sel === null || sel === void 0 ? void 0 : sel.addRange(range);
    };
    // Returns true when the entire math-field content is just an empty fraction
    // (both numerator and denominator contain only \placeholder{} tokens — no
    // user input). Used to delete the whole fraction on backspace.
    var isMfOnlyEmptyFrac = function (value) {
        var stripped = value.replace(/\\placeholder\{[^}]*\}/g, '').trim();
        return stripped === '\\frac{}{}';
    };
    var createMathField = function (editor, getColorEnum) {
        var _a, _b, _c;
        if (getColorEnum === void 0) { getColorEnum = false; }
        var mf = new MathfieldElement({
            contentPlaceholder: placeHolder,
        });
        // Set these as properties after creation (required for MathLive 0.108+)
        mf.smartFence = false;
        mf.smartMode = false;
        mf.setAttribute('math-virtual-keyboard-policy', 'off');
        // In manipulative label editors (fraction tiles etc.) characters should
        // render in text/upright mode so they match the surrounding plain text
        // visually. Without this, letters like 'a' are rendered in math-italic
        // (Latin Modern Math) which has a smaller x-height than the app font,
        // making mixed text+math labels look inconsistent.
        if (isManipulative) {
            mf.defaultMode = 'text';
        }
        // Completely hide the math-field from assistive technology. aria-hidden on
        // the host removes it from the AT tree entirely. The shadow-DOM container
        // span gets the same treatment (with a setTimeout retry for the span that
        // connectedCallback creates) so VoiceOver cannot reach the internal math
        // structure even when focus moves inside.
        mf.setAttribute('aria-hidden', 'true');
        var suppressMlA11y = function () {
            var sr = mf.shadowRoot;
            if (!sr)
                return;
            // Suppress aria-live/status elements via CSS
            if (!sr.querySelector('#ml-a11y-suppress')) {
                var s = document.createElement('style');
                s.id = 'ml-a11y-suppress';
                s.textContent = '[role="status"], [aria-live] { display: none !important; }';
                sr.appendChild(s);
            }
            // Hide the main shadow-DOM container from AT
            var container = sr.querySelector(':scope > span');
            if (container)
                container.setAttribute('aria-hidden', 'true');
            // Target .ML__keyboard-sink directly. MathLive's onSelectionDidChange
            // calls keyboardDelegate.setValue(latexExpanded) on every edit, writing
            // raw LaTeX like "\placeholder{}" into this contenteditable span's
            // textContent. iOS 18 VoiceOver respects aria-hidden on the element; iOS
            // 26+ VoiceOver reads the focused element's textContent directly, bypassing
            // aria-hidden on ancestors. The MutationObserver clears both aria-label
            // (which MathLive's setAriaLabel() overrides) and textContent (which VO
            // reads on iOS 26+) immediately after MathLive writes them. Clearing
            // textContent is safe because iOS input goes through the custom keypad
            // (mf.insert) — physical-keyboard events fire via keydown/beforeinput and
            // don't depend on the sink's text content.
            var sink = sr.querySelector('.ML__keyboard-sink, [part="keyboard-sink"]');
            if (sink && !sink.dataset.a11ySuppressed) {
                sink.dataset.a11ySuppressed = 'true';
                sink.setAttribute('aria-hidden', 'true');
                sink.setAttribute('tabindex', '-1');
                // Suppress the iOS native keyboard (math is entered via the custom
                // keypad). Set ONCE here — do NOT re-enforce via the MutationObserver
                // below: re-applying inputmode mid-interaction makes iOS select the
                // whole field on tap (denominator-placeholder tap selects everything).
                // Physical keyboards are unaffected (inputmode only gates the on-screen
                // keyboard). See docs/components/MathEditor.md "iPad / iOS 26".
                sink.setAttribute('inputmode', 'none');
                sink.setAttribute('aria-label', '');
                // Rewrite the sink's text content to U+200B so VoiceOver reads
                // nothing, without replacing the text node itself.
                //
                // Why in-place mutation matters: MathLive's setValue() calls
                //   sink.textContent = latex   (creates/replaces text node)
                //   selectAllChildren(sink)    (Range anchored on sink with endOffset=1)
                // Our MutationObserver fires as a microtask after both lines. If we
                // also do sink.textContent = ZWS we create a NEW text node — the old
                // Range endOffset=1 still points at 1 child so the selection is
                // technically valid. But if sink already had NO children (e.g. MathLive
                // cleared the sink in an input handler) and we add one, the previous
                // empty selection stays at offset=0 and the browser snaps the
                // document cursor back to the contenteditable. To avoid every edge
                // case, mutate .data on the existing Text node when one is present so
                // the child-count never changes; only fall back to textContent= when
                // there is genuinely no text node.
                var setSinkZWS_1 = function () {
                    var node = sink.firstChild;
                    if ((node === null || node === void 0 ? void 0 : node.nodeType) === Node.TEXT_NODE) {
                        if (node.data !== WHITESPACE_CHAR)
                            node.data = WHITESPACE_CHAR;
                    }
                    else if (sink.textContent !== WHITESPACE_CHAR) {
                        sink.textContent = WHITESPACE_CHAR;
                    }
                };
                setSinkZWS_1();
                var obs = new MutationObserver(function () {
                    if (sink.getAttribute('aria-label') !== '')
                        sink.setAttribute('aria-label', '');
                    if (sink.getAttribute('aria-hidden') !== 'true')
                        sink.setAttribute('aria-hidden', 'true');
                    setSinkZWS_1();
                });
                obs.observe(sink, {
                    attributes: true,
                    attributeFilter: ['aria-label', 'aria-hidden'],
                    childList: true,
                    characterData: true,
                    subtree: true,
                });
            }
        };
        suppressMlA11y();
        setTimeout(suppressMlA11y, 0);
        // Skip the math-field in the document Tab order. Without this, Tab from
        // the contenteditable parent lands inside the mf and the user gets
        // trapped (no keyboard-only path back out). Click / touch / programmatic
        // focus still work — tabindex="-1" only blocks the keyboard Tab walk.
        mf.setAttribute('tabindex', '-1');
        // Track this instance for cleanup later
        mathFieldInstancesRef.current.add(mf);
        var _col = '#000000';
        if (isManipulative) {
            _col = ((_a = data === null || data === void 0 ? void 0 : data.data) === null || _a === void 0 ? void 0 : _a.color) || '#000';
        }
        else {
            _col = color === 'BLACK' || !getColorEnum ? (_b = data === null || data === void 0 ? void 0 : data.data) === null || _b === void 0 ? void 0 : _b.color : colorProp || ColorEnum[color];
        }
        mf.applyStyle({ color: _col });
        if (!_col) {
            _col = colorProp || ColorEnum['BLACK'];
        }
        mf.setAttribute('color', _col);
        mf.style.color = _col;
        // Disable editing in viewonly mode
        if (viewOnlyRef.current) {
            mf.setAttribute('readonly', 'true');
            mf.readonly = true;
            mf.style.userSelect = 'none';
            mf.style.cursor = 'inherit';
            mf.style.pointerEvents = 'none';
            var cursorStyle = document.createElement('style');
            cursorStyle.textContent =
                ':host, * { pointer-events: none !important; } .ML__base { cursor: inherit !important; caret-color: transparent; }' +
                    PLACEHOLDER_FIX_CSS;
            (_c = mf.shadowRoot) === null || _c === void 0 ? void 0 : _c.appendChild(cursorStyle);
        }
        else {
            // On iPad/iOS, WebKit renders MathLive's internal shadow-DOM contentEditable
            // selection visibly. MathLive selects its keyboard-shim element (setting its
            // content to the current LaTeX value like "$ \placeholder{} $") so the iOS
            // virtual keyboard can replace it with new input. That selection flashes on
            // screen as highlighted text. MathLive drives its own cursor via CSS classes
            // (.ML__cursor), so suppressing the native ::selection highlight here is safe.
            var selectionStyle_1 = document.createElement('style');
            selectionStyle_1.textContent =
                '::selection { background: transparent; color: inherit; }' + PLACEHOLDER_FIX_CSS;
            // Shadow root may not be open yet at construction time — retry once connected.
            if (mf.shadowRoot) {
                mf.shadowRoot.appendChild(selectionStyle_1);
            }
            else {
                setTimeout(function () {
                    var _a;
                    (_a = mf.shadowRoot) === null || _a === void 0 ? void 0 : _a.appendChild(selectionStyle_1);
                }, 0);
            }
            // Firefox: temporarily disable pointer-events before DOM insertion.
            // connectedCallback can trigger pointerdown → onFocus → onBlur before
            // MathLive's ariaLiveText is initialized, causing a crash.
            mf.style.pointerEvents = 'none';
        }
        // Re-enable pointer-events after connectedCallback completes.
        setTimeout(function () {
            if (!viewOnlyRef.current && mf.isConnected) {
                mf.style.pointerEvents = '';
            }
        }, 0);
        // Prevent the contentEditable parent from intercepting pointer/mouse events,
        // so the mathfield can handle its own cursor positioning and text selection.
        // All three events must be stopped: pointerdown fires first but mousedown
        // still fires separately and would cause the contentEditable to reposition
        // the browser caret, overriding MathLive's internal cursor placement.
        // Firefox: ensure ariaLiveText fallback exists BEFORE MathLive's internal
        // shadow DOM handlers fire. Capture phase on the host element fires before
        // the shadow DOM's event listeners, preventing the onBlur crash.
        mf.addEventListener('pointerdown', function () { return ensureAriaLiveText(mf); }, {
            capture: true,
        });
        mf.addEventListener('focusin', function () {
            ensureAriaLiveText(mf);
            // Defer so MathLive's internal model is settled before we read position
            requestAnimationFrame(function () { return announceFracPosition(mf); });
        }, {
            capture: true,
        });
        mf.addEventListener('pointerdown', function (e) {
            e.stopPropagation();
            // (window as any).__jumpSnap?.('mf-tap', mf, `${idPreffix}[${index}]`);
        });
        mf.addEventListener('mousedown', function (e) {
            e.stopPropagation();
            // Only preventDefault on the first mousedown (when not yet focused) so
            // we can manually focus with preventScroll. When the math-field is
            // already focused (e.g. tapping the denominator after typing the
            // numerator on iPad), preventDefault here blocks the browser's native
            // tap-to-position so MathLive can't resolve the hit-test and falls
            // back to select-all of the parent expression.
            if (!mf.matches(':focus')) {
                e.preventDefault();
                mf.focus({ preventScroll: true });
            }
        });
        mf.addEventListener('click', function (e) {
            e.stopPropagation();
            // This click never reaches the editor div's onClick (stopped above),
            // so the link toolbar's clear-on-click-elsewhere logic there never
            // runs — clear it here instead.
            clickedAnchorRef.current = null;
            setActiveLink(null);
        });
        // MathLive's event is `selection-change` (with hyphen) — the
        // standard DOM `selectionchange` event is fired on `document`, not
        // on math-field, so listening for it here would never fire. The
        // formatting-state sync below + the fraction-position announcement
        // both depend on this.
        mf.addEventListener('selection-change', function () {
            var _a;
            emitFormattingState(mf);
            // Announce caret position when entering / leaving a \frac argument
            // so screen readers say "Numerator" / "Denominator" the moment the
            // user arrives in the input box (after pressing the / fraction key,
            // arrowing across, or tabbing through placeholders). Cached against
            // the mf so we only announce on transitions, not on every keystroke.
            //
            // MathLive's atom model exposes `parentBranch` on each atom: inside
            // a \frac, 'above' = numerator and 'below' = denominator. We walk
            // up from the cursor's current atom until we find a genfrac
            // ancestor and read the branch the cursor sits in.
            try {
                var model = (_a = mf._mathfield) === null || _a === void 0 ? void 0 : _a.model;
                if (!model)
                    return;
                var atom = model.at(model.position);
                var branchInFrac = 'outside';
                // Track the branch as we walk up — the LAST non-frac ancestor we
                // pass through before hitting a genfrac is the branch the cursor
                // is in. Stop at the first genfrac ancestor.
                var currentBranch = atom === null || atom === void 0 ? void 0 : atom.parentBranch;
                while (atom && atom.type !== 'root') {
                    if (atom.type === 'genfrac') {
                        if (currentBranch === 'above' || currentBranch === 'below') {
                            branchInFrac = currentBranch;
                        }
                        break;
                    }
                    currentBranch = atom.parentBranch;
                    atom = atom.parent;
                }
                var prev = lastFracBranchRef.current.get(mf);
                if (prev !== branchInFrac) {
                    lastFracBranchRef.current.set(mf, branchInFrac);
                    if (branchInFrac === 'above') {
                        announce(t('text.fractionNumerator'));
                    }
                    else if (branchInFrac === 'below') {
                        announce(t('text.fractionDenominator'));
                    }
                    // No announcement when transitioning OUT of a fraction — the
                    // surrounding context (next atom typed, or MathLive's own
                    // spoken-text on blur) gives the user enough orientation.
                }
            }
            catch (_b) {
                // MathLive private API may shift across versions — silently bail.
            }
        });
        // iPad fall-back for taps that land MathLive on a select-all because the
        // hit-test couldn't resolve to a precise atom (typically tapping an empty
        // placeholder like the denominator while the mf is already focused
        // from typing the numerator). The focus listener has the same logic, but
        // it only fires when focus enters the mf — not when the user re-taps
        // inside an already-focused mf. Only collapses when at least one
        // placeholder is present so deliberate select-all on filled content
        // (Cmd/Ctrl+A, drag-select) is preserved.
        // Collapse target: the offset under the tap point, so tapping the
        // denominator puts the caret in the denominator (and the numerator in the
        // numerator) instead of always jumping to the next placeholder.
        // Taps only: a pointer that MOVED between down and up is a drag-select, and
        // a drag that happens to cover the whole expression is a deliberate
        // select-all the user is about to act on (delete, recolour, bold) — never
        // collapse that.
        // iPad fallback #2: even after MathLive's own internal cursor (below) is
        // correctly resolved, the browser's native DOM selection on the
        // surrounding contentEditable cell can separately drift onto the
        // <math-field> element's own containing editorDiv — landing on an
        // adjacent (spacer) text node — anywhere from ~0 up to ~500ms after a tap
        // (confirmed via on-device logging; this is a native gesture-recognition
        // delay, not something our own code schedules). If left alone, the next
        // keystroke is handled as plain-text input on that div, which deletes
        // the "selected" math-field and replaces it with the typed character.
        // `selectionchange` fires on every native selection change regardless of
        // timing, so it catches the drift whenever it actually happens rather
        // than guessing a delay. The watch is intentionally short-lived (~1s,
        // well past the observed drift) and stops itself — a long-lived listener
        // would risk fighting a later LEGITIMATE click out of the fraction into
        // surrounding plain text, which also transits focus through editorDiv.
        var reclaimFocusIfDrifted = function () {
            var _a;
            if (!mf.isConnected)
                return;
            if (document.activeElement === mf)
                return;
            // Focusing the math-field directly moves the browser's native
            // selection into it as part of the same focus change. A separate
            // window.getSelection().removeAllRanges() call was tried first, but it
            // forces a moment where the outer editorDiv blurs with nowhere (yet)
            // for focus to go, which the app's own click-away deselect logic
            // misread as the user leaving the manipulative, deselecting it.
            var ranges = (_a = mf.selection) === null || _a === void 0 ? void 0 : _a.ranges;
            mf.focus({ preventScroll: true });
            // MathLive's selection RANGE survives the drift untouched — only
            // document.activeElement moves away and back. But the browser paints a
            // math-field's selection as "inactive" (no visible highlight) while it
            // isn't the actually-focused element, and merely restoring focus
            // doesn't itself force a repaint of that highlight. Re-apply the same
            // range after refocusing so MathLive redraws it as active (confirmed
            // via on-device testing: without this, typing into the reclaimed
            // denominator still worked, but the tapped placeholder never visibly
            // highlighted).
            if (ranges) {
                mf.selection = { ranges: ranges };
            }
        };
        var stopSelectionDriftWatch = null;
        var watchForSelectionDrift = function () {
            stopSelectionDriftWatch === null || stopSelectionDriftWatch === void 0 ? void 0 : stopSelectionDriftWatch();
            var debounceId = null;
            var handler = function () {
                // Only reclaim when the drift lands EXACTLY on this field's own
                // editorDiv (not e.g. a toolbar button or a different cell/widget
                // the user deliberately moved to) — that precise target is what
                // distinguishes the accidental fallback from a legitimate focus
                // change elsewhere, which must never be overridden.
                if (!(mf.isConnected && document.activeElement === editorRef.current)) {
                    if (debounceId) {
                        clearTimeout(debounceId);
                        debounceId = null;
                    }
                    return;
                }
                // Don't react to the very first sighting: iOS Safari does a normal,
                // self-correcting math-field → editorDiv → math-field focus bounce
                // on every tap into an expression, not just the buggy drift case.
                // Reclaiming mid-bounce interrupted MathLive's own focus resolution
                // and stripped the tapped placeholder's visual selection highlight,
                // even though the caret still ended up in the right place. Wait
                // briefly for the bounce to resolve on its own; only intervene if
                // the drift is still there afterward.
                if (debounceId)
                    return;
                debounceId = setTimeout(function () {
                    debounceId = null;
                    if (mf.isConnected && document.activeElement === editorRef.current) {
                        reclaimFocusIfDrifted();
                    }
                }, 60);
            };
            document.addEventListener('selectionchange', handler);
            var timeoutId = setTimeout(function () {
                document.removeEventListener('selectionchange', handler);
                if (debounceId)
                    clearTimeout(debounceId);
                stopSelectionDriftWatch = null;
            }, 1000);
            stopSelectionDriftWatch = function () {
                clearTimeout(timeoutId);
                if (debounceId)
                    clearTimeout(debounceId);
                document.removeEventListener('selectionchange', handler);
            };
        };
        var pointerDownAt = null;
        mf.addEventListener('pointerdown', function (ev) {
            var pe = ev;
            pointerDownAt = { x: pe.clientX, y: pe.clientY };
        });
        mf.addEventListener('pointerup', function (ev) {
            var pe = ev;
            var tapX = pe.clientX;
            var tapY = pe.clientY;
            var down = pointerDownAt;
            pointerDownAt = null;
            if (!isTapWithinSlop(down, tapX, tapY))
                return;
            watchForSelectionDrift();
            requestAnimationFrame(function () {
                if (!mf.isConnected)
                    return;
                try {
                    if (!isMathSelectAll(mf))
                        return;
                    if (!hasPlaceholder(mf.value))
                        return;
                    collapseCaretAtPoint(mf, tapX, tapY);
                }
                catch (_a) {
                    /* ignore — MathLive private API may shift across versions */
                }
            });
        });
        // Intercept Tab before MathLive's shadow-DOM keyboardSink (capture phase).
        //   • Forward Tab + keypad visible → first keypad button ('1') so the
        //     user can drive math input from the keyboard.
        //   • Otherwise (Shift+Tab, or no keypad mounted) → exit the math editor
        //     to the nearest tabbable element outside `.mathEditorParent`.
        // Arrow keys handle in-math navigation between groups. The mf itself
        // is tabindex="-1" so the document Tab walk never lands here from
        // outside the mf — the user only reaches this code path after a click
        // or programmatic focus puts the caret inside an mf.
        // Also blocks MathLive's moveToNextGroup command, which crashes when
        // the internal model state is undefined.
        mf.addEventListener('keydown', function (e) {
            var _a, _b, _c, _d;
            if (e.key !== 'Tab')
                return;
            e.stopImmediatePropagation();
            e.preventDefault();
            if (!e.shiftKey && keypadWrapperRef.current) {
                var firstBtn = keypadWrapperRef.current.querySelector('button:not([disabled]):not([tabindex="-1"])');
                if (firstBtn) {
                    firstBtn.focus();
                    return;
                }
            }
            // Delegate to the parent's Tab handler when one is provided (e.g. table
            // cells), so cell navigation matches regular-text Tab behaviour exactly.
            if (onTabRef.current) {
                onTabRef.current(e.shiftKey);
                return;
            }
            // No keypad to land in (Shift+Tab, or keypad isn't mounted yet) —
            // exit the math editor entirely.
            var root = (_a = mf.closest('.mathEditorParent')) !== null && _a !== void 0 ? _a : editorRef.current;
            if (!root)
                return;
            var tabbableSelector = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), [contenteditable="true"]';
            var all = Array.from(document.querySelectorAll(tabbableSelector)).filter(function (el) {
                if (root.contains(el))
                    return false;
                if (el.offsetParent === null && el !== document.body)
                    return false;
                return true;
            });
            var target = null;
            if (e.shiftKey) {
                for (var _i = 0, all_1 = all; _i < all_1.length; _i++) {
                    var el = all_1[_i];
                    if (root.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_PRECEDING) {
                        target = el;
                    }
                }
            }
            else {
                target =
                    (_b = all.find(function (el) { return root.compareDocumentPosition(el) & Node.DOCUMENT_POSITION_FOLLOWING; })) !== null && _b !== void 0 ? _b : null;
            }
            if (target) {
                target.focus();
            }
            else {
                (_d = (_c = document.activeElement) === null || _c === void 0 ? void 0 : _c.blur) === null || _d === void 0 ? void 0 : _d.call(_c);
            }
        }, { capture: true });
        // Escape inside the math-field: exit cleanly. Without this, MathLive's
        // own Esc handling can leave focus stuck on the shadow-DOM keyboard sink
        // — the user can't Tab forward/backward and arrow keys also stop
        // working from the user's perspective. We close the keypad, blur the mf,
        // and place the caret in the contenteditable editor just after the mf
        // so the user lands in a known-good text-editing state.
        mf.addEventListener('keydown', function (e) {
            var _a, _b;
            if (e.key !== 'Escape')
                return;
            e.stopImmediatePropagation();
            e.preventDefault();
            setmathKeyboardVisible(false);
            setActiveMathText && setActiveMathText(-1);
            var editorEl = mf.closest('[contenteditable]');
            if (!editorEl) {
                (_b = (_a = mf).blur) === null || _b === void 0 ? void 0 : _b.call(_a);
                return;
            }
            editorEl.focus({ preventScroll: true });
            var sel = window.getSelection();
            var parent = mf.parentNode;
            if (sel && parent) {
                var range = document.createRange();
                var mfIdx = Array.from(parent.childNodes).indexOf(mf);
                // Caret immediately after the mf — past any trailing spacer the
                // browser collapses through.
                range.setStart(parent, mfIdx + 1);
                range.collapse(true);
                sel.removeAllRanges();
                sel.addRange(range);
            }
            if (lastFocusedMathField.current === mf) {
                lastFocusedMathField.current = null;
            }
        }, { capture: true });
        // Arrow exits at the boundaries of the math-field, in step with the
        // editor's mf-adjacent enter handlers:
        //   • ArrowLeft at offset 0 (start)        → exit to the editor caret
        //                                            position immediately before
        //                                            the mf
        //   • ArrowRight at lastOffset (end)        → exit to the editor caret
        //                                            position immediately after
        //                                            the mf
        // Any other position inside the mf falls through to MathLive's native
        // arrow stepping so the user can surf through math content normally.
        mf.addEventListener('keydown', function (e) {
            if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight')
                return;
            if (e.shiftKey || e.altKey || e.metaKey || e.ctrlKey)
                return;
            // Use the public `position` getter — simpler than digging into
            // `selection.ranges` and stable across MathLive minor versions.
            var pos = mf.position;
            var lastOffset = mf.lastOffset;
            if (typeof pos !== 'number' || typeof lastOffset !== 'number')
                return;
            // Range-extending (rare without shift but possible) stays with
            // MathLive — only collapsed-cursor boundary exits route here.
            var sel = mf.selection;
            var ranges = sel === null || sel === void 0 ? void 0 : sel.ranges;
            if (Array.isArray(ranges) && ranges.length === 1) {
                var r = ranges[0];
                var start = Array.isArray(r) ? r[0] : r === null || r === void 0 ? void 0 : r.start;
                var end = Array.isArray(r) ? r[1] : r === null || r === void 0 ? void 0 : r.end;
                if (typeof start === 'number' && typeof end === 'number' && start !== end) {
                    return;
                }
            }
            // Left exits ONLY at offset 0 (start). Right exits ONLY at lastOffset
            // (end). Anywhere else falls through so MathLive moves the cursor
            // through math content normally.
            var exitLeft = e.key === 'ArrowLeft' && pos === 0;
            var exitRight = e.key === 'ArrowRight' && pos === lastOffset;
            console.log('[mf-arrow-debug]', {
                key: e.key,
                pos: pos,
                lastOffset: lastOffset,
                exitLeft: exitLeft,
                exitRight: exitRight,
                willExit: exitLeft || exitRight,
            });
            if (!exitLeft && !exitRight)
                return;
            var editorEl = mf.closest('[contenteditable]');
            if (!editorEl)
                return;
            e.stopImmediatePropagation();
            e.preventDefault();
            editorEl.focus({ preventScroll: true });
            var winSel = window.getSelection();
            var parent = mf.parentNode;
            if (!winSel || !parent)
                return;
            var range = document.createRange();
            var mfIdx = Array.from(parent.childNodes).indexOf(mf);
            range.setStart(parent, exitLeft ? mfIdx : mfIdx + 1);
            range.collapse(true);
            winSel.removeAllRanges();
            winSel.addRange(range);
            announce('Exited math expression');
        }, { capture: true });
        // Prevent MathLive's internal scrollIntoView from scrolling the outer container.
        // The parent .mainHolder uses transform: scale(zoomLevel), causing the browser to
        // miscalculate scroll offsets and push the entire view out of the visible area.
        mf.scrollIntoView = function () { };
        // Prevent the browser's native "keep focused element in view" auto-scroll on
        // physical keyboard input. The whiteboard scroll container wraps a transform:
        // scale() element, so the browser miscalculates the required scroll offset and
        // jumps the view. The in-app keypad uses mf.insert() which only triggers
        // MathLive's scrollIntoView (already no-op'd above) and doesn't hit this path.
        mf.addEventListener('keydown', function (ev) {
            var _a, _b, _c, _d;
            // Preserve scroll on the whiteboard container (transform: scale() miscalc)
            var scrollEl = editor.closest('.customScrollbarContent');
            var savedLeft = (_a = scrollEl === null || scrollEl === void 0 ? void 0 : scrollEl.scrollLeft) !== null && _a !== void 0 ? _a : 0;
            var savedTop = (_b = scrollEl === null || scrollEl === void 0 ? void 0 : scrollEl.scrollTop) !== null && _b !== void 0 ? _b : 0;
            // Preserve window + document scroll too — structural inserts like
            // `/` → \frac cause MathLive to shift focus inside the shadow DOM
            // which VoiceOver + the browser respond to by scrolling the page.
            // Covers Safari/iPad VO jumpiness on fraction / sqrt / exponent inputs.
            var structuralKey = ev.key === '/' ||
                ev.key === '^' ||
                ev.key === '_' ||
                ev.key === '(' ||
                ev.key === '{' ||
                ev.key === '[' ||
                (ev.altKey && ['f', 'F', 'e', 'E', 'r', 'R'].includes(ev.key));
            var winX = window.scrollX;
            var winY = window.scrollY;
            var docEl = document.scrollingElement;
            var docLeft = (_c = docEl === null || docEl === void 0 ? void 0 : docEl.scrollLeft) !== null && _c !== void 0 ? _c : winX;
            var docTop = (_d = docEl === null || docEl === void 0 ? void 0 : docEl.scrollTop) !== null && _d !== void 0 ? _d : winY;
            var restore = function () {
                if (scrollEl) {
                    scrollEl.scrollLeft = savedLeft;
                    scrollEl.scrollTop = savedTop;
                }
                if (structuralKey) {
                    window.scrollTo(winX, winY);
                    if (docEl) {
                        docEl.scrollLeft = docLeft;
                        docEl.scrollTop = docTop;
                    }
                }
            };
            requestAnimationFrame(restore);
            // Second rAF catches late scrolls from MathLive's async structural
            // changes (seen on fraction insert with VoiceOver active).
            if (structuralKey) {
                requestAnimationFrame(function () { return requestAnimationFrame(restore); });
            }
        }, { capture: true });
        mf.addEventListener('copy', function (e) { return e.preventDefault(); });
        mf.addEventListener('paste', function (e) { return e.preventDefault(); });
        mf.addEventListener('cut', function (e) { return e.preventDefault(); });
        mf.addEventListener('keydown', function (ev) {
            if (viewOnlyRef.current) {
                ev.preventDefault();
                return;
            }
            // Prevent copy/paste/cut shortcuts
            if ((ev.ctrlKey || ev.metaKey) && ['c', 'v', 'x'].includes(ev.key.toLowerCase())) {
                ev.preventDefault();
                ev.stopPropagation();
                return;
            }
            // Prevent alphabet typing inside math-field — only math input allowed
            if (ev.key.length === 1 &&
                /[a-zA-Z]/.test(ev.key) &&
                !ev.ctrlKey &&
                !ev.metaKey &&
                !ev.altKey) {
                ev.preventDefault();
                return;
            }
            // Prevent Enter key inside math-field — line breaks make no sense
            // in a single-line math expression. Text editor handles its own
            // Enter key separately (inserts <br>).
            if (ev.key === 'Enter' || ev.keyCode === 13) {
                ev.preventDefault();
                ev.stopPropagation();
                return;
            }
            if ((ev.key === 'Backspace' || ev.key === 'Delete') &&
                (mf.value === '' || isMfOnlyEmptyFrac(mf.value))) {
                ev.preventDefault();
                var next = mf.nextSibling;
                mathFieldInstancesRef.current.delete(mf);
                if (lastFocusedMathField.current === mf) {
                    lastFocusedMathField.current = null;
                }
                mf.remove();
                announceAssertive(t('text.mathEditor.mathFieldDeleted'));
                batchDisposeMathfields([mf]);
                // Always hide keyboard — cursor moves to contentEditable, not another mathfield.
                // If the user clicks another mathfield, its focus handler will re-open it.
                setmathKeyboardVisible(false);
                setActiveMathText && setActiveMathText(-1);
                if (next)
                    placeCaretAfter(next);
                else
                    editor.focus({ preventScroll: true });
                // Sync data after deletion
                getMathData();
                return;
            }
            // Prevent input if maxCharacters limit is reached
            if (maxCharacters) {
                var currentCount = getTotalCharacterCount();
                var currentMfValue = mf.value || '';
                // For non-deletion keys, check if we're at limit.
                // Exception: when cursor is inside a \frac argument, Math.max() makes
                // the total look full even if one side is empty — skip the block here
                // and let the input-event revert handler enforce the actual per-side limit.
                if (ev.key !== 'Backspace' &&
                    ev.key !== 'Delete' &&
                    currentCount >= maxCharacters &&
                    !isInFrac(mf)) {
                    // Allow navigation keys
                    if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End', 'Tab'].includes(ev.key)) {
                        ev.preventDefault();
                        announce(t('text.mathEditor.charLimitReached'));
                        return;
                    }
                }
            }
        });
        // iPad: virtual keyboard backspace fires beforeinput on the math-field
        // instead of keydown. When the math-field is empty, remove it and move
        // focus back to the contentEditable editor.
        mf.addEventListener('beforeinput', function (ev) {
            var inputEv = ev;
            if (inputEv.inputType === 'deleteContentBackward' &&
                (mf.value === '' || isMfOnlyEmptyFrac(mf.value))) {
                ev.preventDefault();
                var next = mf.nextSibling;
                mathFieldInstancesRef.current.delete(mf);
                if (lastFocusedMathField.current === mf) {
                    lastFocusedMathField.current = null;
                }
                mf.remove();
                batchDisposeMathfields([mf]);
                setmathKeyboardVisible(false);
                setActiveMathText && setActiveMathText(-1);
                if (next)
                    placeCaretAfter(next);
                else
                    editor.focus({ preventScroll: true });
                getMathData();
            }
        });
        // When the user taps a placeholder inside the math-field, iOS scrolls the
        // entire window to keep the focused input visible when the keyboard opens.
        // The lock must stay active for the entire focus session (not just 500ms),
        // because iOS may issue multiple scroll adjustments as the keyboard settles.
        // Strategy:
        //   pointerdown/touchstart → snapshot scroll position, install window lock
        //   mf focus             → confirm lock is active (belt-and-suspenders)
        //   mf blur              → remove window lock
        // Install on first touch (before focus fires) to catch the earliest scroll.
        // Uses the module-level singleton lock (see installWinLock) so listeners
        // can't leak/fight across math-field recreation.
        // The scroll container to pin (whiteboard canvas, JumpBar, or Table cell).
        var _scrollContainer = function () {
            var _a;
            return mf.closest('.customScrollbarContent') ||
                ((_a = editorRef.current) === null || _a === void 0 ? void 0 : _a.closest('.customScrollbarContent'));
        };
        var _touchLockFired = false;
        var _onTouch = function () {
            if (!needsIOSScrollLock())
                return;
            if (_touchLockFired)
                return;
            _touchLockFired = true;
            installWinLock(window.scrollX, window.scrollY);
            installElLock(_scrollContainer());
            setTimeout(function () {
                _touchLockFired = false;
            }, 600);
        };
        mf.addEventListener('pointerdown', _onTouch);
        mf.addEventListener('touchstart', _onTouch, { passive: true });
        // Clicking the expression is the natural way to ask for the keypad back
        // after it was dismissed. When the field still holds DOM focus no 'focus'
        // event fires on that click, so the open-on-focus path below never ran and
        // the keypad stayed gone (user: "I can't get the keypad back"). Only act on
        // the already-focused case — otherwise the focus handler owns the open, and
        // opening here would race its select-all/caret fallbacks.
        mf.addEventListener('pointerdown', function (e) {
            if (viewOnlyRef.current || mathKeyboardVisibleRef.current)
                return;
            if (document.activeElement !== mf)
                return;
            // Primary button, no modifiers: a right-click (context menu) or a
            // shift/ctrl-click (adding this widget to a multi-selection) is not a
            // request to edit.
            if (e.button !== 0 || e.shiftKey || e.ctrlKey || e.metaKey)
                return;
            // Same gate as the focus path below: only open when this editor really is
            // the active selection, or the keypad could open with no matching
            // isFocused→false transition to ever close it again.
            if (!isFocusedRef.current)
                return;
            lastFocusedMathField.current = mf;
            mathKeyboardVisibleRef.current = true;
            setmathKeyboardVisible(true);
            setActiveMathText && typeof index == 'number' && setActiveMathText(index);
        });
        // Re-confirm lock on actual focus (covers keyboard navigation). No-op if
        // the singleton is already installed (keeps the original saved position).
        mf.addEventListener('focus', function () {
            if (!needsIOSScrollLock())
                return;
            installWinLock(window.scrollX, window.scrollY);
            installElLock(_scrollContainer());
        });
        // Release on blur — debounced inside removeWinLock/removeElLock so it
        // survives the keypad blur→refocus churn and only tears down when the user
        // genuinely leaves the math-field.
        mf.addEventListener('blur', function () {
            removeWinLock();
            removeElLock();
        });
        // Add input listener to prevent input when limit is reached
        if (maxCharacters) {
            // Store previous value for potential revert
            mf.setAttribute('data-prev-value', '');
            mf.setAttribute('data-prev-position', '');
            mf.addEventListener('input', function () {
                var _a;
                var currentCount = getTotalCharacterCount();
                var prevValue = mf.getAttribute('data-prev-value') || '';
                if (currentCount > maxCharacters) {
                    // Revert to previous value if exceeded.
                    //
                    // Re-assigning `value` rebuilds MathLive's model and drops the caret
                    // at end-of-field, which for a `\frac` is the DENOMINATOR. Nothing
                    // signalled that the rejected keystroke had also moved the caret, so
                    // the user's next character silently edited the *other* side of the
                    // fraction ("12" + rejected "3" + "9" → 12/9). Restore the caret
                    // offset captured alongside prevValue so a refusal is inert: the next
                    // character edits the side the user is looking at.
                    var prevPosition = mf.getAttribute('data-prev-position') || '';
                    mf.value = prevValue;
                    if (prevPosition !== '') {
                        var pos = Number(prevPosition);
                        if (Number.isFinite(pos)) {
                            try {
                                mf.position = pos;
                            }
                            catch (_b) {
                                // Offset no longer addressable in the rebuilt model — leaving
                                // MathLive's default caret is still better than throwing.
                            }
                        }
                    }
                    announce(t('text.mathEditor.charLimitReached'));
                }
                else {
                    // Update previous value + the caret that goes with it. Both are read
                    // together on the next over-limit input, so they must be captured
                    // together or the restored offset would refer to a different model.
                    mf.setAttribute('data-prev-value', mf.value || '');
                    mf.setAttribute('data-prev-position', String((_a = mf.position) !== null && _a !== void 0 ? _a : ''));
                }
            });
        }
        // Live-sync edits inside the math-field to the parent via cb. Without
        // this, the keydown listener only calls getMathData on Backspace/Delete
        // when the field is empty, so typing inside `\frac{...}{...}` (e.g.
        // bumping 13 → 14) never propagates. Consumers like the JumpBar need
        // every keystroke to recompute the bar length from the new fraction.
        // force=true bypasses the mathKeyboardVisible bail since the keyboard
        // is open while the user is editing. Defer one microtask so MathLive
        // has finished updating mf.value before calcMathData walks the DOM.
        var liveSync = function () {
            if (viewOnlyRef.current)
                return;
            Promise.resolve().then(function () {
                var _a;
                (_a = getMathDataRef.current) === null || _a === void 0 ? void 0 : _a.call(getMathDataRef, false, true);
            });
        };
        mf.addEventListener('input', function () {
            // iOS 26: revert iOS's per-input document + container scroll before paint.
            if (needsIOSScrollLock()) {
                holdWindowScrollBriefly();
                holdElScrollBriefly(_scrollContainer());
            }
            // (window as any).__jumpSnap?.('mf-input', mf, `${idPreffix}[${index}]`);
        });
        mf.addEventListener('input', liveSync);
        mf.addEventListener('change', liveSync);
        mf.addEventListener('focus', function (e) {
            if (viewOnlyRef.current)
                return;
            // Ignore a stray focus landing here immediately after a DOM rebuild
            // (initial mount, duplication, or content reset) — a duplicated text
            // item is auto-selected on mount, so without this guard that stray
            // focus would open the keypad before the user has clicked anything.
            if (Date.now() - justHydratedRef.current < 300)
                return;
            // Track the element that triggered keypad open (e.g., math keypad button)
            var related = e.relatedTarget;
            var openKeyboard = function () {
                var _a;
                if (related)
                    keypadTriggerRef.current = related;
                lastFocusedMathField.current = mf;
                setmathKeyboardVisible(true);
                setActiveMathText && typeof index == 'number' && setActiveMathText(index);
                if (maxCharacters) {
                    mf.setAttribute('data-prev-value', mf.value || '');
                    mf.setAttribute('data-prev-position', String((_a = mf.position) !== null && _a !== void 0 ? _a : ''));
                }
                // Announce math-field focus for SR users
                var editor = editorRef.current;
                var mfIndex = editor
                    ? Array.from(editor.querySelectorAll('math-field')).indexOf(mf) + 1
                    : 1;
                announce("Math expression ".concat(mfIndex, ". Use the keypad or keyboard to edit. Alt+K opens keypad."));
                // iPad fallback: if MathLive lands on a select-all because the tap
                // couldn't be resolved to a precise position (typical when tapping
                // an empty placeholder like a fraction denominator), collapse the
                // selection — preferring the next empty placeholder, otherwise the
                // end of the expression.
                watchForSelectionDrift();
                requestAnimationFrame(function () {
                    if (!mf.isConnected)
                        return;
                    try {
                        if (!isMathSelectAll(mf))
                            return;
                        collapseCaretAtPoint(mf);
                    }
                    catch (_a) {
                        /* ignore — MathLive private API may shift across versions */
                    }
                });
            };
            // A leftover native DOM focus can land on this math-field (e.g. the
            // browser re-firing focus during a shift-click) even when our own
            // selection logic has NOT made this editor the active one — e.g. it's
            // being added to a multi-selection rather than edited alone. Only open
            // the keyboard when this editor is actually the sole focused/selected
            // item; otherwise the keyboard could open and never get a matching
            // isFocused→false transition to close it again.
            if (isFocusedRef.current) {
                e.preventDefault();
                openKeyboard();
                return;
            }
            // First click on a math-field in a not-yet-selected box: MathLive calls
            // mf.focus() from its own pointerdown handler, which fires (and this
            // 'focus' listener with it) BEFORE React commits the click's
            // setSelectedTool update and re-syncs isFocusedRef — so isFocusedRef
            // is still stale/false here even though this box is about to become
            // the sole selection. Wait two frames for that commit to land, then
            // retry once; bail if focus moved elsewhere in the meantime (e.g. a
            // genuine multi-select, where isFocusedRef correctly stays false).
            requestAnimationFrame(function () {
                requestAnimationFrame(function () {
                    if (document.activeElement !== mf)
                        return;
                    if (!isFocusedRef.current)
                        return;
                    openKeyboard();
                });
            });
        });
        mf.addEventListener('blur', function (e) {
            var _a, _b, _c;
            var relatedTarget = e.relatedTarget;
            if (viewOnlyRef.current) {
                setmathKeyboardVisible(false);
                setActiveMathText && setActiveMathText(-1);
                return;
            }
            var editor = editorRef.current;
            // Announce spoken-math value via MathLive's getValue('spoken-text') API
            try {
                var spoken = (_b = (_a = mf).getValue) === null || _b === void 0 ? void 0 : _b.call(_a, 'spoken-text');
                if (spoken && typeof spoken === 'string' && spoken.trim()) {
                    announce("Math expression updated: ".concat(spoken));
                }
            }
            catch (_d) {
                /* ignore if API unavailable */
            }
            // If focus moved within the same editor, hide the keypad only.
            if (editor && relatedTarget && editor.contains(relatedTarget)) {
                // Opt-in via stickyKeypadAfterInsertOnTouch: keep the math context
                // alive instead of closing the keypad on every internal blur.
                // Required when the math-trigger button is a sibling of the
                // editorDiv inside the same focused subtree (JumpBar / CustomTick
                // / fraction-tile labels). Two distinct triggers cause this branch
                // to fire and both should be sticky:
                //   1. iOS Safari oscillates focus between a freshly-inserted
                //      math-field and its parent editorDiv (iPad-specific).
                //   2. MathLive's internal cursor moves during mf.insert(...)
                //      for fraction / symbols can blur the host mf with
                //      relatedTarget = the editorDiv (desktop + iPad).
                // The old 500 ms post-insertMath gate covered (1) but lapsed
                // between keypad button clicks (every "+" or "/" press) so the
                // keypad closed mid-edit. Holding the math-field focus
                // unconditionally for stickyKeypadAfterInsertOnTouch consumers
                // matches their intent ("user is editing a single math expression,
                // never the surrounding text") and lets users keep typing on the
                // keypad continuously. The keypad still closes when the editor
                // genuinely loses selection — that runs through the !isFocused
                // useEffect at line 391 below, which is unaffected.
                if (stickyKeypadAfterInsertOnTouch) {
                    requestAnimationFrame(function () {
                        if (mf.isConnected)
                            safeFocusMathField(mf);
                    });
                    return;
                }
                // Non-sticky consumers (table cells, canvas text boxes) mix plain text
                // and math in one editor, so an internal blur normally DOES mean the
                // user left the math context — the keypad must close. But on iPad a tap
                // *inside* the expression (e.g. a fraction's denominator) makes Safari
                // bounce focus mf → editorDiv → mf within the same gesture, and closing
                // synchronously killed the keypad mid-edit. Defer one frame and skip the
                // close when focus landed on ANY math-field in this editor.
                //
                // "Any", not just this one: clicking straight from expression 1 to
                // expression 2 in the same box runs the new field's focus handler
                // (setmathKeyboardVisible(true)) synchronously in the blur's own task,
                // so a deferred close lands a frame LATER and wins — the keypad
                // disappeared and nothing reopened it. Skipping the close is safe
                // because the keypad is anchored to the editor box, not to the
                // individual field, so no reposition is owed on a field switch.
                requestAnimationFrame(function () {
                    var _a;
                    // Teardown between the blur and this frame (popup closed, page
                    // switched, row deleted) — the keypad went with it. Bail rather than
                    // clear an activeMathText that now belongs to a different editor.
                    // rAF can be parked for a long time in a hidden tab, so this matters.
                    if (!mf.isConnected)
                        return;
                    var active = document.activeElement;
                    var ed = (_a = editorRef.current) !== null && _a !== void 0 ? _a : editor;
                    if ((active === null || active === void 0 ? void 0 : active.nodeName) === 'MATH-FIELD' && (ed === null || ed === void 0 ? void 0 : ed.contains(active)))
                        return;
                    setmathKeyboardVisible(false);
                    setActiveMathText && setActiveMathText(-1);
                });
                return;
            }
            // If focus moved to the keypad, do nothing — the keypad handler
            // will re-focus the mathfield and insert a value.
            if (keypadWrapperRef.current &&
                relatedTarget &&
                keypadWrapperRef.current.contains(relatedTarget)) {
                return;
            }
            // If focus moved to the tool bottom panel, right side panel, sidebar,
            // or footer, keep selection so buttons can operate on the focused math content.
            if (relatedTarget instanceof HTMLElement) {
                if (relatedTarget.closest('.toolBottomPanelContainer') ||
                    relatedTarget.closest('#rightSideDrawerContainer') ||
                    relatedTarget.closest('.sidebar') ||
                    relatedTarget.closest('.footer') ||
                    relatedTarget.closest('.collapsibleRight') ||
                    relatedTarget.closest('.collapsibleOptionHolder')) {
                    return;
                }
            }
            // If switching focus to another mathfield, keep this one and don't remove it
            if ((relatedTarget === null || relatedTarget === void 0 ? void 0 : relatedTarget.nodeName) === 'MATH-FIELD' ||
                (!relatedTarget && ((_c = e.target) === null || _c === void 0 ? void 0 : _c.nodeName) === 'MATH-FIELD')) {
                return;
            }
            // Auto-remove mathfield only when it is truly empty (no value at all).
            // \placeholder{} counts as real content (a visible box) so we keep it.
            // Sticky consumers (JumpBar / CustomTick / fraction-tile labels) skip
            // this in-blur removal: a freshly-inserted empty mf would otherwise
            // be deleted between keypad taps (e.g. when relatedTarget is a
            // sibling that misses every early-return above), dropping
            // lastFocusedMathField and routing subsequent keypad inserts to the
            // editorDiv. The empty-mf cleanup for sticky runs at session end via
            // the !isFocused effect below.
            var raw = (mf.value || '').trim();
            if (raw === '' && mf.isConnected && !stickyKeypadAfterInsertOnTouch) {
                var next = mf.nextSibling;
                if (next &&
                    next.nodeType === Node.TEXT_NODE &&
                    (next.textContent === WHITESPACE_CHAR || next.textContent === '')) {
                    next.remove();
                }
                mf.remove();
                mathFieldInstancesRef.current.delete(mf);
                if (lastFocusedMathField.current === mf) {
                    lastFocusedMathField.current = null;
                }
                batchDisposeMathfields([mf]);
                // Hide keyboard if no math fields remain
                var remainingMf = editor === null || editor === void 0 ? void 0 : editor.querySelector('math-field');
                if (!remainingMf) {
                    setmathKeyboardVisible(false);
                    setActiveMathText && setActiveMathText(-1);
                }
            }
            // Sticky-keypad consumers own the close lifecycle externally; never
            // auto-deselect from inside the math-field blur. Without this, a blur
            // whose relatedTarget happens to be a wrapper carrying data-mfdeselect
            // (e.g. the JumpBar's outer div) tears down selection + keypad
            // mid-edit on iPad even though the user is still tapping the keypad.
            var deselect = !stickyKeypadAfterInsertOnTouch &&
                (relatedTarget.getAttribute('data-mfdeselect') === 'true' ||
                    relatedTarget.className.includes('customScrollbar'));
            if (deselect) {
                setSelectedTool([]);
                setmathKeyboardVisible(false);
                setActiveMathText && setActiveMathText(-1);
                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                }
                timeoutRef.current = setTimeout(function () {
                    timeoutRef.current = null;
                    getMathData();
                }, 100);
            }
        });
        return mf;
    };
    // Cleanup function to properly dispose of MathfieldElement instances
    // Now using optimized batch disposal from mathliveOptimizations
    var cleanupMathFields = function () {
        if (mathFieldInstancesRef.current.size === 0)
            return;
        // Use batch disposal for efficient cleanup
        batchDisposeMathfields(mathFieldInstancesRef.current);
        mathFieldInstancesRef.current.clear();
        // Schedule garbage collection hint after cleanup
        scheduleSuggestGarbageCollection(500);
    };
    // Cleanup on component unmount
    useEffect(function () {
        return function () {
            var _a;
            // Flush any pending data save before disposing math fields
            // (blur sets a 100ms timeout for getMathData; if unmount happens
            // before it fires, the entered text would be lost)
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
                (_a = getMathDataRef.current) === null || _a === void 0 ? void 0 : _a.call(getMathDataRef);
            }
            if (onChangeSaveTimerRef.current) {
                clearTimeout(onChangeSaveTimerRef.current);
                onChangeSaveTimerRef.current = null;
            }
            cleanupMathFields();
        };
    }, []);
    /**
     * Re-open the keypad on the expression the caret is already in, without
     * inserting a new one. `lastFocusedMathField` is nulled whenever the editorDiv
     * itself takes focus (caret in plain text), so it doubles as "the caret is
     * inside an expression". Returns false when it isn't — the caller then inserts.
     */
    var reopenKeypadAtCaret = function () {
        var target = lastFocusedMathField.current;
        var editor = editorRef.current;
        if (viewOnlyRef.current || !target || !target.isConnected)
            return false;
        if (!(editor === null || editor === void 0 ? void 0 : editor.contains(target)))
            return false;
        if (document.activeElement !== target)
            safeFocusMathField(target);
        mathKeyboardVisibleRef.current = true;
        setmathKeyboardVisible(true);
        setActiveMathText && typeof index == 'number' && setActiveMathText(index);
        return true;
    };
    var insertMath = function () {
        var _a, _b;
        if (viewOnlyRef.current)
            return;
        if (!mlReady)
            return alert('MathLive is still loading…');
        // Guard: prevent rapid double insertion (e.g., re-render triggers setMathData
        // which rebuilds the editor, then this runs and adds another math-field)
        var now = Date.now();
        if (now - insertMathGuardRef.current < 300)
            return;
        insertMathGuardRef.current = now;
        var editor = editorRef.current;
        var sel = window.getSelection();
        var mf = createMathField(editor, true);
        ensureTrailingTextNode(editor);
        var spacerAfter = document.createTextNode(WHITESPACE_CHAR);
        var spacerBefore = document.createTextNode(WHITESPACE_CHAR);
        if (viewOnlyRef.current) {
            mf.readonly = true;
        }
        var focusMf = function () {
            safeFocusMathField(mf);
            // Retry if focus didn't land inside the mf
            requestAnimationFrame(function () {
                if (document.activeElement !== mf && mf.isConnected) {
                    safeFocusMathField(mf);
                }
            });
        };
        if (!(sel === null || sel === void 0 ? void 0 : sel.rangeCount) || !editor.contains(sel.anchorNode)) {
            // If the editor's last text content ends with 'e' or 'a', insert a space
            // to prevent font-shaping distortion on those glyphs when adjacent to ZWS.
            var lastTextNode = (function () {
                var node = editor.lastChild;
                while (node && node.nodeType !== Node.TEXT_NODE)
                    node = node.previousSibling;
                return node;
            })();
            if (lastTextNode) {
                var t_1 = (_a = lastTextNode.textContent) !== null && _a !== void 0 ? _a : '';
                if (t_1.length > 0 && /[eaoEAO]/.test(t_1[t_1.length - 1])) {
                    editor.appendChild(document.createTextNode(' '));
                }
            }
            editor.appendChild(spacerBefore);
            editor.appendChild(mf);
            ensureAriaLiveText(mf);
            editor.appendChild(spacerAfter);
            // insertMath() mutates the DOM directly and doesn't dispatch a native
            // 'input' event, so the auto-resize effect (which listens for 'input')
            // never sees this growth — dispatch one so the text box can resize to fit.
            editor.dispatchEvent(new Event('input', { bubbles: true }));
            setTimeout(focusMf, 50);
            return;
        }
        var range = sel.getRangeAt(0);
        range.collapse(true);
        // If the character immediately before the cursor is 'e' or 'a', insert a
        // regular space to break the font-shaping context before the ZWS spacer.
        // Without it, the contextual glyph for those letters changes when the
        // next "character" is U+200B, making them appear in the wrong font style.
        if (range.startContainer.nodeType === Node.TEXT_NODE && range.startOffset > 0) {
            var textBefore = (_b = range.startContainer.textContent) !== null && _b !== void 0 ? _b : '';
            var ch = textBefore[range.startOffset - 1];
            if (/[eaoEAO]/.test(ch)) {
                var spaceNode = document.createTextNode(' ');
                range.insertNode(spaceNode);
                range.setStartAfter(spaceNode);
                range.collapse(true);
            }
        }
        // Insert in reverse DOM order: spacerAfter first, then mf before it, then spacerBefore before mf
        range.insertNode(spacerAfter);
        range.insertNode(mf);
        range.insertNode(spacerBefore);
        ensureAriaLiveText(mf);
        // See the no-selection branch above for why this dispatch is needed.
        editor.dispatchEvent(new Event('input', { bubbles: true }));
        setTimeout(focusMf, 20);
    };
    /**
     * `outerHTML` with the blank-line caret spacers taken back out.
     *
     * `ensureBlankLineCarets` puts a U+200B between adjacent `<br>`s purely so
     * the caret has a layout box there — it must never reach save state. This
     * editor's standing rule (see the WHITESPACE_CHAR note in `calcMathData`
     * below) is that ZWS lives in the DOM only, because a persisted one comes
     * back as a sibling of typed text and distorts the shaping of adjacent
     * letters. Element nodes are serialised verbatim as `outerHTML`, so the
     * spacers inside one would otherwise ride along; `setMathData` re-adds them
     * on load, which also keeps the content-identical bail-out comparing equal
     * instead of rebuilding (and dirtying) every blank-line box on sight.
     *
     * The strip is a bare U+200B removal, unlike the padded ` ?ZWS+ ?` used for
     * text runs: the caret sits after the spacer, so a space the user typed as
     * the first character of a blank line is real content, not a shaping guard.
     */
    var outerHtmlWithoutBlankLineCarets = function (el) {
        if (!el.querySelector('br'))
            return el.outerHTML;
        var clone = el.cloneNode(true);
        // Both sides of the `<br>`: the spacer for a blank line sits after its
        // opening `<br>`, but the one for a blank FIRST line sits *before* the
        // leading `<br>` (that line has no `<br>` of its own to follow).
        clone.querySelectorAll('br').forEach(function (br) {
            [br.nextSibling, br.previousSibling].forEach(function (sibling) {
                var _a, _b;
                if ((sibling === null || sibling === void 0 ? void 0 : sibling.nodeType) !== Node.TEXT_NODE)
                    return;
                var stripped = ((_a = sibling.textContent) !== null && _a !== void 0 ? _a : '').replace(new RegExp(WHITESPACE_CHAR, 'g'), '');
                if (stripped.length > 0)
                    sibling.textContent = stripped;
                else
                    (_b = sibling.parentNode) === null || _b === void 0 ? void 0 : _b.removeChild(sibling);
            });
        });
        return clone.outerHTML;
    };
    var calcMathData = function () {
        var _a;
        var editor = editorRef.current;
        var output = [];
        // Decompose the children of a color span that contains a math-field
        // (math-fields can't be saved as outerHTML — MathLive never serializes
        // mf.value into the DOM, so the latex would be silently lost). Recurses
        // into any child element that itself still contains a math-field (e.g. a
        // <strong>/<em>/<u>, a font-size span, a link) instead of falling back to
        // outerHTML for it, which would hit the same data-loss problem one level
        // deeper. `wrappers` accumulates every such ancestor element (tag name +
        // its attributes) on the way down; a decomposed text run is re-wrapped in
        // all of them so bold/italic/underline/links/font-size/color survive even
        // when they share a span with a math-field. `spanColor`/`spanFontSize` are
        // tracked separately because math items carry color/size via `col`/
        // `fontSize` fields, not HTML — without forwarding the ancestor font-size
        // onto the math-field too, a fraction that visually shrank alongside its
        // surrounding text (via inherited CSS) renders back at full size after a
        // save/reload, since `setMathData` builds it as a plain sibling with no
        // font-size span around it to inherit from.
        var decomposeMathSpan = function (childNodes, spanColor, spanFontSize, wrappers) {
            childNodes.forEach(function (child) {
                var _a, _b, _c;
                if (child.nodeType === Node.TEXT_NODE) {
                    var raw = (_a = child.textContent) !== null && _a !== void 0 ? _a : '';
                    // Strip the ZWS spacer marker itself, and — ONLY when it directly
                    // touches one — the single font-shaping guard space insertMath
                    // adds next to it (see its e/a/o adjacency comment). A generic
                    // `.trim()` here also ate any OTHER leading/trailing whitespace,
                    // which silently dropped real inter-word gaps whenever the browser
                    // merged a plain word with its surrounding spaces into one text
                    // node (e.g. `<span>Checking</span>" the "<strong>Fraction</strong>`
                    // → the spaces around "the" were trimmed away on both sides).
                    // A ZWS touching a <br> is a blank-line caret spacer, not a math
                    // spacer: the caret sits next to it, so a space there is the user's
                    // own first character and the padded pattern would eat it. Same
                    // carve-out as the top-level text branch in calcMathData below.
                    var touchesBr = ((_b = child.previousSibling) === null || _b === void 0 ? void 0 : _b.nodeName) === 'BR' || ((_c = child.nextSibling) === null || _c === void 0 ? void 0 : _c.nodeName) === 'BR';
                    var cleaned = touchesBr
                        ? raw.replace(new RegExp(WHITESPACE_CHAR, 'g'), '')
                        : raw.replace(new RegExp(" ?".concat(WHITESPACE_CHAR, "+ ?"), 'g'), '');
                    if (cleaned.length > 0) {
                        if (wrappers.length > 0) {
                            var wrapped = wrappers.reduceRight(function (html, w) { return "<".concat(w.tagName).concat(w.attrs, ">").concat(html, "</").concat(w.tagName, ">"); }, cleaned);
                            output.push({ type: 'html', value: wrapped });
                        }
                        else {
                            output.push({ type: 'text', value: cleaned });
                        }
                    }
                }
                else if (child.nodeName === 'MATH-FIELD') {
                    var mf = child;
                    if (mf.value !== '') {
                        var latex = mf.value;
                        var fontSize = spanFontSize || mf.style.fontSize || undefined;
                        if (isManipulative) {
                            var simpleLetter = /^[a-zA-Z0-9]$/.test(latex);
                            var textModeMatch = latex.match(/^\\text\{([a-zA-Z0-9])\}$/);
                            if (simpleLetter) {
                                output.push({ type: 'text', value: latex });
                            }
                            else if (textModeMatch) {
                                output.push({ type: 'text', value: textModeMatch[1] });
                            }
                            else {
                                output.push({ type: 'math', latex: latex, col: spanColor || mf.style.color, fontSize: fontSize });
                            }
                        }
                        else {
                            output.push({ type: 'math', latex: latex, col: spanColor || mf.style.color, fontSize: fontSize });
                        }
                    }
                }
                else if (child.nodeType === Node.ELEMENT_NODE) {
                    var childEl = child;
                    if (childEl.querySelector('math-field')) {
                        var attrs = Array.from(childEl.attributes)
                            .map(function (a) { return " ".concat(a.name, "=\"").concat(a.value, "\""); })
                            .join('');
                        decomposeMathSpan(childEl.childNodes, childEl.style.color || spanColor, childEl.style.fontSize || spanFontSize, __spreadArray(__spreadArray([], wrappers, true), [{ tagName: childEl.tagName.toLowerCase(), attrs: attrs }], false));
                    }
                    else {
                        // No math-field inside THIS child, so its own outerHTML round-trips
                        // safely (a <br>, a font-size span with only text, a plain nested
                        // span) — but it can still sit inside an ancestor wrapper that DID
                        // need decomposing (e.g. this is the inner color span, and an outer
                        // font-size span is why we're decomposing at all). Re-apply the
                        // accumulated wrappers so that ancestor formatting isn't dropped.
                        var wrapped = wrappers.reduceRight(function (html, w) { return "<".concat(w.tagName).concat(w.attrs, ">").concat(html, "</").concat(w.tagName, ">"); }, outerHtmlWithoutBlankLineCarets(childEl));
                        output.push({ type: 'html', value: wrapped });
                    }
                }
            });
        };
        // Don't save WHITESPACE_CHAR in data - it should only exist in DOM as spacers
        (_a = editor === null || editor === void 0 ? void 0 : editor.childNodes) === null || _a === void 0 ? void 0 : _a.forEach(function (node) {
            var _a, _b, _c, _d;
            // Skip transient spans whose only content is U+200B — mf spacer
            // wrappers (data-mf-spacer) and any legacy color-anchor spans from
            // older builds. Persisting them re-introduces a ZWS sibling next to
            // typed text which causes font-fallback distortion on adjacent letters
            // (most visibly "a" / "e"). Strip only the ZWS marker, not `\s`
            // generically — a span whose only content is a genuine space (a real
            // inter-word gap) must survive, not be treated as transient.
            if (node.nodeType === Node.ELEMENT_NODE &&
                node.tagName === 'SPAN' &&
                !node.querySelector('math-field') &&
                ((_a = node.textContent) !== null && _a !== void 0 ? _a : '').replace(new RegExp(WHITESPACE_CHAR, 'g'), '').length === 0) {
                return;
            }
            if (node.nodeType === Node.TEXT_NODE) {
                var rawText = (_b = node.textContent) !== null && _b !== void 0 ? _b : '';
                // Same fix as decomposeMathSpan above: only strip the ZWS marker and
                // the single font-shaping guard space directly touching it — not any
                // other whitespace, which would drop real inter-word gaps whenever
                // the browser merges a plain word with its surrounding spaces into
                // one text node.
                //
                // Touching a <br> on either side, the ZWS is a blank-line caret
                // spacer, not a math spacer, and the caret sits next to it — so a
                // space there is the user's own first character. Strip the marker
                // alone. Both sides matter: the spacer for a blank *first* line sits
                // before the leading <br> (that line has no <br> of its own to
                // follow), and the padded pattern would eat the space with it.
                var touchesBr = ((_c = node.previousSibling) === null || _c === void 0 ? void 0 : _c.nodeName) === 'BR' || ((_d = node.nextSibling) === null || _d === void 0 ? void 0 : _d.nodeName) === 'BR';
                var cleanedText = touchesBr
                    ? rawText.replace(new RegExp(WHITESPACE_CHAR, 'g'), '')
                    : rawText.replace(new RegExp(" ?".concat(WHITESPACE_CHAR, "+ ?"), 'g'), '');
                if (cleanedText.length > 0) {
                    output.push({ type: 'text', value: cleanedText });
                }
            }
            else if (node.nodeName === 'MATH-FIELD') {
                var mf = node;
                if (mf.value !== '') {
                    var latex = mf.value;
                    // A top-level math-field can carry its own font-size directly
                    // (e.g. restored by setMathData from a saved `fontSize` field, or
                    // resized in-place) even with no ancestor font-size span — capture
                    // it here too so a re-save doesn't drop it the next time round.
                    var fontSize = mf.style.fontSize || undefined;
                    // Normalise single-letter/digit math to plain text in manipulative
                    // label editors so the saved data never re-introduces math-italic
                    // rendering for characters that should match surrounding text.
                    if (isManipulative) {
                        var simpleLetter = /^[a-zA-Z0-9]$/.test(latex);
                        var textModeMatch = latex.match(/^\\text\{([a-zA-Z0-9])\}$/);
                        if (simpleLetter) {
                            output.push({ type: 'text', value: latex });
                        }
                        else if (textModeMatch) {
                            output.push({ type: 'text', value: textModeMatch[1] });
                        }
                        else {
                            output.push({ type: 'math', latex: latex, col: mf.style.color, fontSize: fontSize });
                        }
                    }
                    else {
                        output.push({ type: 'math', latex: latex, col: mf.style.color, fontSize: fontSize });
                    }
                }
            }
            else {
                var el = node;
                // ANY element that contains a math-field has to be decomposed, never
                // serialised as outerHTML: MathLive never writes `mf.value` into the
                // DOM, so the latex would be silently lost.
                //
                // This gate used to read `el.tagName === 'SPAN' && …`, which was a
                // latent data-loss bug — the *recursive* branch inside
                // `decomposeMathSpan` has always been tag-agnostic (it accumulates
                // whatever `childEl.tagName` it finds), so only the TOP-LEVEL entry was
                // restricted, and a `<strong>`/`<em>`/`<u>` sitting directly under the
                // editor with a math-field inside it fell straight through to
                // `outerHtmlWithoutBlankLineCarets` and lost the expression on reload.
                //
                // Reachable before only by manually selecting across text and a
                // math-field and pressing Bold; box-level formatting makes it a
                // ONE-CLICK operation on any box holding math, so the asymmetry had to
                // go. Same failure family as #333 / #354: content that renders is
                // judged plain HTML and thrown away.
                if (el.querySelector('math-field')) {
                    var attrs = Array.from(el.attributes)
                        .map(function (a) { return " ".concat(a.name, "=\"").concat(a.value, "\""); })
                        .join('');
                    decomposeMathSpan(el.childNodes, el.style.color || '', el.style.fontSize || '', [
                        { tagName: el.tagName.toLowerCase(), attrs: attrs },
                    ]);
                }
                else {
                    output.push({ type: 'html', value: outerHtmlWithoutBlankLineCarets(el) });
                }
            }
        });
        return output;
    };
    // State saving on focus-out is handled in the first useEffect([isFocused]) above.
    var getMathData = function (skipUndo, force) {
        if (skipUndo === void 0) { skipUndo = false; }
        if (force === void 0) { force = false; }
        var editor = editorRef.current;
        // Use the REF (not the state variable) to avoid the stale-closure bug.
        // When the user clicks away from a MathEditor whose keypad is open,
        // setmathKeyboardVisible(false) is called but the state update is batched.
        // The getMathData closures captured by setTimeout / useEffect still hold
        // the OLD mathKeyboardVisible=true, causing every save to be skipped and
        // the data to remain empty.  The WhiteBoard empty-text cleanup then
        // removes the textbox.  Reading the ref at call-time picks up the latest
        // value regardless of which render created the closure.
        // DO NOT REMOVE — see docs/components/MathEditor.md
        if (!force && (mathKeyboardVisibleRef.current || viewOnlyRef.current)) {
            return;
        }
        var output = calcMathData();
        if (isTextUpdate && typeof index == 'number' && !isManipulative) {
            // if (!skipUndo && textEditHistoryIndexRef.current >= 0) {
            //   // const newSerialized = JSON.stringify(output);
            //   // const oldForUndo = textBeforeEditRef.current;
            //   // if (oldForUndo !== null && oldForUndo !== newSerialized) {
            //   //   updateStateEntryNew(textEditHistoryIndexRef.current, {
            //   //     type: 'text',
            //   //     id: index,
            //   //     propertyName: 'text',
            //   //     new: newSerialized,
            //   //   });
            //   // }
            //   // textBeforeEditRef.current = null;
            //   // textEditHistoryIndexRef.current = -1;
            // }
            setText(function (prev) {
                var newText = __spreadArray([], prev, true);
                if (!newText[index] || !newText[index].data)
                    return prev;
                newText[index].data.text = output;
                return newText;
            });
        }
        else {
            cb === null || cb === void 0 ? void 0 : cb(output, 'text', index, {
                height: editor === null || editor === void 0 ? void 0 : editor.clientHeight,
                width: editor === null || editor === void 0 ? void 0 : editor.clientWidth,
            });
        }
    };
    // Keep ref in sync so unmount cleanup always calls the latest version
    getMathDataRef.current = getMathData;
    useEffect(function () {
        var _a, _b, _c, _d, _e, _f, _g;
        // Skip if insertMath() was called recently — the editor is being mutated
        // directly and a re-render with stale/empty data would destroy the new field.
        var insertGuardAge = Date.now() - insertMathGuardRef.current;
        var editorIsEmpty = !editorRef.current || editorRef.current.childNodes.length === 0;
        if (insertGuardAge < 500) {
            return;
        }
        // If the editor DOM is empty (first mount, React Strict Mode remount, or
        // after a full reset), always inject data regardless of focus state.
        // A duplicated text item is immediately selected (isFocused=true) on mount,
        // so without this bypass the focus guard below would block the initial
        // setMathData call and the duplicated editor would render empty.
        // Using the live DOM state (childNodes.length) rather than a mount flag
        // also handles React Strict Mode's double-invocation: on the second run
        // the component DOM is fresh/empty even though refs still have old values.
        if (!editorIsEmpty) {
            if (!viewOnlyRef.current && (mathKeyboardVisibleRef.current || isFocusedRef.current)) {
                return;
            }
            var editor = editorRef.current;
            if (editor) {
                var activeEl = document.activeElement;
                if (activeEl === editor || editor.contains(activeEl)) {
                    return;
                }
            }
        }
        // Early bail-out: compare incoming data with current editor content to avoid
        // unnecessary rebuilds caused by new object references with identical content.
        // This prevents the cascade: tapping one editor → parent re-render → other
        // editors get new data refs → rebuild → destroys cursor in the focused editor.
        if (!editorIsEmpty && editorRef.current) {
            var incoming = typeof ((_a = data === null || data === void 0 ? void 0 : data.data) === null || _a === void 0 ? void 0 : _a.text) !== 'string'
                ? (_b = data === null || data === void 0 ? void 0 : data.data) === null || _b === void 0 ? void 0 : _b.text
                : (data === null || data === void 0 ? void 0 : data.type) === 'equation'
                    ? [
                        {
                            type: 'math',
                            value: '',
                            latex: equationStringToLatex(data.data.text.trim()),
                            col: (_c = data === null || data === void 0 ? void 0 : data.data) === null || _c === void 0 ? void 0 : _c.color,
                        },
                    ]
                    : [{ value: (_d = data === null || data === void 0 ? void 0 : data.data) === null || _d === void 0 ? void 0 : _d.text, type: 'html' }];
            var current = calcMathData();
            // Quick length check before expensive stringify comparison
            if ((current === null || current === void 0 ? void 0 : current.length) === (incoming === null || incoming === void 0 ? void 0 : incoming.length) &&
                JSON.stringify(current) === JSON.stringify(incoming)) {
                return;
            }
        }
        // Clear any pending blur timeout so it doesn't block reloading text
        // (e.g., when user clicks Done then Edit quickly)
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
        if (typeof ((_e = data === null || data === void 0 ? void 0 : data.data) === null || _e === void 0 ? void 0 : _e.text) !== 'string') {
            setMathData((_f = data === null || data === void 0 ? void 0 : data.data) === null || _f === void 0 ? void 0 : _f.text);
        }
        else if ((data === null || data === void 0 ? void 0 : data.type) === 'equation') {
            // Legacy "equation" items store the formula as a plain string (e.g. "30/60 + 15/60 =").
            // Convert a/b notation to \frac{a}{b} and render as a single math expression.
            var latex = equationStringToLatex(data.data.text.trim());
            setMathData([{ type: 'math', value: '', latex: latex, col: (_g = data === null || data === void 0 ? void 0 : data.data) === null || _g === void 0 ? void 0 : _g.color }]);
        }
        else {
            // Plain-string text (e.g. imported from production JSON) carries line
            // breaks as raw "\n". Those collapse to a single space once dropped into
            // innerHTML, so convert them to <br> to preserve the authored line breaks.
            setMathData([{ value: data.data.text.replace(/\r\n|\r|\n/g, '<br>'), type: 'html' }]);
        }
    }, [(_d = data === null || data === void 0 ? void 0 : data.data) === null || _d === void 0 ? void 0 : _d.text]);
    var setMathData = function (data) {
        var editor = editorRef.current;
        // Clean up existing MathfieldElement instances before clearing editor
        var existingMathFields = editor.querySelectorAll('math-field');
        existingMathFields.forEach(function (mf) {
            var mathFieldElement = mf;
            mathFieldInstancesRef.current.delete(mathFieldElement);
        });
        editor.innerHTML = '';
        // Use DocumentFragment to batch all DOM appends (single reflow)
        var fragment = document.createDocumentFragment();
        var deferredMathFields = [];
        data === null || data === void 0 ? void 0 : data.forEach(function (item) {
            var _a, _b;
            if (item.type === 'text') {
                // Skip WHITESPACE_CHAR items (shouldn't be in data, but handle legacy data)
                if (item.value === WHITESPACE_CHAR)
                    return;
                fragment.appendChild(document.createTextNode(normalizePlaceholderText(item.value)));
            }
            else if (item.type === 'math') {
                // In manipulative label editors, single letters/digits stored as math
                // (e.g. via the math keypad) must render identically to surrounding
                // plain text. Render them as text nodes instead of math-fields to
                // avoid the math-italic / different x-height appearance.
                if (isManipulative) {
                    var latex = item.latex || '';
                    var simpleLetter = /^[a-zA-Z0-9]$/.test(latex);
                    var textModeMatch = latex.match(/^\\text\{([a-zA-Z0-9])\}$/);
                    if (simpleLetter) {
                        fragment.appendChild(document.createTextNode(latex));
                        return;
                    }
                    else if (textModeMatch) {
                        fragment.appendChild(document.createTextNode(textModeMatch[1]));
                        return;
                    }
                }
                var mf = createMathField(editor);
                var col = isManipulative ? ((_b = (_a = props.data) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.color) || item.col : item.col;
                if (col) {
                    mf.style.color = col;
                }
                // Restore the font-size the fraction had while it shared a span with
                // resized surrounding text (see calcMathData's decomposeMathSpan) —
                // without this it renders back at default size after reload even
                // though it visually shrank alongside the text before saving.
                if (item.fontSize) {
                    mf.style.fontSize = item.fontSize;
                }
                // WHITESPACE_CHAR spacer before mathfield for cursor positioning
                fragment.appendChild(document.createTextNode(WHITESPACE_CHAR));
                fragment.appendChild(mf);
                // Defer value assignment until after fragment is attached to DOM
                deferredMathFields.push({ mf: mf, latex: item.latex || '' });
                // WHITESPACE_CHAR spacer after mathfield for cursor positioning
                fragment.appendChild(document.createTextNode(WHITESPACE_CHAR));
            }
            else if (item.type === 'html') {
                var wrapper = document.createElement('div');
                wrapper.innerHTML = normalizePlaceholderText(item.value);
                // Append every top-level node, not just the first. Plain-string text
                // (where "\n" was converted to <br> above) parses into multiple
                // siblings — text, <br>, text — and taking only firstChild dropped
                // everything after the first line break. Snapshot childNodes first
                // since appendChild mutates the live list.
                Array.from(wrapper.childNodes).forEach(function (node) {
                    var _a;
                    // Drop empty / ZWS-only spans from legacy data (mf spacer wrappers
                    // and old color-anchor spans). Re-injecting them places a ZWS
                    // sibling next to typed text and triggers font-fallback distortion
                    // on adjacent letters (a / e). Spacers around math-fields are
                    // recreated by the math branch above when needed.
                    // IMPORTANT: strip only the ZWS marker here, not `\s` generically —
                    // a span whose entire content is a genuine space (a real inter-word
                    // gap calcMathData now preserves as its own text/html item) must
                    // NOT be treated as "empty" and dropped.
                    var isEmptySpan = node.nodeType === Node.ELEMENT_NODE &&
                        node.tagName === 'SPAN' &&
                        !node.querySelector('math-field') &&
                        !node.querySelector('br') &&
                        ((_a = node.textContent) !== null && _a !== void 0 ? _a : '').replace(new RegExp(WHITESPACE_CHAR, 'g'), '').length === 0;
                    if (isEmptySpan)
                        return;
                    fragment.appendChild(node);
                });
            }
        });
        // Single DOM attachment — triggers one reflow
        editor.appendChild(fragment);
        // Blank lines restored from save state arrive as bare <br><br> pairs, the
        // same caret-less shape insertLineBreak used to leave behind.
        ensureBlankLineCarets(editor);
        justHydratedRef.current = Date.now();
        // Set values after DOM insertion so connectedCallback has fired
        deferredMathFields.forEach(function (_a) {
            var mf = _a.mf, latex = _a.latex;
            ensureAriaLiveText(mf);
            mf.value = latex;
        });
        // Merge adjacent text nodes into one.  This matters in manipulative label
        // editors where a math item converted to a text node (e.g. 'a') would
        // otherwise sit next to the preceding text node ('pa') as two separate
        // anonymous flex items in Safari — causing the second character to render
        // at a subtly different size.  normalize() coalesces them into 'paa',
        // exactly what happens after blur→save→rebuild, which is why pressing
        // space "fixed" the size before this call was added.
        editor.normalize();
        // Verify all math-field values stuck after MathLive initialization completes.
        // When duplicating a MathEditor, connectedCallback may be deferred, causing
        // the synchronous mf.value = latex above to silently fail. This post-loop
        // check runs after the microtask queue drains (setTimeout(0)), by which time
        // connectedCallback has fired and _mathfield is ready.
        setTimeout(function () {
            var mathIdx = 0;
            var mfs = editor.querySelectorAll('math-field');
            data === null || data === void 0 ? void 0 : data.forEach(function (item) {
                if (item.type === 'math') {
                    var mf = mfs[mathIdx];
                    if (mf && mf.isConnected) {
                        var expected = item.latex || '';
                        if (mf.value !== expected) {
                            mf.value = expected;
                        }
                    }
                    mathIdx++;
                }
            });
        }, 0);
    };
    var findColorSpan = function (node) {
        while (node && node !== document.body) {
            if (node instanceof HTMLSpanElement && node.style.color && node.style.color !== '') {
                return node;
            }
            node = node.parentNode;
        }
        return null;
    };
    var unwrapElement = function (el) {
        var parent = el.parentNode;
        if (!parent)
            return;
        while (el.firstChild)
            parent.insertBefore(el.firstChild, el);
        parent.removeChild(el);
    };
    var unwrapNestedSpans = function (fragment) {
        var spans = fragment.querySelectorAll('span[style]');
        spans.forEach(function (span) { return unwrapElement(span); });
    };
    // After inserting a new color span, if it landed inside an ancestor color span
    // (because the selection was partial within that span), lift it out so siblings
    // are flat rather than nested: <red>before</red><new>sel</new><red>after</red>
    var liftSpanOutOfParent = function (span, parentSpan) {
        var grandParent = parentSpan.parentNode;
        if (!grandParent)
            return;
        var siblings = Array.from(parentSpan.childNodes);
        var spanIdx = siblings.indexOf(span);
        if (spanIdx === -1)
            return; // not a direct child — skip complex nesting
        var before = siblings.slice(0, spanIdx);
        var after = siblings.slice(spanIdx + 1);
        // Move the lifted span to grandParent, right after parentSpan
        grandParent.insertBefore(span, parentSpan.nextSibling);
        // Remove after-nodes from parentSpan
        after.forEach(function (n) { return parentSpan.removeChild(n); });
        // Wrap after-nodes in a new span with parentSpan's color
        if (after.length > 0) {
            var afterSpan_1 = document.createElement('span');
            afterSpan_1.style.color = parentSpan.style.color;
            after.forEach(function (n) { return afterSpan_1.appendChild(n); });
            grandParent.insertBefore(afterSpan_1, span.nextSibling);
        }
        // If nothing remains before the lifted span, remove the now-empty parentSpan
        if (before.length === 0) {
            grandParent.removeChild(parentSpan);
        }
    };
    var formatLink = function (url) {
        if (viewOnlyRef.current)
            return;
        var editor = editorRef.current;
        var sel = window.getSelection();
        if (!editor || !sel)
            return;
        // REMOVE LINK — find all anchors in the editor and unwrap them
        if (url === '') {
            var anchors = editor.querySelectorAll('a');
            anchors.forEach(function (anchor) {
                if (anchor.parentNode) {
                    var parent_2 = anchor.parentNode;
                    var firstChild = anchor.firstChild;
                    var lastChild = anchor.lastChild;
                    while (anchor.firstChild) {
                        parent_2.insertBefore(anchor.firstChild, anchor);
                    }
                    parent_2.removeChild(anchor);
                    // Select the unwrapped content
                    if (firstChild && lastChild && sel) {
                        var range_1 = document.createRange();
                        range_1.setStartBefore(firstChild);
                        range_1.setEndAfter(lastChild);
                        sel.removeAllRanges();
                        sel.addRange(range_1);
                        lastSelection.current = range_1.cloneRange();
                    }
                }
            });
            clickedAnchorRef.current = null;
            setActiveLink(null);
            getMathData(false, true);
            return;
        }
        var range = null;
        // Try to get current selection
        if (sel.rangeCount > 0) {
            range = sel.getRangeAt(0);
        }
        // If no current selection or selection is collapsed, try to use saved selection (for iPad)
        if (!range || range.collapsed) {
            if (lastSelection.current && !lastSelection.current.collapsed) {
                range = lastSelection.current.cloneRange();
                // Restore the selection so the link can be applied
                sel.removeAllRanges();
                sel.addRange(range);
            }
            else {
                return;
            }
        }
        // Do not touch MathLive content — linking math-fields is not supported
        if (range.commonAncestorContainer.nodeName === 'MATH-FIELD')
            return;
        // Find parent <a> if selection is already inside a link
        var findAnchor = function (node) {
            while (node && node !== editor) {
                if (node instanceof HTMLAnchorElement)
                    return node;
                node = node.parentNode;
            }
            return null;
        };
        var anchor = findAnchor(range.commonAncestorContainer);
        // UPDATE EXISTING LINK
        if (anchor) {
            anchor.setAttribute('href', url);
            anchor.setAttribute('target', '_blank');
            setActiveLink(url);
            return;
        }
        // Do not touch MathLive content — bail if the selection about to be
        // wrapped in a new link overlaps any math-field, not just when the
        // selection is confined entirely to one (math-fields are DOM siblings
        // of text/spans, so a drag can span across a math-field's boundary).
        var overlapsMathField = Array.from(editor.querySelectorAll('math-field')).some(function (mf) {
            return range.intersectsNode(mf);
        });
        if (overlapsMathField)
            return;
        // CREATE NEW LINK
        var link = document.createElement('a');
        link.href = url;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        var contents = range.extractContents();
        // Strip any inline-colored spans the selection may have pulled in — if one
        // ends up nested inside the <a>, its own explicit color wins over the
        // anchor's, hiding the link color for that text (see docs/components/MathEditor.md).
        unwrapNestedSpans(contents);
        link.appendChild(contents);
        range.insertNode(link);
        // Move caret after link
        range.setStartAfter(link);
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);
        setActiveLink(url);
        getMathData(false, true);
    };
    // Decide which side to place the keyboard (once when it becomes visible).
    // Order is right → left → below, then a flip to above if a below placement
    // would have to be clamped back up onto the content. Text editors
    // (idPreffix 'text' / 'tableText') skip the right branch, which would
    // collide with their formatting toolbar.
    // For whiteboard editors (isTextUpdate), position is relative to the canvas
    // scroll container so the keyboard scrolls with the text box.
    // For other editors, position is in document space (portaled to body).
    var KEYPAD_WIDTH = 520; // 503px box-sizing:border-box + margin buffer
    var recalculateKeypadPosition = function () {
        var _a, _b, _c, _d, _e;
        if (!editorRef.current)
            return;
        var editor = editorRef.current;
        var editorRect = editor.getBoundingClientRect();
        // Everything below is measured against the ANCHOR, not the editorDiv.
        // They are the same box unless the consumer passed keypadAnchorRef — a
        // `width: fit-content` editorDiv centred inside a bigger widget grows
        // outward from its centre as the user types, so a keypad placed a gap
        // away from *it* ends up on top of the text (see keypadAnchorRef's doc
        // comment in src/interface/mathEditor.ts).
        var anchorRect = (_b = (_a = keypadAnchorRef === null || keypadAnchorRef === void 0 ? void 0 : keypadAnchorRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect()) !== null && _b !== void 0 ? _b : editorRect;
        var gap = 10;
        var sidebarEl = document.querySelector('.sidebar');
        var bottomToolbarEl = document.querySelector('.toolBottomPanelContainer');
        var sidebarRight = sidebarEl ? sidebarEl.getBoundingClientRect().right : 0;
        var bottomToolbarTop = bottomToolbarEl
            ? bottomToolbarEl.getBoundingClientRect().top
            : window.innerHeight;
        var keypadHeight = ((_c = keypadWrapperRef.current) === null || _c === void 0 ? void 0 : _c.offsetHeight) || 250;
        var headerEl = document.querySelector('.appHeader');
        var headerBottom = headerEl ? headerEl.getBoundingClientRect().bottom : 0;
        var scrollX = window.scrollX || 0;
        var scrollY = window.scrollY || 0;
        // For canvas editors (isTextUpdate), the keyboard lives inside the CSS
        // transform: scale(zoomLevel) container. All viewport measurements derived
        // from getBoundingClientRect() must be compared against CSS dimensions
        // (keypadHeight, KEYPAD_WIDTH) after converting those to viewport units by
        // multiplying by scale.
        var scale = isTextUpdate ? zoomLevel || 1 : 1;
        // Viewport pixel equivalents of fixed CSS dimensions:
        var effectiveKeypadWidth = KEYPAD_WIDTH * scale;
        var effectiveKeypadHeight = keypadHeight * scale;
        // The text-editor formatting toolbar sits below the editorDiv inside the
        // canvas; its CSS height is ~80px, so its viewport height scales with zoom.
        var toolbarOffset = 80 * scale;
        // For canvas editors, get mainHolder bounds to prevent the keyboard from
        // escaping the canvas. mathEditorParent has transform:translate(-50%,-50%)
        // which creates a new CSS containing block, so mainHolder's overflow:hidden
        // won't clip keyboard children that extend beyond its visual boundary.
        var mainEl = isTextUpdate ? document.querySelector('.mainHolder') : null;
        var mainRect = mainEl ? mainEl.getBoundingClientRect() : null;
        var rightSpace = window.innerWidth - anchorRect.right;
        var leftSpace = anchorRect.left - sidebarRight;
        // Gate left placement on keyboard fitting within the canvas left edge.
        var wouldFitInCanvasLeft = mainRect
            ? anchorRect.left - effectiveKeypadWidth - gap >= mainRect.left
            : true;
        var top;
        var left;
        var isBelow = false;
        // Text editors skip the right-side branch — a right-anchored keypad ends
        // up overlapping the formatting toolbar (B / I / U / align / link) which
        // sits directly below the text box. Left → below → above order keeps the
        // toolbar fully visible. Other manipulatives keep the original priority.
        var isTextEditor = idPreffix === 'text' || idPreffix === 'tableText';
        // The editorFocusOptions accessibility panel (pencil/kebab) also renders
        // to the left of the box, top-aligned — when the keypad is placed there
        // too it would sit right on top of the panel. Push the keypad down below
        // the panel instead of moving the panel itself.
        var focusPanelHeight = enableFocusPanel && !focusPanelRight
            ? ((_d = focusOptionsRef.current) === null || _d === void 0 ? void 0 : _d.getBoundingClientRect().height) || 0
            : 0;
        if (!isTextEditor && rightSpace >= effectiveKeypadWidth + gap) {
            top = anchorRect.top + scrollY;
            left = anchorRect.right + gap + scrollX;
        }
        else if (leftSpace >= effectiveKeypadWidth + gap && wouldFitInCanvasLeft) {
            top = anchorRect.top + scrollY + (focusPanelHeight ? focusPanelHeight + gap : 0);
            left = anchorRect.left - effectiveKeypadWidth - gap + scrollX;
        }
        else {
            // below (fallback when not enough room on the left or keyboard would clip canvas edge)
            top = anchorRect.bottom + toolbarOffset + gap + scrollY;
            left = anchorRect.left + scrollX;
            isBelow = true;
        }
        // Clamp: stay below header, above bottom toolbar, within horizontal bounds.
        // For below placement the bottom-toolbar clamp is skipped: at high zoom levels
        // effectiveKeypadHeight can be larger than the remaining viewport space, which
        // would pull the keyboard up and overlap the editor. It is better to let the
        // keyboard extend below the toolbar than to cover the text being edited.
        var belowBaseline = anchorRect.bottom + toolbarOffset + gap + scrollY;
        top = Math.max(headerBottom + gap + scrollY, top);
        if (!isBelow) {
            top = Math.min(bottomToolbarTop - effectiveKeypadHeight - gap + scrollY, top);
        }
        // For below placement, guarantee top never rises above the editor baseline
        // (clamping could push it up into the editor at high zoom).
        if (isBelow) {
            top = Math.max(belowBaseline, top);
        }
        left = Math.max(sidebarRight + gap + scrollX, left);
        left = Math.min(window.innerWidth - effectiveKeypadWidth - gap + scrollX, left);
        // For whiteboard editors, convert to coordinates relative to the
        // mathEditorParent so the keyboard is a direct child (no portal) and
        // scrolls with the text box automatically.
        if (isTextUpdate) {
            var parentEl = (_e = editorRef.current) === null || _e === void 0 ? void 0 : _e.closest('.mathEditorParent');
            if (parentEl) {
                var parentRect = parentEl.getBoundingClientRect();
                // Divide by zoomLevel because the parent lives inside the scaled canvas
                // (transform: scale(zoomLevel) on mainHolder). CSS top/left are in the
                // pre-transform coordinate system, so viewport pixels must be converted
                // to element pixels by dividing by the scale factor.
                top = (top - scrollY - parentRect.top) / scale;
                left = (left - scrollX - parentRect.left) / scale;
                // Hard-clamp to canvas bounds in element space. The pre-check above
                // catches most cases, but this safety net handles edge cases (e.g. the
                // editor itself is very close to the canvas edge).
                if (mainRect) {
                    var minLeftCss = (mainRect.left - parentRect.left) / scale;
                    var maxLeftCss = (mainRect.right - parentRect.left) / scale - KEYPAD_WIDTH;
                    left = Math.max(minLeftCss, left);
                    left = Math.min(maxLeftCss, left);
                }
            }
        }
        // editorRect.height is in viewport px; after the isTextUpdate conversion
        // top is in element-space px, so divide by scale to match coordinate spaces.
        var finalTop = top + (offsetKeypadByEditorHeight ? editorRect.height / scale : 0);
        var finalLeft = left;
        // Final viewport bound for portaled editors (tick / jump-bar /
        // fraction-tile labels — everything except the canvas text editor, which
        // uses the isTextUpdate canvas-space clamps above). On small devices like
        // iPad a below-placed keypad can run off the bottom of the screen; this
        // guarantees the whole keypad stays visible. Applied to finalTop so the
        // offsetKeypadByEditorHeight shift is included. Skipped when the keypad is
        // taller/wider than the available band so we never force it up over the
        // editor (extreme cases fall back to the prior best-effort position).
        if (!isTextUpdate) {
            var viewportBottom = Math.min(bottomToolbarTop, window.innerHeight);
            var minTop = headerBottom + gap + scrollY;
            var maxTop = viewportBottom - effectiveKeypadHeight - gap + scrollY;
            // A below placement that doesn't fit gets dragged back UP by the clamp
            // below — straight over the box it is supposed to sit under. Flip it
            // above the anchor instead, which is the whole point of the below
            // placement (stay clear of the content) achieved from the other side.
            // Only when there is genuinely room up there; otherwise fall through to
            // the clamp's best effort. The editor-height offset is deliberately
            // dropped here — it exists to push the keypad past a box it sits below,
            // and means nothing above it.
            if (isBelow && maxTop >= minTop && finalTop > maxTop) {
                var aboveTop = anchorRect.top + scrollY - gap - effectiveKeypadHeight;
                if (aboveTop >= minTop)
                    finalTop = aboveTop;
            }
            if (maxTop >= minTop) {
                finalTop = Math.min(Math.max(finalTop, minTop), maxTop);
            }
            var minLeft = Math.max(sidebarRight, 0) + gap + scrollX;
            var maxLeft = window.innerWidth - effectiveKeypadWidth - gap + scrollX;
            // Keep the left edge on-screen even if the keypad is wider than the
            // available horizontal band (buttons read left-to-right).
            finalLeft = maxLeft >= minLeft ? Math.min(Math.max(finalLeft, minLeft), maxLeft) : minLeft;
        }
        setKeypadPos({
            top: finalTop,
            left: finalLeft,
        });
    };
    // useLayoutEffect, not useEffect: `keypadPos` starts at (0, 0) and keeps the
    // previous open's value afterwards, so measuring in a requestAnimationFrame
    // (which runs *after* the browser has painted) let the keypad paint one frame
    // at the stale position first — for the portaled consumers (everything except
    // isTextUpdate) that is `absolute` against <body>, i.e. a visible flash of the
    // whole keypad in the page's upper-left corner before it jumped to its anchor.
    // A layout effect runs after the DOM is committed but before paint, and every
    // ref it measures (keypadWrapperRef for offsetHeight, focusOptionsRef) is
    // already attached by then, so the first painted frame is the correct one.
    // The rAF pass is kept as a second correction for anything that only settles
    // after paint (fonts/zoom reflow changing the keypad's own height).
    useLayoutEffect(function () {
        if (!mathKeyboardVisible || !editorRef.current)
            return;
        recalculateKeypadPosition();
        var raf = requestAnimationFrame(recalculateKeypadPosition);
        return function () { return cancelAnimationFrame(raf); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mathKeyboardVisible]);
    // Keep the keypad anchored to its box while the keypad is open — a text
    // manipulative's editorDiv IS the box the user drags to resize (no
    // keypadAnchorRef wrapper to hide behind, unlike fraction-tile labels), so
    // the open-time-only effect above leaves the keypad stranded mid-resize.
    useEffect(function () {
        var _a;
        if (!mathKeyboardVisible)
            return;
        var target = (_a = keypadAnchorRef === null || keypadAnchorRef === void 0 ? void 0 : keypadAnchorRef.current) !== null && _a !== void 0 ? _a : editorRef.current;
        if (!target)
            return;
        var observer = new ResizeObserver(function () {
            recalculateKeypadPosition();
        });
        observer.observe(target);
        return function () { return observer.disconnect(); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mathKeyboardVisible]);
    // Scroll editor into view when native iPad keyboard appears
    useEffect(function () {
        var editor = editorRef.current;
        if (!editor)
            return;
        // Detect if this is a touch device (iPad/mobile)
        // Handler for when editor or math field gets focus (native keyboard appears)
        var handleFocusForKeyboard = function () {
            // Don't scroll if the custom keypad is visible - we're handling positioning ourselves
            if (mathKeyboardVisibleRef.current)
                return;
            // Don't scroll if focus was triggered by a color change, not a user tap
            if (isColorChangeFocusRef.current)
                return;
            // Delay to allow keyboard to appear and viewport to resize
            setTimeout(function () {
                var _a;
                var editorRect = editor.getBoundingClientRect();
                // Use visualViewport for accurate viewport height when keyboard is open
                var viewportHeight = ((_a = window.visualViewport) === null || _a === void 0 ? void 0 : _a.height) || window.innerHeight;
                // Calculate if editor is hidden or partially hidden by keyboard
                var editorBottom = editorRect.bottom;
                var editorTop = editorRect.top;
                // If editor would be covered by keyboard, manually scroll the
                // nearest scrollable parent instead of using scrollIntoView().
                // scrollIntoView miscalculates offsets when the parent uses
                // transform: scale(zoomLevel).
                if (editorBottom > viewportHeight || editorTop < 0) {
                    var scrollParent = editor.closest('[class*="scrollbar"], [class*="Scrollbar"]') ||
                        editor.closest('[style*="overflow"]');
                    if (scrollParent) {
                        var parentRect = scrollParent.getBoundingClientRect();
                        // editorRect/parentRect are visual (post-transform) viewport pixels, but
                        // scrollTop moves the scrollParent's content in pre-transform (unscaled)
                        // layout pixels since transform: scale(zoomLevel) on mainHolder doesn't
                        // affect layout size. Divide the visual delta by zoomLevel before adding
                        // it to scrollTop, or the offset is off by the zoom factor (see the same
                        // fix applied to the keypad positioning above).
                        var scale = zoomLevel || 1;
                        var targetY = scrollParent.scrollTop +
                            (editorRect.top - parentRect.top - parentRect.height / 2 + editorRect.height / 2) /
                                scale;
                        scrollParent.scrollTo({ top: targetY, behavior: 'smooth' });
                    }
                }
            }, 300); // Delay for keyboard animation
        };
        var mathFields;
        if (isTouchDevice) {
            // Listen to focus events on editor
            editor.addEventListener('focus', handleFocusForKeyboard, true);
            // Also listen to focus on any math-field elements
            mathFields = editor.querySelectorAll('math-field');
            mathFields === null || mathFields === void 0 ? void 0 : mathFields.forEach(function (mf) {
                mf.addEventListener('focus', handleFocusForKeyboard, true);
            });
        }
        return function () {
            editor.removeEventListener('focus', handleFocusForKeyboard, true);
            mathFields === null || mathFields === void 0 ? void 0 : mathFields.forEach(function (mf) {
                mf.removeEventListener('focus', handleFocusForKeyboard, true);
            });
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [zoomLevel]);
    useEffect(function () {
        var editor = editorRef.current;
        if (!editor)
            return;
        editor.childNodes.forEach(function (node) {
            var _a;
            if (node.nodeName === 'MATH-FIELD') {
                var mf = node;
                mf.style.color = ((_a = data === null || data === void 0 ? void 0 : data.data) === null || _a === void 0 ? void 0 : _a.color) || ColorEnum['BLACK'];
            }
        });
    }, [(_e = data === null || data === void 0 ? void 0 : data.data) === null || _e === void 0 ? void 0 : _e.color]);
    // Auto-focus when the editor is selected (newly created or clicked)
    useEffect(function () {
        if (!isFocused)
            return;
        var editor = editorRef.current;
        if (!editor)
            return;
        // Short delay to ensure the editor is fully rendered
        var timeoutId = setTimeout(function () {
            var _a;
            // Don't steal focus if it's already within the editor
            // (e.g., user clicked in text area before/after the mathfield)
            var activeEl = document.activeElement;
            if (activeEl === editor || editor.contains(activeEl)) {
                return;
            }
            // Don't steal focus if the caller has opted in and the toolbar already has focus
            if (suppressAutoFocusWhenToolbarActive && ((_a = activeEl === null || activeEl === void 0 ? void 0 : activeEl.closest) === null || _a === void 0 ? void 0 : _a.call(activeEl, '.bottomPanelTool'))) {
                return;
            }
            // First try to focus a math-field if one exists
            var mathField = editor.querySelector('math-field');
            if (mathField) {
                safeFocusMathField(mathField);
            }
            else {
                // Otherwise focus the editor itself
                editor.focus({ preventScroll: true });
            }
        }, 50);
        return function () { return clearTimeout(timeoutId); };
    }, [isFocused]);
    // Wait for MathLive to fully register
    useEffect(function () {
        var element = customElements.get('math-field');
        if (!element) {
            customElements.whenDefined('math-field').then(function () {
                setMlReady(true);
            });
            customElements.define('math-field', MathfieldElement);
        }
        else {
            setMlReady(true);
        }
    }, []);
    useEffect(function () {
        var changeColour = function (payload) {
            var _a, _b, _c, _d, _e;
            if (viewOnlyRef.current)
                return;
            var localColor = payload.color;
            var sel = window.getSelection();
            var editor = editorRef.current;
            if (!editor || !sel) {
                return;
            }
            var mf = lastFocusedMathField.current;
            // `lastFocusedMathField` is a *last*-focused ref (see mathSelectionIsEmpty's
            // doc comment) — it stays set after the user clicks elsewhere in the SAME
            // box, so without this emptiness check every subsequent colour change kept
            // routing here and calling `applyStyle` against a stale/collapsed selection
            // (a no-op), and `recolourWholeBox` below was never reached. Same gate
            // `exec` already uses for bold/italic/underline.
            if (mf && !mathSelectionIsEmpty(mf)) {
                // Save scroll position before focus — focus + applyStyle can trigger
                // browser auto-scroll that miscalculates offsets due to transform: scale().
                var scrollEl_3 = editor.closest('.customScrollbarContent');
                var savedScrollTop_3 = (_a = scrollEl_3 === null || scrollEl_3 === void 0 ? void 0 : scrollEl_3.scrollTop) !== null && _a !== void 0 ? _a : 0;
                var savedScrollLeft_3 = (_b = scrollEl_3 === null || scrollEl_3 === void 0 ? void 0 : scrollEl_3.scrollLeft) !== null && _b !== void 0 ? _b : 0;
                // Suppress the 300ms-delayed scroll-into-view handlers that fire on focus.
                // Focus here is programmatic (for MathLive selection), not user-initiated.
                isColorChangeFocusRef.current = true;
                // Re-focus so MathLive's selection is active after toolbar click
                safeFocusMathField(mf);
                mf.applyStyle({ color: localColor });
                if (scrollEl_3) {
                    requestAnimationFrame(function () {
                        scrollEl_3.scrollTop = savedScrollTop_3;
                        scrollEl_3.scrollLeft = savedScrollLeft_3;
                    });
                }
                // Clear after the 300ms delayed scroll handlers would have checked the flag
                setTimeout(function () {
                    isColorChangeFocusRef.current = false;
                }, 350);
                return;
            }
            if (sel.rangeCount === 0) {
                // No DOM selection means the user selected the BOX and wants all of its
                // text recoloured (e.g. the context menu, or the picker with nothing
                // selected). Now allowed in every editor rather than canvas text boxes
                // only, matching box-level bold/italic/underline/size.
                if (boxLevelStylingAllowed) {
                    recolourWholeBox(localColor);
                }
                else {
                }
                return;
            }
            var range = sel.getRangeAt(0);
            if (!editor.contains(range.commonAncestorContainer)) {
                return;
            }
            // Save scroll position — DOM manipulation (extractContents/insertNode)
            // can trigger browser auto-scroll due to transform: scale() offsets.
            var scrollEl = editor.closest('.customScrollbarContent');
            var savedScrollTop = (_c = scrollEl === null || scrollEl === void 0 ? void 0 : scrollEl.scrollTop) !== null && _c !== void 0 ? _c : 0;
            var savedScrollLeft = (_d = scrollEl === null || scrollEl === void 0 ? void 0 : scrollEl.scrollLeft) !== null && _d !== void 0 ? _d : 0;
            // Collapsed cursor (no text selected): no DOM mutation. The picker
            // color now flows through DataContext and gets enforced at type time
            // by onBeforeInput / insertTextIntoEditor / enforceCaretColor \u2014 all
            // three read the current color and wrap typed chars in a fresh span,
            // so a DOM "color anchor" is no longer required.
            //
            // Removing the anchor matters: the anchor used to be a span containing
            // a U+200B, with the caret placed inside it. Typing then put the new
            // chars in the same text node as the ZWS (`\u200Be`), which the
            // browser shapes as one run. `oso-sans` has no glyph for U+200B so the
            // run falls back to a different font \u2014 and adjacent letters (most
            // visibly `e` and `a`) end up rendered in the fallback font. By not
            // inserting the anchor at all, no ZWS ever sits next to typed text.
            if (range.collapsed) {
                // Collapsed caret and no selection → recolour the whole box, same rule
                // as the rangeCount === 0 branch above.
                if (boxLevelStylingAllowed) {
                    recolourWholeBox(localColor);
                }
                if (scrollEl) {
                    requestAnimationFrame(function () {
                        scrollEl.scrollTop = savedScrollTop;
                        scrollEl.scrollLeft = savedScrollLeft;
                    });
                }
                return;
            }
            // Text is selected: extract, unwrap any nested color spans, then rewrap
            // with the new color. Using extractContents removes from the live DOM and
            // returns the fragment in one step, so partial selections inside an existing
            // color span are handled cleanly.
            var fragment = range.extractContents();
            unwrapNestedSpans(fragment);
            var span = document.createElement('span');
            span.style.color = localColor;
            span.appendChild(fragment);
            range.insertNode(span);
            // If the new span landed inside an ancestor color span (partial selection
            // within an existing colored region), lift it out to produce flat siblings:
            // <orig-color>before</orig-color><new-color>sel</new-color><orig-color>after</orig-color>
            var parentColorSpan = findColorSpan(span.parentNode);
            if (parentColorSpan && parentColorSpan !== span) {
                liftSpanOutOfParent(span, parentColorSpan);
            }
            var newRange = document.createRange();
            newRange.selectNodeContents(span);
            sel.removeAllRanges();
            sel.addRange(newRange);
            lastSelection.current = newRange.cloneRange();
            // Restore scroll position after DOM manipulation
            if (scrollEl) {
                requestAnimationFrame(function () {
                    scrollEl.scrollTop = savedScrollTop;
                    scrollEl.scrollLeft = savedScrollLeft;
                });
            }
            (_e = getMathDataRef.current) === null || _e === void 0 ? void 0 : _e.call(getMathDataRef);
        };
        var onClick = function (data) {
            var _a, _b;
            if (viewOnlyRef.current)
                return;
            switch (data.id) {
                case 'bold':
                    if (idPreffix !== 'tableText')
                        exec('bold');
                    break;
                case 'italic':
                    if (idPreffix !== 'tableText')
                        exec('italic');
                    break;
                case 'underline':
                    if (idPreffix !== 'tableText')
                        exec('underline');
                    break;
                case 'mathtext':
                    if (mathKeyboardVisibleRef.current) {
                        // Keypad already open — exit the current math-field.
                        var mf = lastFocusedMathField.current;
                        if (mf && ((_a = editorRef.current) === null || _a === void 0 ? void 0 : _a.contains(mf))) {
                            if ((mf.value || '').trim() === '') {
                                // Empty math-field — remove it and place cursor where it was
                                var next = mf.nextSibling;
                                mathFieldInstancesRef.current.delete(mf);
                                lastFocusedMathField.current = null;
                                mf.remove();
                                batchDisposeMathfields([mf]);
                                if (next)
                                    placeCaretAfter(next);
                                else
                                    (_b = editorRef.current) === null || _b === void 0 ? void 0 : _b.focus({ preventScroll: true });
                            }
                            else {
                                // Has content — move cursor just after the math-field
                                var spacer = mf.nextSibling;
                                placeCaretAfter(spacer !== null && spacer !== void 0 ? spacer : mf);
                                lastFocusedMathField.current = null;
                            }
                            setmathKeyboardVisible(false);
                            setActiveMathText && setActiveMathText(-1);
                            getMathData();
                        }
                    }
                    else {
                        // Arm the post-open focus shift before insertMath kicks the
                        // mf-focus → keypad-open chain. The keypad-visibility useEffect
                        // reads this ref once the keypad mounts and lands focus on '1'.
                        focusKeypadOnOpenRef.current = true;
                        // With the caret already inside an expression this button means
                        // "bring the keypad back to it". It used to always insertMath(),
                        // which dropped a second empty math-field into the cell instead of
                        // reopening the keypad on the fraction the user was editing.
                        if (!reopenKeypadAtCaret())
                            insertMath();
                    }
                    break;
                case 'closeMathText':
                    setmathKeyboardVisible(false);
                    setSelectedTool([]);
                    setActiveMathText && setActiveMathText(-1);
                    break;
                case 'linkInput':
                    formatLink(data.val);
                    break;
                case 'removeLink':
                    formatLink('');
                    break;
                case 'leftAlign':
                    if (isTextUpdate && typeof index == 'number') {
                        setText(function (prev) {
                            var newText = __spreadArray([], prev, true);
                            var oldData = newText[index].data.alignment;
                            newText[index].data.alignment = 'left';
                            saveState({
                                type: 'text',
                                id: index,
                                propertyName: 'alignment',
                                old: oldData,
                                new: 'left',
                            });
                            return newText;
                        });
                    }
                    else {
                        cb === null || cb === void 0 ? void 0 : cb('left', 'alignment', index);
                    }
                    break;
                case 'centerAlign':
                    if (isTextUpdate && typeof index == 'number') {
                        setText(function (prev) {
                            var newText = __spreadArray([], prev, true);
                            var target = __assign({}, newText[index]);
                            var newData = __assign({}, target.data);
                            var oldData = newText[index].data.alignment;
                            newData.alignment = 'center';
                            target.data = newData;
                            newText[index] = target;
                            saveState({
                                type: 'text',
                                id: index,
                                propertyName: 'alignment',
                                old: oldData,
                                new: 'center',
                            });
                            return newText;
                        });
                    }
                    else {
                        cb === null || cb === void 0 ? void 0 : cb('center', 'alignment', index);
                    }
                    break;
                case 'rightAlign':
                    if (isTextUpdate && typeof index == 'number') {
                        setText(function (prev) {
                            var newText = __spreadArray([], prev, true);
                            var target = __assign({}, newText[index]);
                            var newData = __assign({}, target.data);
                            var oldData = newText[index].data.alignment;
                            newData.alignment = 'right';
                            target.data = newData;
                            newText[index] = target;
                            saveState({
                                type: 'text',
                                id: index,
                                propertyName: 'alignment',
                                old: oldData,
                                new: 'right',
                            });
                            return newText;
                        });
                    }
                    else {
                        cb === null || cb === void 0 ? void 0 : cb('right', 'alignment', index);
                    }
                    break;
                case 'larger':
                    // Apply font size increase to selected text/math only. Table cells manage
                    // font size entirely through TableManipulativeComp's own bulk handler (a
                    // per-selection span override here would desync from that cell-level model).
                    if (idPreffix !== 'tableText') {
                        execFontSize('larger');
                        if (!isTextUpdate) {
                            cb === null || cb === void 0 ? void 0 : cb('up', 'fontSize', index);
                        }
                    }
                    break;
                case 'smaller':
                    // Apply font size decrease to selected text/math only. See 'larger' above.
                    if (idPreffix !== 'tableText') {
                        execFontSize('smaller');
                        if (!isTextUpdate) {
                            cb === null || cb === void 0 ? void 0 : cb('down', 'fontSize', index);
                        }
                    }
                    break;
            }
        };
        // Force sync: immediately write current DOM content to state
        // (bypasses mathKeyboardVisible guard used by getMathData)
        var forceSync = function () {
            var editor = editorRef.current;
            if (!editor || viewOnlyRef.current)
                return;
            var output = calcMathData();
            if (isTextUpdate && typeof index == 'number' && !isManipulative) {
                setText(function (prev) {
                    var newText = __spreadArray([], prev, true);
                    if (!newText[index] || !newText[index].data)
                        return prev;
                    newText[index] = __assign(__assign({}, newText[index]), { data: __assign(__assign({}, newText[index].data), { text: output }) });
                    return newText;
                });
            }
        };
        if (isFocused) {
            customEventHandler.addEventListener('colourChange', changeColour);
            customEventHandler.addEventListener('bottomPanel', onClick);
            customEventHandler.addEventListener('forceSync', forceSync);
        }
        else {
            getMathData();
        }
        return function () {
            customEventHandler.removeEventListener('colourChange', changeColour);
            customEventHandler.removeEventListener('bottomPanel', onClick);
            customEventHandler.removeEventListener('forceSync', forceSync);
        };
    }, [isFocused, color, mlReady]);
    // Tell this editor's focus-time scrollIntoView to stand down: any keypad
    // press re-focuses the editorDiv, and that onFocus scroll would fight the
    // insert instead of helping. Called by preserveScroll() and, separately, by
    // the two keypad branches that re-focus WITHOUT going through it — `space`
    // (`mf.insert('\;')`) and `clear` (bare `ensureFocus()`). Those two produce
    // the same visible jump as a fraction insert, so they need the same guard
    // even though they have no scroll position worth preserving.
    var armInsertScrollGuard = function () {
        insertScrollGuardRef.current = Date.now();
    };
    // Save and restore all scroll positions around a mf.insert() call.
    // mf.insert() for structural commands (frac, sqrt, exponent, placeholder)
    // triggers MathLive's async scrollIntoView on a later frame, jumping the
    // viewport. We lock the scroll container by immediately snapping it back
    // inside a 'scroll' event listener for a 500 ms window after the insert.
    var preserveScroll = function (fn) {
        var _a, _b, _c, _d, _e;
        var scrollEl = (_a = editorRef.current) === null || _a === void 0 ? void 0 : _a.closest('.customScrollbarContent');
        var savedLeft = (_b = scrollEl === null || scrollEl === void 0 ? void 0 : scrollEl.scrollLeft) !== null && _b !== void 0 ? _b : 0;
        var savedTop = (_c = scrollEl === null || scrollEl === void 0 ? void 0 : scrollEl.scrollTop) !== null && _c !== void 0 ? _c : 0;
        var winX = window.scrollX;
        var winY = window.scrollY;
        var docEl = document.scrollingElement;
        var docLeft = (_d = docEl === null || docEl === void 0 ? void 0 : docEl.scrollLeft) !== null && _d !== void 0 ? _d : winX;
        var docTop = (_e = docEl === null || docEl === void 0 ? void 0 : docEl.scrollTop) !== null && _e !== void 0 ? _e : winY;
        // iOS 26: the setter override below only neutralises the CONTAINER's
        // scroll. iOS additionally scrolls the *document* to bring the caret into
        // view on every insert (miscalculated under the canvas transform), and the
        // window/doc restore at the bottom of this function runs 500ms later — so
        // inserting a fraction painted a visible jump that snapped back "within a
        // second". These are the same rAF-before-paint holds the math-field `input`
        // and editorDiv `onChangeHandler` already use; it re-asserts the saved
        // position every frame (~24 frames), so iOS's scroll is reverted before it
        // is shown and the 500ms restore below only has to finish the job.
        // Deliberately NOT holdElScrollBriefly() here: the setter override makes
        // the container's scrollTop/scrollLeft read back as the saved value, so a
        // container hold would be a permanent no-op for as long as it runs.
        holdWindowScrollBriefly();
        armInsertScrollGuard();
        // Override scrollLeft/scrollTop setters on the container so MathLive's
        // synchronous scrollIntoView inside mf.insert() is a no-op — this
        // prevents the one-frame blink that the event-listener approach cannot.
        // The getter returns the saved value so MathLive's internal layout reads
        // still see a consistent position.
        if (scrollEl) {
            Object.defineProperty(scrollEl, 'scrollLeft', {
                configurable: true,
                get: function () { return savedLeft; },
                // Deliberate no-op: swallowing the assignment IS the lock. Must not
                // log — preserveScroll wraps every keypad insert, so a console call
                // here fires once per keypress in a user's production console.
                set: function () { },
            });
            Object.defineProperty(scrollEl, 'scrollTop', {
                configurable: true,
                get: function () { return savedTop; },
                set: function () { },
            });
        }
        fn();
        // Unlock after MathLive has fully settled, then restore the saved position.
        setTimeout(function () {
            if (scrollEl) {
                delete scrollEl.scrollLeft;
                delete scrollEl.scrollTop;
                scrollEl.scrollLeft = savedLeft;
                scrollEl.scrollTop = savedTop;
            }
            window.scrollTo(winX, winY);
            if (docEl) {
                docEl.scrollLeft = docLeft;
                docEl.scrollTop = docTop;
            }
        }, 500);
    };
    var keypadBtnClickHandler = function (sae) {
        var _a, _b;
        // Debounce guard: prevent duplicate execution from touch+mouse dual-fire
        // or Firefox focus/blur re-entry within a single interaction
        var now = Date.now();
        if (now - keypadGuardRef.current < 80)
            return;
        keypadGuardRef.current = now;
        if (viewOnlyRef.current)
            return;
        var mf = lastFocusedMathField.current;
        var editor = editorRef.current;
        if (!editor && !mf)
            return;
        // Check character limit before inserting (except for backspace and clear).
        // Skip when inside a \frac argument — the input-event revert handler enforces
        // the per-side limit so each fraction side can independently reach maxCharacters.
        if (maxCharacters && sae.id !== 'back' && sae.id !== 'clear') {
            var currentCount = getTotalCharacterCount();
            if (currentCount >= maxCharacters && !(mf && isInFrac(mf))) {
                return;
            }
        }
        var keyMap = {
            one: '1',
            two: '2',
            three: '3',
            four: '4',
            five: '5',
            six: '6',
            seven: '7',
            eight: '8',
            nine: '9',
            zero: '0',
            plus: '+',
            minus: '-',
            multiply: '\\times ',
            divideSign: '\\div ',
            equals: '=',
            dot: '.',
            comma: ',',
            percent: '\\%',
            leftParen: '(',
            rightParen: ')',
            lessThan: '<',
            greaterThan: '>',
            lessThanOrEqual: '\\le ',
            greaterThanOrEqual: '\\ge ',
            dollar: '\\$',
            cent: '\\text{¢}',
            questionMark: '\\placeholder{}',
        };
        // Helper: only refocus if mathfield doesn't already have focus.
        // With the blur-handler keypad guard the mathfield stays focused,
        // so calling focus() again would disrupt cursor position (e.g.,
        // inside fraction placeholders).
        var ensureFocus = function () {
            if (mf && document.activeElement !== mf) {
                safeFocusMathField(mf);
            }
        };
        // Check if cursor is inside a specific LaTeX command (e.g. \frac or ^)
        // Uses two counters: commandSeen (hit a \frac or ^) and braceDepth (all
        // open braces, including those of nested commands like \placeholder{ }).
        // Check if cursor is inside a \frac or superscript by walking up
        // MathLive's internal atom tree. The model is on _mathfield.model
        // (not directly on the MathfieldElement custom element).
        // - 'genfrac' = \frac atom type
        // - parentBranch 'superscript' = cursor is inside ^{} argument
        // - !hasEmptyBranch('superscript') = cursor is at the BASE of a power
        //   (after inserting \placeholder{}^{\placeholder{}}, MathLive places
        //   the cursor at the base placeholder which is at root level, not in
        //   the superscript branch, so parentBranch check alone is not enough)
        var isCursorInside = function (command) {
            var _a;
            if (!mf)
                return false;
            try {
                var model = (_a = mf._mathfield) === null || _a === void 0 ? void 0 : _a.model;
                if (!model)
                    return false;
                var atom = model.at(model.position);
                while (atom && atom.type !== 'root') {
                    if (command === 'frac' && atom.type === 'genfrac')
                        return true;
                    if (command === 'superscript') {
                        // Case 1: cursor is inside the ^{} exponent argument
                        if (atom.parentBranch === 'superscript')
                            return true;
                        // Case 2: this atom itself has a non-empty superscript
                        if (typeof atom.hasEmptyBranch === 'function' && !atom.hasEmptyBranch('superscript'))
                            return true;
                        // Case 3 (base-as-sibling): MathLive may place the base atom and
                        // SubsupAtom as siblings in the mathlist rather than parent-child.
                        // Check if the NEXT sibling has a non-empty superscript branch.
                        if (atom.parent && atom.parentBranch != null) {
                            var siblings = atom.parent.branch(atom.parentBranch);
                            if (siblings) {
                                for (var i = 0; i < siblings.length; i++) {
                                    if (siblings[i] === atom && i + 1 < siblings.length) {
                                        var next = siblings[i + 1];
                                        if (typeof next.hasEmptyBranch === 'function' &&
                                            !next.hasEmptyBranch('superscript'))
                                            return true;
                                        if (next.superscript)
                                            return true;
                                        break;
                                    }
                                }
                            }
                        }
                    }
                    atom = atom.parent;
                }
            }
            catch (_b) {
                // model API not accessible — fall through
            }
            return false;
        };
        if (sae.id === 'spaceBar') {
            if (mf) {
                ensureFocus();
                // Same \frac exemption as the master gate, the keydown gate and the
                // generic-insert gate: inside a fraction the count is Math.max of the
                // two sides, so a full numerator makes the total look full while the
                // denominator is still empty. Without this the space key was silently
                // dropped where the digit keys inserted fine.
                if (maxCharacters && !isInFrac(mf)) {
                    var currentCount = getTotalCharacterCount();
                    if (currentCount >= maxCharacters) {
                        announce(t('text.mathEditor.charLimitReached'));
                        return;
                    }
                }
                armInsertScrollGuard();
                mf.insert('\\;');
            }
            else {
                insertTextIntoEditor(' ');
            }
            return;
        }
        if (sae.id === 'back') {
            if (mf) {
                if (mf.value === '' || isMfOnlyEmptyFrac(mf.value)) {
                    // Math-field is empty or contains only an empty fraction template — remove it
                    var next = mf.nextSibling;
                    mathFieldInstancesRef.current.delete(mf);
                    if (lastFocusedMathField.current === mf) {
                        lastFocusedMathField.current = null;
                    }
                    mf.remove();
                    batchDisposeMathfields([mf]);
                    setmathKeyboardVisible(false);
                    setActiveMathText && setActiveMathText(-1);
                    if (next)
                        placeCaretAfter(next);
                    else
                        editor === null || editor === void 0 ? void 0 : editor.focus({ preventScroll: true });
                    getMathData();
                }
                else {
                    ensureFocus();
                    mf.executeCommand('deleteBackward');
                }
            }
            else {
                deleteBackwardInEditor();
            }
            return;
        }
        if (sae.id === 'power') {
            if (mf) {
                // DO NOT REMOVE — Nested power (^) prevention.
                // See docs/components/MathEditor.md "Nested Power (`^`) Prevention" section.
                //
                // Prevents power-inside-power nesting. Uses isCursorInside which
                // walks the atom tree and checks:
                //   Case 1: parentBranch === 'superscript'       → cursor in exponent
                //   Case 2: !hasEmptyBranch('superscript')       → atom has superscript
                //   Case 3: next sibling has superscript          → base-as-sibling
                if (isCursorInside('superscript'))
                    return;
                ensureFocus();
                // \frac exemption — see the spaceBar branch above.
                if (maxCharacters && !isInFrac(mf)) {
                    var currentCount = getTotalCharacterCount();
                    if (currentCount >= maxCharacters) {
                        announce(t('text.mathEditor.charLimitReached'));
                        return;
                    }
                }
                preserveScroll(function () { return mf.insert('\\placeholder{ }^{ \\placeholder{ }}'); });
            }
            return;
        }
        if (sae.id === 'clear') {
            if (mf) {
                mf.value = '';
                // Reset the revert baseline too. Assigning `value` programmatically is
                // not guaranteed to dispatch `input`, so without this `data-prev-value`
                // keeps the pre-clear content — and the next over-limit insert would
                // revert to it, resurrecting what the user just cleared.
                if (maxCharacters) {
                    mf.setAttribute('data-prev-value', '');
                    mf.setAttribute('data-prev-position', '');
                }
                armInsertScrollGuard();
                ensureFocus();
            }
            else {
                editor.innerText = '';
            }
            announce(t('text.mathEditor.mathCleared'));
            return;
        }
        if (sae.id === 'slash') {
            if (!mf) {
                insertTextIntoEditor('/');
                return;
            }
            // Check before ensureFocus() for the same reason as the power guard above
            if (isCursorInside('frac'))
                return;
            ensureFocus();
            if (maxCharacters) {
                var currentCount = getTotalCharacterCount();
                if (currentCount >= maxCharacters) {
                    return;
                }
            }
            preserveScroll(function () { return mf.insert("\\frac{\\placeholder{ }}{\\placeholder{ }}"); });
            return;
        }
        if (sae.id === 'questionMark') {
            if (!mf) {
                insertTextIntoEditor('?');
                return;
            }
            ensureFocus();
            // \frac exemption — see the spaceBar branch above.
            if (maxCharacters && !isInFrac(mf)) {
                var currentCount = getTotalCharacterCount();
                if (currentCount >= maxCharacters) {
                    announce(t('text.mathEditor.charLimitReached'));
                    return;
                }
            }
            // Insert placeholder and position cursor after it (not inside it)
            preserveScroll(function () { return mf.insert('\\placeholder{}', { selectionMode: 'after' }); });
            return;
        }
        var insertValue = (_b = (_a = keyMap[sae.id]) !== null && _a !== void 0 ? _a : sae === null || sae === void 0 ? void 0 : sae.title) !== null && _b !== void 0 ? _b : '';
        if (!insertValue)
            return;
        if (mf) {
            ensureFocus();
            // Check limit again before inserting. Same \frac exemption as the master
            // gate above and the keydown gate: inside a fraction the count is the
            // Math.max of the two sides, so a full numerator makes the total look
            // full while the denominator is still empty. Let the insert through and
            // leave the per-side limit to the input-event revert handler, which
            // compares with > rather than >=. Without this the keypad's digit keys
            // could not fill a fraction's second side on a small maxCharacters label
            // (a 1/12 fraction tile allows 2) even though physical typing could.
            if (maxCharacters) {
                var currentCount = getTotalCharacterCount();
                if (currentCount >= maxCharacters && !isInFrac(mf)) {
                    return;
                }
            }
            preserveScroll(function () { return mf.insert(insertValue); });
            return;
        }
        insertTextIntoEditor(insertValue.replace(/\\(.)/g, '$1'));
    };
    var insertTextIntoEditor = function (text) {
        var _a, _b, _c, _d;
        var editor = editorRef.current;
        if (!editor)
            return;
        // Check character limit before inserting
        if (maxCharacters) {
            var currentCount = getTotalCharacterCount();
            var textToInsert = text.length;
            if (currentCount + textToInsert > maxCharacters) {
                // Only insert what fits
                var remaining = maxCharacters - currentCount;
                if (remaining <= 0) {
                    return; // No space left
                }
                text = text.substring(0, remaining);
            }
        }
        editor.focus({ preventScroll: true });
        var sel = window.getSelection();
        if (!sel || sel.rangeCount === 0)
            return;
        var range = sel.getRangeAt(0);
        range.deleteContents();
        // Wrap the insertion in a span carrying the current picker color
        // whenever the caret isn't already inside a span with that color.
        // Keypad inserts skip the beforeinput color-wrap path, so without this,
        // picking a new color (or refocusing a textbox after a color change)
        // and then typing via the keypad inherits the surrounding stale color.
        // The changeColour handler bails when the selection is lost during the
        // picker interaction (common on iPad), so we cannot rely on the anchor
        // span it normally inserts — every insert must enforce the color
        // itself.
        var buildInsertNode = function () {
            var _a;
            if (!isTextUpdate || isManipulative) {
                return document.createTextNode(text);
            }
            var pickerColor = ColorEnum[color] || ColorEnum['BLACK'];
            var toRGB = function (c) {
                var _a, _b, _c, _d;
                var s = c.trim().toLowerCase();
                var hex = (_b = (_a = s.match(/^#([0-9a-f]{6})$/i)) === null || _a === void 0 ? void 0 : _a[1]) !== null && _b !== void 0 ? _b : (_d = (_c = s.match(/^#([0-9a-f]{3})$/i)) === null || _c === void 0 ? void 0 : _c[1]) === null || _d === void 0 ? void 0 : _d.replace(/./g, '$&$&');
                if (hex) {
                    return "".concat(parseInt(hex.slice(0, 2), 16), ",").concat(parseInt(hex.slice(2, 4), 16), ",").concat(parseInt(hex.slice(4, 6), 16));
                }
                var m = s.match(/rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/);
                return m ? "".concat(m[1], ",").concat(m[2], ",").concat(m[3]) : s;
            };
            var normalizedPicker = toRGB(pickerColor);
            var node = range.startContainer;
            var alreadyCorrect = false;
            while (node && node !== editor) {
                if (node instanceof HTMLElement && node.style.color) {
                    alreadyCorrect = toRGB(node.style.color) === normalizedPicker;
                    break;
                }
                node = node.parentNode;
            }
            if (alreadyCorrect)
                return document.createTextNode(text);
            // If the immediately-preceding sibling is a span with the same
            // picker color, append into it so the keypad doesn't fragment
            // typed runs into a chain of single-character spans.
            var prevNode = range.startContainer.nodeType === Node.TEXT_NODE
                ? range.startContainer.parentElement
                : range.startContainer.childNodes[range.startOffset - 1];
            var prevSpan = prevNode instanceof HTMLSpanElement &&
                prevNode.style.color &&
                toRGB(prevNode.style.color) === normalizedPicker
                ? prevNode
                : null;
            if (prevSpan) {
                // Append into the span's existing trailing text node when present so
                // consecutive same-color chars stay in a SINGLE text node — separate
                // text nodes form separate shaping runs and the browser renders the
                // glyphs with subtle width / kerning differences (visible as
                // "the font changed" on duplicated letters like "ee" / "aa").
                var last = prevSpan.lastChild;
                if (last && last.nodeType === Node.TEXT_NODE) {
                    last.data = ((_a = last.data) !== null && _a !== void 0 ? _a : '') + text;
                    return last;
                }
                var t_2 = document.createTextNode(text);
                prevSpan.appendChild(t_2);
                return t_2;
            }
            var span = document.createElement('span');
            span.style.color = pickerColor;
            span.textContent = text;
            return span;
        };
        var insertNode = buildInsertNode();
        // Append-into-prevSpan path already attached the node; nothing to insert
        // at the range. Detect by checking parent — a freshly-created node has
        // no parent until insertNode() runs.
        if (!insertNode.parentNode) {
            range.insertNode(insertNode);
        }
        // Place caret at the end of the inserted text (inside the new/existing
        // span when wrapped, otherwise after the plain text node) so subsequent
        // keypad inserts merge into the same colored run.
        if (insertNode.nodeType === Node.ELEMENT_NODE) {
            var lastChild = insertNode.firstChild;
            if (lastChild) {
                range.setStart(lastChild, (_b = (_a = lastChild.textContent) === null || _a === void 0 ? void 0 : _a.length) !== null && _b !== void 0 ? _b : 0);
            }
            else {
                range.setStartAfter(insertNode);
            }
        }
        else {
            range.setStart(insertNode, (_d = (_c = insertNode.textContent) === null || _c === void 0 ? void 0 : _c.length) !== null && _d !== void 0 ? _d : 0);
        }
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);
    };
    var deleteBackwardInEditor = function () {
        var editor = editorRef.current;
        if (!editor)
            return;
        editor.focus({ preventScroll: true });
        var sel = window.getSelection();
        if (!sel || sel.rangeCount === 0)
            return;
        var range = sel.getRangeAt(0);
        if (!range.collapsed) {
            range.deleteContents();
            return;
        }
        var startContainer = range.startContainer, startOffset = range.startOffset;
        if (startContainer.nodeType === Node.TEXT_NODE && startOffset > 0) {
            var textNode = startContainer;
            textNode.deleteData(startOffset - 1, 1);
            range.setStart(textNode, startOffset - 1);
            range.collapse(true);
            sel.removeAllRanges();
            sel.addRange(range);
        }
    };
    var focusHandler = function (e) {
        if (viewOnlyRef.current && !selectOnViewOnly)
            return;
        // If this item is already part of a multi-selection and no modifier key
        // is pressed, keep the multi-selection intact so dragging one item moves
        // all selected items together via the resizer.
        var mouseEvt = e;
        var isModifier = mouseEvt.ctrlKey || mouseEvt.metaKey || mouseEvt.shiftKey;
        if (!isModifier && selectedTool.length > 1 && selectedTool.includes(selectionType)) {
            return;
        }
        var _tool = selectTool(mouseEvt, selectionType, selectedTool, false);
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
        setSelectedTool(_tool);
        onFocusEvent && (cb === null || cb === void 0 ? void 0 : cb(calcMathData(), 'focus', index));
        // Firefox fix: in Firefox, pointerdown.stopPropagation() on math-field
        // can prevent Firefox from completing native focus assignment in popup
        // contexts. When the editor has no DOM focus at all and the click landed
        // on the outer mathEditorParent wrapper, focus the appropriate element
        // synchronously inside the click handler (user-gesture context).
        if (e.type === 'click') {
            var editor = editorRef.current;
            if (editor && !editor.contains(document.activeElement)) {
                var clickedMf = e.target.closest('math-field');
                var mathField = clickedMf !== null && clickedMf !== void 0 ? clickedMf : editor.querySelector('math-field');
                if (mathField) {
                    safeFocusMathField(mathField);
                }
                else {
                    editor.focus({ preventScroll: true });
                }
            }
        }
    };
    /**
     * Put a zero-width-space text node on every blank line (each pair of
     * adjacent `<br>`s), so the caret has somewhere to live there.
     *
     * A blank line built as `<br><br>` offers only an *element*-anchored caret
     * position — `SPAN`/offset 2, between the two elements. Chromium reports a
     * zero-size rect for such a position and paints no caret, so clicking a
     * blank line or arrowing down onto one looked like the cursor had vanished
     * or jumped elsewhere, even though the selection was correct (typing there
     * always landed on the right line). Anchoring the caret in a text node
     * instead gives it a real rect on the blank line's line box.
     *
     * U+200B is the same spacer this editor already places around math-fields
     * for caret positioning, and `calcMathData` strips it back out of text runs
     * on save.
     */
    var ensureBlankLineCarets = function (root) {
        var _a;
        if (!root)
            return;
        var nodes = [];
        var walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT);
        for (var node = walker.nextNode(); node; node = walker.nextNode()) {
            var child = node;
            if (node.nodeType === Node.TEXT_NODE) {
                var text = (_a = node.textContent) !== null && _a !== void 0 ? _a : '';
                var stripped = text.replace(new RegExp(WHITESPACE_CHAR, 'g'), '');
                // An all-ZWS node is an existing spacer — tracking it is what keeps
                // this idempotent across the hydration and insertLineBreak calls. A
                // truly empty text node is neither content nor spacer: ignore it, or
                // the shape a math-field insert leaves behind reads as already fixed.
                if (stripped.length)
                    nodes.push({ node: child, kind: 'content' });
                else if (text.length)
                    nodes.push({ node: child, kind: 'spacer' });
            }
            else if (node.nodeName === 'BR') {
                nodes.push({ node: child, kind: 'br' });
            }
            else if (node.nodeName === 'MATH-FIELD' || node.nodeName === 'IMG') {
                nodes.push({ node: child, kind: 'content' });
            }
        }
        var spacer = function () { return document.createTextNode(WHITESPACE_CHAR); };
        // A blank first line. Guarded on there being something after it: an editor
        // whose only node is a bogus `<br>` is an EMPTY box, not one starting with
        // a blank line, and giving it a spacer would make it read as having content.
        if (nodes.length > 1 && nodes[0].kind === 'br') {
            nodes[0].node.before(spacer());
        }
        // Any two `<br>`s with nothing caret-bearing between them.
        for (var i = 0; i < nodes.length - 1; i++) {
            if (nodes[i].kind === 'br' && nodes[i + 1].kind === 'br') {
                nodes[i].node.after(spacer());
            }
        }
    };
    /**
     * An all-ZWS text node — a caret spacer. A **zero-length** text node is not
     * one: it renders nothing and can hold no caret, which is the same rule
     * `ensureBlankLineCarets` applies when it classifies nodes. The editor
     * routinely carries a leading empty text node, so conflating the two puts
     * the caret somewhere it cannot be seen.
     */
    var isSpacerText = function (node) {
        var _a;
        if (node.nodeType !== Node.TEXT_NODE)
            return false;
        var text = (_a = node.textContent) !== null && _a !== void 0 ? _a : '';
        return text.length > 0 && text.replace(new RegExp(WHITESPACE_CHAR, 'g'), '') === '';
    };
    /**
     * The caret spacer of a blank FIRST line, when the caret sits in it and that
     * line is still empty. Backspace has to special-case this one: the spacer
     * sits *before* the leading `<br>` (that line has no `<br>` of its own to
     * follow), so the browser's own Backspace eats the spacer and then finds
     * nothing before the `<br>` left to delete — the blank line survives, and
     * with its spacer gone the caret on it is invisible again. Returns the pair
     * to remove so the handler can delete the line itself instead.
     */
    var blankFirstLineSpacerAtCaret = function () {
        var _a;
        var editor = editorRef.current;
        var sel = window.getSelection();
        if (!editor || !sel || !sel.isCollapsed || sel.rangeCount === 0)
            return null;
        var anchor = sel.anchorNode;
        if (!anchor || !isSpacerText(anchor))
            return null;
        // Nothing may render before the spacer, and its line must be terminated by
        // a `<br>` — otherwise this is a spacer somewhere in the middle, which the
        // browser deletes correctly on its own.
        var walker = document.createTreeWalker(editor, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT);
        var seenSpacer = false;
        for (var node = walker.nextNode(); node; node = walker.nextNode()) {
            if (node === anchor) {
                seenSpacer = true;
            }
            else if (node.nodeName === 'BR') {
                return seenSpacer ? { spacer: anchor, br: node } : null;
            }
            else if (node.nodeName === 'MATH-FIELD' || node.nodeName === 'IMG') {
                return null;
            }
            else if (node.nodeType === Node.TEXT_NODE &&
                !isSpacerText(node) &&
                ((_a = node.textContent) !== null && _a !== void 0 ? _a : '').length > 0) {
                return null;
            }
        }
        return null;
    };
    /**
     * Insert a line break (<br>) at the current cursor position.
     * Two <br>s are inserted when the cursor is at the very end of the
     * editor content — a single trailing <br> in contentEditable is just
     * the line terminator and doesn't produce a visible new line.
     */
    var insertLineBreak = function () {
        var _a;
        var sel = window.getSelection();
        if (!sel || sel.rangeCount === 0)
            return;
        var range = sel.getRangeAt(0);
        range.deleteContents();
        var br = document.createElement('br');
        range.insertNode(br);
        // If the <br> is now the last meaningful node (only followed by
        // whitespace text nodes or nothing), add a second <br> so the
        // browser actually renders the new blank line.
        var next = br.nextSibling;
        while (next &&
            next.nodeType === Node.TEXT_NODE &&
            !((_a = next.textContent) !== null && _a !== void 0 ? _a : '').replace(/[\u200B\s]/g, '').length) {
            next = next.nextSibling;
        }
        if (!next) {
            var extra = document.createElement('br');
            br.after(extra);
        }
        // Give the new blank line something the caret can be anchored *inside*.
        ensureBlankLineCarets(editorRef.current);
        // Place cursor on the new line — inside its spacer text node when one was
        // just added, so the caret has a layout box (see ensureBlankLineCarets).
        var spacer = br.nextSibling;
        if (spacer && spacer.nodeType === Node.TEXT_NODE && spacer.textContent === WHITESPACE_CHAR) {
            range.setStart(spacer, 1);
        }
        else {
            range.setStartAfter(br);
        }
        range.collapse(true);
        sel.removeAllRanges();
        sel.addRange(range);
    };
    /**
     * The `<span style="font-size">` that ends immediately before a collapsed
     * caret, when the caret itself sits outside every font-size span.
     *
     * `applyFontSize` carries a resized run in such a span, so a caret parked at
     * the end of the box is a *sibling after* it and inherits the editor's base
     * size instead — which is why text appended to a resized box came out
     * visibly smaller than the text it was appended to. Returns the span so the
     * caller can keep typing inside it, or null when there is nothing to
     * continue.
     */
    var sizedSpanBeforeCaret = function (range) {
        var _a, _b;
        var editor = editorRef.current;
        if (!editor)
            return null;
        var startEl = range.startContainer.nodeType === Node.ELEMENT_NODE
            ? range.startContainer
            : range.startContainer.parentElement;
        if (!startEl || !editor.contains(startEl))
            return null;
        // Already inside a sized span — normal CSS inheritance is correct here.
        if (startEl.closest('span[style*="font-size"]'))
            return null;
        var prev;
        if (range.startContainer.nodeType === Node.TEXT_NODE) {
            // Only a caret at the very start of its text node sits directly after a
            // sibling; anywhere else the preceding character is this text node's own.
            if (range.startOffset !== 0)
                return null;
            prev = range.startContainer.previousSibling;
        }
        else {
            prev = (_a = range.startContainer.childNodes[range.startOffset - 1]) !== null && _a !== void 0 ? _a : null;
        }
        // Step over the ZWS spacers and the empty text nodes splitText leaves.
        while (prev &&
            prev.nodeType === Node.TEXT_NODE &&
            !((_b = prev.textContent) !== null && _b !== void 0 ? _b : '').replace(new RegExp(WHITESPACE_CHAR, 'g'), '').length) {
            prev = prev.previousSibling;
        }
        return prev instanceof HTMLElement && prev.style.fontSize ? prev : null;
    };
    // Post-input safety net for picker-color enforcement. Runs after every
    // input event; if the just-typed chars landed in a span whose color
    // doesn't match the current picker (e.g. preventDefault didn't fire
    // because the inputType wasn't `insertText`, or some other path inserted
    // raw text), extract that run and rewrap it. Belt-and-suspenders for the
    // long-standing client report that "newly typed text follows the old
    // color after focus-out → focus-in".
    var enforceCaretColor = function (e) {
        var _a, _b;
        var ne = e.nativeEvent;
        if (!isTextUpdate || isManipulative || viewOnlyRef.current) {
            return;
        }
        if (!ne.data) {
            return;
        }
        // Only handle textual insertions (cover the variants iPad/desktop
        // browsers emit for keyboard typing, predictive text, autocomplete,
        // and paste). Skip composition mid-progress to avoid mid-IME mutations.
        var allowed = new Set([
            'insertText',
            'insertReplacementText',
            'insertFromComposition',
            'insertFromPaste',
        ]);
        if (!allowed.has(ne.inputType)) {
            return;
        }
        var insertedLen = ne.data.length;
        if (insertedLen <= 0)
            return;
        var sel = window.getSelection();
        if (!sel || sel.rangeCount === 0 || !sel.isCollapsed)
            return;
        var range = sel.getRangeAt(0);
        var editor = editorRef.current;
        if (!editor || !editor.contains(range.startContainer))
            return;
        if (range.startContainer.nodeType !== Node.TEXT_NODE)
            return;
        var textNode = range.startContainer;
        var startOffset = range.startOffset;
        if (startOffset < insertedLen)
            return;
        var pickerColor = ColorEnum[color] || ColorEnum['BLACK'];
        var toRGB = function (c) {
            var _a, _b, _c, _d;
            var s = c.trim().toLowerCase();
            var hex = (_b = (_a = s.match(/^#([0-9a-f]{6})$/i)) === null || _a === void 0 ? void 0 : _a[1]) !== null && _b !== void 0 ? _b : (_d = (_c = s.match(/^#([0-9a-f]{3})$/i)) === null || _c === void 0 ? void 0 : _c[1]) === null || _d === void 0 ? void 0 : _d.replace(/./g, '$&$&');
            if (hex) {
                return "".concat(parseInt(hex.slice(0, 2), 16), ",").concat(parseInt(hex.slice(2, 4), 16), ",").concat(parseInt(hex.slice(4, 6), 16));
            }
            var m = s.match(/rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/);
            return m ? "".concat(m[1], ",").concat(m[2], ",").concat(m[3]) : s;
        };
        var normalizedPicker = toRGB(pickerColor);
        // If the caret's nearest colored ancestor already matches the picker,
        // no fix-up needed.
        var coloredAncestor = null;
        var node = textNode.parentNode;
        while (node && node !== editor) {
            if (node instanceof HTMLElement && node.style.color) {
                coloredAncestor = node;
                break;
            }
            node = node.parentNode;
        }
        if (coloredAncestor && toRGB(coloredAncestor.style.color) === normalizedPicker) {
            return;
        }
        // The just-typed chars sit at [startOffset - insertedLen, startOffset)
        // in textNode. Split textNode at startOffset so chars after the caret
        // become a sibling, then strip the inserted run from textNode.
        var insertedText = textNode.substringData(startOffset - insertedLen, insertedLen);
        var remainingNode = startOffset < textNode.length ? textNode.splitText(startOffset) : null;
        textNode.deleteData(startOffset - insertedLen, insertedLen);
        var parent = textNode.parentNode;
        if (!parent)
            return;
        // If the previous sibling element is a span with a matching color,
        // extend its trailing text node so the just-typed run merges into the
        // existing same-color shaping run. Without this, two adjacent spans
        // end up holding the same color but with separate element boundaries —
        // the browser starts a new shaping run between them and duplicated
        // letters ("ee" / "aa") render with subtly different glyph metrics.
        var insertAfterNode = textNode; // chars are inserted after textNode
        var adjacentEl = insertAfterNode.previousSibling
            ? // previousSibling-of-our-textNode is the wrong direction; we need
                // the element that sits just BEFORE the typed run, which is the
                // textNode itself's preceding element if textNode is now empty,
                // otherwise textNode acts as the boundary itself.
                null
            : null;
        // The actual lookup: the span we want to merge with is either textNode
        // itself (if textNode's parent is a color span and we're inside it) or
        // the previous sibling element of textNode if textNode is at the
        // editor level.
        var mergeTarget = null;
        if (parent instanceof HTMLSpanElement &&
            parent.style.color &&
            toRGB(parent.style.color) === normalizedPicker) {
            mergeTarget = parent;
        }
        else {
            var prev = textNode.previousSibling;
            if (prev instanceof HTMLSpanElement &&
                prev.style.color &&
                toRGB(prev.style.color) === normalizedPicker) {
                mergeTarget = prev;
            }
        }
        if (mergeTarget) {
            var last = mergeTarget.lastChild;
            var target = void 0;
            if (last && last.nodeType === Node.TEXT_NODE) {
                last.data = ((_a = last.data) !== null && _a !== void 0 ? _a : '') + insertedText;
                target = last;
            }
            else {
                target = document.createTextNode(insertedText);
                mergeTarget.appendChild(target);
            }
            // Clean up empty source text nodes that splitText left behind
            if (textNode.length === 0 && textNode.parentNode) {
                textNode.parentNode.removeChild(textNode);
            }
            var newRange_2 = document.createRange();
            newRange_2.setStart(target, ((_b = target.data) !== null && _b !== void 0 ? _b : '').length);
            newRange_2.collapse(true);
            sel.removeAllRanges();
            sel.addRange(newRange_2);
            return;
        }
        // No adjacent same-color span — create a fresh span with the run.
        var span = document.createElement('span');
        span.style.color = pickerColor;
        span.textContent = insertedText;
        if (remainingNode) {
            parent.insertBefore(span, remainingNode);
        }
        else if (textNode.nextSibling) {
            parent.insertBefore(span, textNode.nextSibling);
        }
        else {
            parent.appendChild(span);
        }
        var newRange = document.createRange();
        newRange.setStart(span.firstChild, insertedText.length);
        newRange.collapse(true);
        sel.removeAllRanges();
        sel.addRange(newRange);
    };
    var onChangeHandler = function (e) {
        var _a, _b, _c;
        // iOS 26: revert iOS's per-input document + container scroll before paint.
        if (needsIOSScrollLock()) {
            holdWindowScrollBriefly();
            holdElScrollBriefly((_a = editorRef.current) === null || _a === void 0 ? void 0 : _a.closest('.customScrollbarContent'));
        }
        // (window as any).__jumpSnap?.('type-input', editorRef.current, `${idPreffix}[${index}]`);
        // Ignore input events that originated inside a math-field — MathLive's
        // insert() dispatches InputEvents that bubble here, but they must not
        // trigger enforceCaretColor or any DOM manipulation on the editorDiv.
        var ne = e.nativeEvent;
        if (ne === null || ne === void 0 ? void 0 : ne.composedPath().some(function (el) { return (el === null || el === void 0 ? void 0 : el.tagName) === 'MATH-FIELD'; }))
            return;
        // Defense-in-depth: force the just-typed chars into a picker-colored
        // span before doing anything else. See enforceCaretColor doc above.
        enforceCaretColor(e);
        // Reconcile mathFieldInstancesRef with DOM — remove refs for
        // math-fields that were deleted by the browser's native editing
        // (e.g. selecting across a mathfield and pressing Backspace).
        var removedMfs = [];
        mathFieldInstancesRef.current.forEach(function (mf) {
            if (!mf.isConnected) {
                removedMfs.push(mf);
            }
        });
        if (removedMfs.length > 0) {
            removedMfs.forEach(function (mf) {
                mathFieldInstancesRef.current.delete(mf);
                if (lastFocusedMathField.current === mf) {
                    lastFocusedMathField.current = null;
                }
            });
            batchDisposeMathfields(removedMfs);
            // Hide keyboard and sync data if no math fields remain
            var mfPresent_1 = ((_b = editorRef.current) === null || _b === void 0 ? void 0 : _b.querySelector('math-field')) !== null;
            if (!mfPresent_1) {
                setmathKeyboardVisible(false);
                setActiveMathText && setActiveMathText(-1);
            }
            if (onChangeSaveTimerRef.current)
                clearTimeout(onChangeSaveTimerRef.current);
            onChangeSaveTimerRef.current = setTimeout(function () { return getMathData(true); }, 500);
            return;
        }
        var mfPresent = ((_c = editorRef.current) === null || _c === void 0 ? void 0 : _c.querySelector('math-field')) !== null;
        if (mathKeyboardVisible || mfPresent)
            return;
        if (onChangeSaveTimerRef.current)
            clearTimeout(onChangeSaveTimerRef.current);
        onChangeSaveTimerRef.current = setTimeout(function () { return getMathData(true); }, 500);
    };
    // Auto-resize: Expand height when content overflows and keep within bounds
    useEffect(function () {
        var editor = editorRef.current;
        if (!editor || !isTextUpdate || typeof index !== 'number')
            return;
        var resizeTimeoutId = null;
        var checkAndResize = function () {
            // Clear any pending resize
            if (resizeTimeoutId) {
                clearTimeout(resizeTimeoutId);
            }
            // Debounce to prevent rapid updates
            resizeTimeoutId = setTimeout(function () {
                var _a, _b, _c, _d, _e, _f, _g;
                var scrollHeight = editor.scrollHeight;
                var clientHeight = editor.clientHeight;
                var currentHeight = ((_a = data === null || data === void 0 ? void 0 : data.data) === null || _a === void 0 ? void 0 : _a.height) || 300;
                var currentPosX = ((_c = (_b = data === null || data === void 0 ? void 0 : data.data) === null || _b === void 0 ? void 0 : _b.position) === null || _c === void 0 ? void 0 : _c.x) || 0;
                var currentPosY = ((_e = (_d = data === null || data === void 0 ? void 0 : data.data) === null || _d === void 0 ? void 0 : _d.position) === null || _e === void 0 ? void 0 : _e.y) || 0;
                var currentPosZ = ((_g = (_f = data === null || data === void 0 ? void 0 : data.data) === null || _f === void 0 ? void 0 : _f.position) === null || _g === void 0 ? void 0 : _g.z) || 0;
                // Only expand if content is significantly overflowing
                // Use a threshold of 10px to avoid infinite resize loops
                var threshold = 10;
                var overflow = scrollHeight - clientHeight;
                var needsUpdate = false;
                var newHeight = currentHeight;
                var newPosY = currentPosY;
                // Only resize if overflow is significant (more than threshold)
                if (overflow > threshold) {
                    newHeight = currentHeight + overflow + 20; // Add 20px padding
                    needsUpdate = true;
                    // Check if expanded box goes out of whiteboard bounds
                    var whiteboardHeight = canvasHeight || 800;
                    // Calculate box boundaries (box is centered at position)
                    var halfHeight = newHeight / 2;
                    var boxTop = currentPosY - halfHeight;
                    var boxBottom = currentPosY + halfHeight;
                    // Adjust position if going out of bounds
                    var margin = 20;
                    // Check bottom boundary
                    if (boxBottom > whiteboardHeight - margin) {
                        newPosY = whiteboardHeight - margin - halfHeight;
                    }
                    // Check top boundary
                    if (boxTop < margin) {
                        newPosY = margin + halfHeight;
                    }
                }
                // Update dimensions and position if needed
                if (needsUpdate) {
                    setText(function (prev) {
                        var _a;
                        var newText = __spreadArray([], prev, true);
                        if (!newText[index] || !newText[index].data)
                            return prev;
                        // Double-check we're actually changing something to avoid infinite loops
                        var oldHeight = newText[index].data.height || 300;
                        var oldPosY = ((_a = newText[index].data.position) === null || _a === void 0 ? void 0 : _a.y) || 0;
                        if (Math.abs(oldHeight - newHeight) < 1 && Math.abs(oldPosY - newPosY) < 1) {
                            return prev; // No significant change, don't update
                        }
                        newText[index] = __assign(__assign({}, newText[index]), { data: __assign(__assign({}, newText[index].data), { height: newHeight, position: {
                                    x: currentPosX,
                                    y: newPosY,
                                    z: currentPosZ,
                                } }) });
                        return newText;
                    });
                }
                resizeTimeoutId = null;
            }, 100); // 100ms debounce
        };
        // Only check on input events, not on every resize
        var handleInput = function () {
            checkAndResize();
        };
        editor.addEventListener('input', handleInput);
        return function () {
            if (resizeTimeoutId) {
                clearTimeout(resizeTimeoutId);
            }
            editor.removeEventListener('input', handleInput);
        };
    }, [(_f = data === null || data === void 0 ? void 0 : data.data) === null || _f === void 0 ? void 0 : _f.height, (_g = data === null || data === void 0 ? void 0 : data.data) === null || _g === void 0 ? void 0 : _g.position, isTextUpdate, index, canvasHeight, setText]);
    // Accessible name for a keypad key. Prefers the curated metadata, then falls
    // back to translating whatever the config supplied — `ariaLabel` if present,
    // otherwise `title`, which for the bottomBtns keys is itself an i18n key.
    var keypadKeyLabel = function (item) {
        var _a;
        var labelKey = (_a = KEYPAD_KEY_META[item.id]) === null || _a === void 0 ? void 0 : _a.labelKey;
        if (labelKey)
            return t(labelKey);
        var raw = item.ariaLabel || item.title;
        return raw ? t(raw) : '';
    };
    // The single tabbable key. Defaults to the first key in DOM order (the left
    // half sweeps first, so that is the '1' key) whenever the remembered key is
    // absent from the current keypad configuration — different consumers pass
    // different `keypadButtons` sets.
    var keypadOrderedIds = useMemo(function () {
        var grid = __spreadArray([], (keypadButtons !== null && keypadButtons !== void 0 ? keypadButtons : []), true).map(function (item, i) { return ({ item: item, i: i }); })
            .sort(function (a, b) {
            var sideA = a.i % 10 < 5 ? 0 : 1;
            var sideB = b.i % 10 < 5 ? 0 : 1;
            if (sideA !== sideB)
                return sideA - sideB;
            return a.i - b.i;
        })
            .map(function (_a) {
            var item = _a.item;
            return item.id;
        });
        var bottom = (bottomBtns !== null && bottomBtns !== void 0 ? bottomBtns : []).filter(function (b) { return b.id !== 'emptyBtn'; }).map(function (b) { return b.id; });
        return __spreadArray(__spreadArray([], grid, true), bottom, true);
    }, [keypadButtons, bottomBtns]);
    var effectiveRovingKeyId = rovingKeyId && keypadOrderedIds.includes(rovingKeyId) ? rovingKeyId : keypadOrderedIds[0];
    var createMathKeyboard = function () {
        var keypadEl = (_jsxs("div", { ref: keypadWrapperRef, className: "keypadWrapper", 
            // A keypad is a toolbar, not a group: one stop in the Tab order, with
            // the arrow keys moving between keys (WAI-ARIA toolbar pattern, and
            // what docs/accessibility/Matheditor.md § 4 already specified). The old
            // role="group" + tabindex="0"-on-every-key shape promised that Tab
            // walked the keys, and it did not: forward Tab left the keypad from the
            // very first key, so `Digit 2` was unreachable and a keyboard-only user
            // could enter exactly one character (WCAG 2.1.1, Level A).
            role: "toolbar", "aria-orientation": "horizontal", "aria-label": t('text.mathKeypad'), onMouseDown: function (e) { return e.preventDefault(); }, onKeyDown: function (e) {
                if (e.key === 'Escape') {
                    e.stopPropagation();
                    setmathKeyboardVisible(false);
                    setActiveMathText && setActiveMathText(-1);
                    announce(t('text.mathEditor.keypadClosed'));
                    setTimeout(function () {
                        var _a;
                        (_a = keypadTriggerRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                        keypadTriggerRef.current = null;
                    }, 0);
                    return;
                }
                // Tab is deliberately NOT handled: a toolbar must not trap focus
                // (WCAG 2.1.2, and docs/accessibility/Matheditor.md § 8 "Tab always
                // exits"). Roving tabindex means the browser's own Tab walk leaves
                // the keypad in one step, in both directions.
                var NAV = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
                if (!NAV.includes(e.key))
                    return;
                var btns = keypadKeyButtons(keypadWrapperRef.current);
                if (btns.length === 0)
                    return;
                var current = btns.indexOf(document.activeElement);
                if (current === -1)
                    return;
                e.preventDefault();
                e.stopPropagation();
                var next = current;
                switch (e.key) {
                    case 'ArrowRight':
                        next = (current + 1) % btns.length;
                        break;
                    case 'ArrowLeft':
                        next = (current - 1 + btns.length) % btns.length;
                        break;
                    case 'ArrowDown':
                        next = Math.min(current + KEYPAD_HALF_COLS, btns.length - 1);
                        break;
                    case 'ArrowUp':
                        next = Math.max(current - KEYPAD_HALF_COLS, 0);
                        break;
                    case 'Home':
                        next = 0;
                        break;
                    case 'End':
                        next = btns.length - 1;
                        break;
                }
                var target = btns[next];
                if (!target)
                    return;
                // Move the single tabbable key with the focus, so Shift+Tab back into
                // the keypad returns to where the user was.
                setRovingKeyId(target.getAttribute('data-id') || null);
                target.focus();
            }, style: {
                position: 'absolute',
                top: keypadPos.top,
                left: keypadPos.left,
                zIndex: 10000,
            }, children: [_jsx("div", { className: "keypadGrid", role: "group", "aria-label": t('text.mathEditor.mathKeys'), children: (function () {
                        // Key metadata is module-level (KEYPAD_KEY_META) so the bottomBtns
                        // strip below resolves its labels the same way — see the comment on
                        // that constant for why space/backspace used to leak raw i18n keys.
                        var items = keypadButtons !== null && keypadButtons !== void 0 ? keypadButtons : [];
                        // DOM order: the keypad is visually split into a left half
                        // (cols 1-5, the number pad) and a right half (cols 6-10, the
                        // operators / structures). Arrow navigation sweeps the left half
                        // top-to-bottom first, then the right half top-to-bottom — so the
                        // focus ring stays on one side instead of zig-zagging across
                        // the visual gap. Grid position is unaffected; gridRow /
                        // gridColumn are derived from visualIndex below.
                        var indexed = items.map(function (item, i) { return ({ item: item, visualIndex: i }); });
                        indexed.sort(function (a, b) {
                            var sideA = a.visualIndex % 10 < 5 ? 0 : 1;
                            var sideB = b.visualIndex % 10 < 5 ? 0 : 1;
                            if (sideA !== sideB)
                                return sideA - sideB;
                            return a.visualIndex - b.visualIndex;
                        });
                        return indexed.map(function (_a) {
                            var item = _a.item, visualIndex = _a.visualIndex;
                            var meta = KEYPAD_KEY_META[item.id];
                            var isOnLeft = visualIndex % 10 < 5;
                            // Grid position: row = floor(idx/10)+1, col = (idx%10)+1
                            var gridRow = Math.floor(visualIndex / 10) + 1;
                            var gridColumn = (visualIndex % 10) + 1;
                            return (_jsx(ButtonComponent, { imgSrc: item.imgSrc, normalCSS: "keypadBtn", defaultCSS: "keypadBtn ".concat((item === null || item === void 0 ? void 0 : item.height100) ? 'height100' : '', " ").concat(isOnLeft ? 'keypadLeft' : 'keypadRight'), ariaLabel: keypadKeyLabel(item), ariaKeyShortcuts: meta === null || meta === void 0 ? void 0 : meta.shortcut, clickActive: item === null || item === void 0 ? void 0 : item.activeState, text: item.title, title: keypadKeyLabel(item), direction: "bottom", isParentRelative: false, isWhiteBoard: true, buttonStyle: { gridRow: gridRow, gridColumn: gridColumn }, dataId: item.id, tabIndex: item.id === effectiveRovingKeyId ? 0 : -1, clickHandler: function (e) {
                                    if (e.detail !== 0)
                                        return;
                                    var btn = e.currentTarget;
                                    setRovingKeyId(item.id);
                                    keypadBtnClickHandler(item);
                                    setTimeout(function () { return btn === null || btn === void 0 ? void 0 : btn.focus(); }, 50);
                                }, onMouseDown: function (e) {
                                    e.preventDefault();
                                    if (Date.now() - keypadTouchRef.current < 500)
                                        return;
                                    keypadBtnClickHandler(item);
                                }, onTouchStart: function (e) {
                                    e.preventDefault();
                                    keypadTouchRef.current = Date.now();
                                    keypadBtnClickHandler(item);
                                } }, item.id));
                        });
                    })() }), _jsx("div", { className: "bottomBtns", children: bottomBtns === null || bottomBtns === void 0 ? void 0 : bottomBtns.map(function (item, idx) {
                        var _a;
                        return item.id === 'emptyBtn' ? (
                        // Non-interactive grid spacer. `inert` strips it from focus
                        // order, the a11y tree, and pointer events — stronger than
                        // tabIndex=-1 alone (which only excludes from sequential tab).
                        _jsx("div", { inert: true, "aria-hidden": "true", className: "keypadBtn bottomBtns keyBtn_".concat(idx + 1) }, item.id)) : (_jsx(ButtonComponent, { defaultCSS: "keypadBtn bottomBtns keyBtn_".concat(idx + 1), clickHandler: function (e) {
                                if (e.detail !== 0)
                                    return;
                                var btn = e.currentTarget;
                                setRovingKeyId(item.id);
                                keypadBtnClickHandler(item);
                                setTimeout(function () { return btn === null || btn === void 0 ? void 0 : btn.focus(); }, 50);
                            }, onMouseDown: function (e) {
                                e.preventDefault();
                                if (Date.now() - keypadTouchRef.current < 500)
                                    return;
                                keypadBtnClickHandler(item);
                            }, onTouchStart: function (e) {
                                e.preventDefault();
                                keypadTouchRef.current = Date.now();
                                keypadBtnClickHandler(item);
                            }, imgSrc: item.imgSrc, normalCSS: "keypadBtn", 
                            // Resolved through the shared metadata (with a t() fallback on
                            // the raw config value) — btnData.json gives space/backspace no
                            // ariaLabel at all, so ButtonComponent used `text` and
                            // announced the literal i18n key "text.spaceBar".
                            ariaLabel: keypadKeyLabel(item), ariaKeyShortcuts: (_a = KEYPAD_KEY_META[item.id]) === null || _a === void 0 ? void 0 : _a.shortcut, clickActive: item === null || item === void 0 ? void 0 : item.activeState, text: item.title, title: keypadKeyLabel(item), direction: "bottom", isParentRelative: false, isWhiteBoard: true, dataId: item.id, tabIndex: item.id === effectiveRovingKeyId ? 0 : -1 }, item.id));
                    }) })] }));
        // For whiteboard editors, render directly (no portal) so the keyboard
        // stays anchored to the text box and scrolls with the canvas.
        // For all other editors, portal to body to escape parent transforms/overflow.
        if (isTextUpdate) {
            return keypadEl;
        }
        return createPortal(
        // data-manipulative-id mirrors the editor's own wrapper so a consumer that
        // positions the portaled keypad (TableManipulativeComp) can find *its*
        // keypad instead of whichever one happens to be mounted on body.
        _jsx("div", { className: "mathEditorParent", "data-manipulative-id": "".concat(idPreffix || 'text', "_").concat(index), style: { display: 'contents' }, children: keypadEl }), document.body);
    };
    useImperativeHandle(ref, function () { return ({
        insertMath: function (opts) {
            // When called from a consumer whose math button is itself a
            // keyboard tab stop (JumpBar / CustomTick / FractionTile), arm
            // the post-open focus shift so the keypad's first button gets
            // focus as soon as the keypad mounts. The mathKeyboardVisible
            // effect reads focusKeypadOnOpenRef and moves focus there.
            if (opts === null || opts === void 0 ? void 0 : opts.focusKeypadOnOpen) {
                focusKeypadOnOpenRef.current = true;
            }
            insertMath();
        },
        focus: function () {
            var editor = editorRef.current;
            if (!editor)
                return;
            editor.focus({ preventScroll: true });
            // On iPad, re-focusing a contentEditable places the cursor at the
            // start. Move it to the end so insertMath() appends at the right spot.
            var sel = window.getSelection();
            if (sel && (!sel.rangeCount || !editor.contains(sel.focusNode) || sel.focusOffset === 0)) {
                var range = document.createRange();
                range.selectNodeContents(editor);
                range.collapse(false);
                sel.removeAllRanges();
                sel.addRange(range);
            }
            focusHandler(new MouseEvent('click'));
        },
        getTotalCharacterCount: function (ignoreSpaces) {
            if (ignoreSpaces === void 0) { ignoreSpaces = false; }
            var count = getTotalCharacterCount(ignoreSpaces);
            return count;
        },
        mathFieldInstancesCount: function () {
            var array = Array.from(mathFieldInstancesRef.current);
            return array.length;
        },
        getNumericValue: function () {
            var editor = editorRef.current;
            if (!editor)
                return { isValid: false, value: null };
            // Walk the editor DOM, collecting text from text nodes and LaTeX from
            // math-field elements so split content like "2" + \frac{5}{3} is unified.
            var combined = '';
            var walk = function (node) {
                var _a;
                if (node.nodeType === Node.TEXT_NODE) {
                    combined += (_a = node.textContent) !== null && _a !== void 0 ? _a : '';
                }
                else if (node.nodeName === 'MATH-FIELD') {
                    combined += node.getValue();
                }
                else {
                    node.childNodes.forEach(walk);
                }
            };
            editor.childNodes.forEach(walk);
            console.log(parseNumericLatex(combined), 'parseNumericLatex(combined)');
            return parseNumericLatex(combined);
        },
        getContent: function () { return calcMathData(); },
        closeKeyboard: function () {
            // Synchronously clear the ref so the data-sync guard (line 2518) is
            // bypassed on the very next render — needed when a resize touchend
            // fires before React has processed the async setmathKeyboardVisible(false)
            // triggered by the isFocused→false transition.
            mathKeyboardVisibleRef.current = false;
            setmathKeyboardVisible(false);
        },
        // Re-open the keypad on the expression the caret is already in. Used by
        // the table toolbar's "Math Keypad" button: once the keypad has been
        // dismissed, pressing it used to always insertMath(), adding a second
        // empty math-field instead of bringing the keypad back to the fraction
        // the user was editing. lastFocusedMathField is cleared whenever the
        // editorDiv itself takes focus (caret in plain text), so it doubles as
        // "the caret is inside an expression" — when it isn't set we return
        // false and the caller falls back to inserting a new expression.
        openKeypad: function () {
            if (viewOnlyRef.current)
                return false;
            // Already open — the 'mathtext' bottomPanel listener above has handled
            // this press, so report "nothing left to do" and stop the caller from
            // inserting a duplicate field on top of it.
            if (mathKeyboardVisibleRef.current)
                return true;
            return reopenKeypadAtCaret();
        },
    }); }, [mlReady]);
    return (_jsx(_Fragment, { children: _jsxs("div", { className: "mathEditorParent ".concat(className), role: suppressOuterGroup ? 'none' : 'group', "aria-hidden": outerAriaHidden || undefined, "aria-label": suppressOuterGroup ? undefined : t('text.mathEditor.rootLabel'), "aria-describedby": "matheditor-hint-".concat(idPreffix, "-").concat(index !== null && index !== void 0 ? index : 0), "data-manipulative-id": "".concat(idPreffix || 'text', "_").concat(index), style: __assign(__assign({ position: !isTextUpdate ? 'unset' : 'absolute', top: ((_j = (_h = data === null || data === void 0 ? void 0 : data.data) === null || _h === void 0 ? void 0 : _h.position) === null || _j === void 0 ? void 0 : _j.y) || 0, left: ((_l = (_k = data === null || data === void 0 ? void 0 : data.data) === null || _k === void 0 ? void 0 : _k.position) === null || _l === void 0 ? void 0 : _l.x) || 0, transform: disableTransform
                    ? undefined
                    : isTextUpdate && (data === null || data === void 0 ? void 0 : data.type) !== 'equation'
                        ? "translate(-50%, -50%)"
                        : undefined, transformOrigin: disableTransform ? undefined : 'center center', width: !isTextUpdate
                    ? '100%'
                    : (data === null || data === void 0 ? void 0 : data.type) === 'equation'
                        ? 'auto'
                        : ((_m = data === null || data === void 0 ? void 0 : data.data) === null || _m === void 0 ? void 0 : _m.lineWidth) || 300, height: !isTextUpdate
                    ? '100%'
                    : (data === null || data === void 0 ? void 0 : data.type) === 'equation'
                        ? 'auto'
                        : ((_o = data === null || data === void 0 ? void 0 : data.data) === null || _o === void 0 ? void 0 : _o.height) || 300 }, (isFocused ? { zIndex: 1001 } : {})), style), onClick: focusHandler, children: [_jsx("span", { id: "matheditor-hint-".concat(idPreffix, "-").concat(index !== null && index !== void 0 ? index : 0), className: "popupSrOnly", "aria-hidden": suppressOuterGroup ? true : undefined, children: t('text.mathEditor.rootHint') }), _jsx("span", { ref: fracPositionLiveRef, "aria-live": "polite", "aria-atomic": "true", className: "popupSrOnly" }), _jsx("div", { id: "editor_".concat(idPreffix, "_").concat(index !== null && index !== void 0 ? index : 0), ref: editorRef, contentEditable: !viewonly, tabIndex: viewonly ? 0 : undefined, spellCheck: isManipulative ? false : undefined, autoCorrect: isManipulative ? 'off' : undefined, autoCapitalize: isManipulative ? 'off' : undefined, 
                    // Math-keypad-only editors (stickyKeypadAfterInsertOnTouch consumers:
                    // JumpBar / CustomTick / fraction-tile labels) are filled via the
                    // custom on-screen keypad, never the native keyboard. Suppress the
                    // native keyboard so iOS doesn't open it (and scroll the document to
                    // fit it) during keypad churn. Static attribute only — see the sink
                    // note above re: NOT re-enforcing inputmode (causes iOS tap-select).
                    inputMode: stickyKeypadAfterInsertOnTouch ? 'none' : undefined, role: "textbox", "aria-label": editorAriaLabel || undefined, "aria-multiline": "true", "aria-keyshortcuts": !viewonly ? 'Alt+M Alt+K' : undefined, "aria-placeholder": dataPlaceholder ? dataPlaceholder : viewonly ? undefined : t('text.mathEditor.typeHere'), className: "editorDiv ".concat(isFocused && 'focused', " ").concat(viewonly && 'editorDivDisable'), "data-placeholder": dataPlaceholder ? dataPlaceholder : viewonly ? undefined : 'Type here...', onTouchStart: function (e) {
                        // iPad: stop parent canvas/whiteboard touch handlers (pan, drag)
                        // from stealing the long-press gesture needed for text selection.
                        if (isFocusedRef.current && !viewOnlyRef.current) {
                            e.stopPropagation();
                        }
                        // Long-press fallback: if native selection doesn't fire within
                        // 500ms, programmatically select the word under the touch point.
                        //
                        // NEVER arm it for a touch inside a <math-field>. MathLive owns the
                        // caret/selection there and the fallback has no notion of atoms: a
                        // math-field is opaque to caretRangeFromPoint, so the range resolves
                        // to the editorDiv and sel.modify('extend', 'forward', 'word')
                        // expands over the WHOLE element — on iPad, pressing a fraction's
                        // denominator for >500ms highlighted the entire editor box, and the
                        // resulting focus move out of the math-field made the non-sticky
                        // internal-blur branch (see the mf 'blur' listener) close the keypad
                        // mid-edit. The math-field stops pointerdown/mousedown/click
                        // propagation but NOT touchstart, so this guard is the only thing
                        // keeping the fallback off the math context.
                        if (shouldArmLongPressWordSelect({
                            isFocused: isFocusedRef.current,
                            viewOnly: viewOnlyRef.current,
                            isTouchDevice: isTouchDevice,
                            target: e.target,
                        })) {
                            var touch = e.touches[0];
                            longPressTouchPosRef.current = { x: touch.clientX, y: touch.clientY };
                            if (longPressTouchRef.current)
                                clearTimeout(longPressTouchRef.current);
                            longPressTouchRef.current = setTimeout(function () {
                                var _a, _b;
                                var pos = longPressTouchPosRef.current;
                                if (!pos)
                                    return;
                                // Re-check the point itself, not focus: layout can shift between
                                // touchstart and this timer (a keypad insert widens the box), so
                                // the press may now be over a math-field even though touchstart
                                // wasn't. Deliberately NOT `document.activeElement === MATH-FIELD`
                                // — in a mixed text+math editor a math-field legitimately holds
                                // focus while the keypad is open, and long-pressing plain text in
                                // that state must still select the word.
                                if (isPointOverMathField(pos.x, pos.y))
                                    return;
                                var sel = window.getSelection();
                                // Only intervene if there's no active selection (native didn't fire)
                                if (sel && sel.isCollapsed) {
                                    // caretRangeFromPoint is WebKit/Blink (covers Safari & Chrome)
                                    var range = (_a = document.caretRangeFromPoint) === null || _a === void 0 ? void 0 : _a.call(document, pos.x, pos.y);
                                    if (range && ((_b = editorRef.current) === null || _b === void 0 ? void 0 : _b.contains(range.startContainer))) {
                                        // Expand range to select the word at the touch point
                                        sel.removeAllRanges();
                                        sel.addRange(range);
                                        sel.modify('move', 'backward', 'word');
                                        sel.modify('extend', 'forward', 'word');
                                    }
                                }
                                longPressTouchRef.current = null;
                                longPressTouchPosRef.current = null;
                            }, 500);
                        }
                    }, onTouchMove: function () {
                        // Cancel long-press if the user moves their finger (scrolling/dragging)
                        if (longPressTouchRef.current) {
                            clearTimeout(longPressTouchRef.current);
                            longPressTouchRef.current = null;
                            longPressTouchPosRef.current = null;
                        }
                    }, onTouchEnd: function () {
                        // Cancel long-press timer on touch end (tap, not long-press)
                        if (longPressTouchRef.current) {
                            clearTimeout(longPressTouchRef.current);
                            longPressTouchRef.current = null;
                            longPressTouchPosRef.current = null;
                        }
                    }, onPointerDown: function (e) {
                        var _a, _b;
                        // DO NOT REMOVE — Firefox first-click fix for text-only editors.
                        // See docs/components/MathEditor.md "Firefox First-Click Fix" section.
                        //
                        // Firefox: contenteditable inside a popup/dialog may not receive
                        // keyboard access from the browser's default mousedown behaviour.
                        // MathLive's <math-field> works around this by calling focus() in
                        // its own pointerdown handler — we do the same for the plain
                        // contenteditable.  If the element was already focused
                        // programmatically (e.g. DiceManipulativePopup auto-focus on
                        // mount), blur first so the subsequent focus() is a *fresh*
                        // operation rather than a no-op on an already-focused element.
                        // The browser's default mousedown (which fires after pointerdown)
                        // still places the caret at the click position.
                        if (viewOnlyRef.current)
                            return;
                        // Math-field pointerdown calls stopPropagation, so we never reach
                        // here for math-field clicks.  Guard anyway for safety.
                        if ((_b = (_a = e.target).closest) === null || _b === void 0 ? void 0 : _b.call(_a, 'math-field'))
                            return;
                        var editor = editorRef.current;
                        if (!editor || editor.querySelector('math-field'))
                            return;
                        if (document.activeElement === editor) {
                            editor.blur();
                        }
                        editor.focus({ preventScroll: true });
                        if (isTouchDevice && e.pointerType === 'touch') {
                            onTouchFocusSelf === null || onTouchFocusSelf === void 0 ? void 0 : onTouchFocusSelf();
                        }
                    }, onFocus: function (e) {
                        // Clear lastFocusedMathField when user focuses the editorDiv itself
                        // (i.e. clicks on regular text, not a math-field inside it).
                        // This ensures exec/execFontSize take the text formatting path.
                        if (e.target === editorRef.current) {
                            lastFocusedMathField.current = null;
                            // Announce for iPad VO which doesn't always read role="textbox"
                            if (!viewOnlyRef.current) {
                                announce('Text field. Type normally.');
                            }
                        }
                        // Keyboard-focus path: show the options popover instead of
                        // dropping the caret straight into contentEditable. Mouse/touch
                        // clicks already flipped editingAllowedRef in the global listener.
                        if (e.target === editorRef.current &&
                            focusFromKeyboardRef.current &&
                            (viewOnlyRef.current || !editingAllowedRef.current)) {
                            focusFromKeyboardRef.current = false;
                            // Select the item first so isFocused becomes true and the
                            // popover renders. The popover auto-focus effect will move
                            // focus to the first button; no manual blur needed.
                            focusHandler(e);
                            return;
                        }
                        focusHandler(e);
                        // iPad/mobile: scroll focused input into view above on-screen keyboard.
                        // Use both a timeout (for when keyboard is already visible) and a
                        // visualViewport resize listener (for when the keyboard is animating in,
                        // where 300ms may not be enough to catch the final viewport height).
                        if (scrollIntoViewOnFocus && !isColorChangeFocusRef.current) {
                            // Returns false when the insert guard suppressed it, so a
                            // one-shot caller (the visualViewport listener below) can keep
                            // waiting instead of burning itself on a no-op.
                            var scrollIfNeeded_1 = function () {
                                var _a, _b;
                                var el = editorRef.current;
                                if (!el)
                                    return true;
                                // A keypad insert just ran on THIS editor, which arms a
                                // window-scroll hold and restores the position 500ms later.
                                // Scrolling now only fights that. One check covers both
                                // callers — the 300ms timeout and the visualViewport resize.
                                // Note this is armed by EVERY
                                // keypad key (the generic `mf.insert(insertValue)` branch),
                                // not only the structural frac/power/placeholder ones.
                                if (Date.now() - insertScrollGuardRef.current < INSERT_SCROLL_GUARD_MS)
                                    return false;
                                var rect = el.getBoundingClientRect();
                                var visibleHeight = (_b = (_a = window.visualViewport) === null || _a === void 0 ? void 0 : _a.height) !== null && _b !== void 0 ? _b : window.innerHeight;
                                if (rect.top < 0 || rect.bottom > visibleHeight) {
                                    // ⚠️ Keep 'smooth'. It looks like a cosmetic choice and is
                                    // not: installWinLock reverts ANY window-scroll delta while
                                    // a math-field is focused and — unlike installElLock — has
                                    // no escape hatch, only a 450ms debounced teardown. A smooth
                                    // scroll emits deltas across many frames, so its tail still
                                    // lands after that debounce expires. An instant scroll is a
                                    // single delta: land it inside the window and winLock
                                    // reverts it outright, leaving 0px of movement — which is
                                    // exactly the MLC#253 symptom (tapping a second table cell
                                    // did not bring it into view). Measured trade, not a guess.
                                    window.scrollTo({
                                        top: rect.top + window.scrollY - visibleHeight / 3,
                                        behavior: 'smooth',
                                    });
                                }
                                return true;
                            };
                            // Defer, never drop. Both callers below are one-shot, so a call
                            // the insert guard suppresses would otherwise be lost outright —
                            // and the insert is exactly what makes the correction necessary:
                            // a fraction makes the row TALLER, so a cell that was clear of
                            // the on-screen keyboard before the insert can be under it
                            // after. `scrollIfNeeded` only ever runs on focus, never on
                            // layout change, so nothing else would bring it back. Measured
                            // at 15px past the keyboard line for a single-line fraction.
                            // Re-running once the guard lapses is safe: preserveScroll's
                            // 500ms restore has already fired by then, so there is no hold
                            // left to fight.
                            var scrollIfNeededOrRetry_1 = function () {
                                if (scrollIfNeeded_1())
                                    return;
                                var remaining = INSERT_SCROLL_GUARD_MS - (Date.now() - insertScrollGuardRef.current);
                                setTimeout(scrollIfNeeded_1, Math.max(0, remaining) + INSERT_SCROLL_RETRY_MARGIN_MS);
                            };
                            // Handle case where keyboard is already visible (e.g. switching cells)
                            setTimeout(scrollIfNeededOrRetry_1, 300);
                            // Handle case where keyboard is appearing — scroll once viewport shrinks
                            if (window.visualViewport) {
                                var vv_1 = window.visualViewport;
                                var onResize_1 = function () {
                                    scrollIfNeededOrRetry_1();
                                    vv_1.removeEventListener('resize', onResize_1);
                                };
                                vv_1.addEventListener('resize', onResize_1);
                                // Clean up if resize never fires (keyboard was already up)
                                setTimeout(function () { return vv_1.removeEventListener('resize', onResize_1); }, 1500);
                            }
                        }
                    }, onBlur: function (e) {
                        var _a, _b;
                        var relatedTarget = e.relatedTarget;
                        // If focus moved to a math-field inside this editor, let its own
                        // focus handler manage keyboard visibility — don't interfere.
                        if ((relatedTarget === null || relatedTarget === void 0 ? void 0 : relatedTarget.nodeName) === 'MATH-FIELD')
                            return;
                        // If focus moved inside the keypad, keep keyboard visible.
                        if (keypadWrapperRef.current &&
                            relatedTarget &&
                            keypadWrapperRef.current.contains(relatedTarget))
                            return;
                        // Clear link popup unless focus moved to the link popup itself.
                        // This must happen BEFORE the toolbar early-returns below so that
                        // tapping a toolbar button (e.g. the new-text-box icon) while the
                        // link popup is visible still dismisses the stale popup — otherwise
                        // it lingers and re-attaches to the next textbox via popupPos.
                        var isLinkPopup = relatedTarget instanceof HTMLElement && relatedTarget.closest('.linkPopup');
                        if (!isLinkPopup) {
                            setActiveLink(null);
                        }
                        // If focus moved to a toolbar button (e.g. color picker), keep
                        // the math keyboard state so the re-render doesn't collapse
                        // open toolbars like the color palette.
                        if (relatedTarget instanceof HTMLElement) {
                            if (relatedTarget.closest('.toolBottomPanelContainer') ||
                                relatedTarget.closest('.bottomPanelToolContainer') ||
                                relatedTarget.closest('#rightSideDrawerContainer') ||
                                relatedTarget.closest('.sidebar') ||
                                relatedTarget.closest('.footer') ||
                                relatedTarget.closest('.collapsibleRight') ||
                                relatedTarget.closest('.collapsibleOptionHolder')) {
                                return;
                            }
                        }
                        // Focus left the editor — hide the math keyboard.
                        // iPad: tapping a keypad button can fire a spurious focusout
                        // on the math-field that bubbles through here, even though
                        // preventDefault on the keypad button's touchstart keeps focus
                        // visually on the mf. Without this guard the keypad closes
                        // after every keystroke (the user reproduces this in fraction
                        // tiles). keypadTouchRef is set on every keypad-button
                        // touchstart, so a fresh value here means the user is mid-tap
                        // and we should leave the keypad open.
                        //
                        // stickyKeypadAfterInsertOnTouch consumers (JumpBar /
                        // CustomTick / fraction-tile labels) opt into "never auto-
                        // close on focusout" — every keypad press causes some
                        // platform-level focus shuffle (preventDefault on the button
                        // doesn't always block it on iPad, and MathLive's internal
                        // cursor moves do similar on desktop). The previous time-
                        // gated guard (recent keypadTouchRef + insertMathGuardRef)
                        // lapsed after ~500 ms of thinking time, dropping the keypad
                        // mid-edit. For sticky consumers we let the dedicated
                        // isFocused cleanup effect at line 391 close the keypad when
                        // selectedTool no longer references the editor — i.e. only
                        // when the user genuinely exits the math context (clicks
                        // outside, deselects). Other consumers retain the old
                        // time-based guard.
                        // Sticky-keypad consumers (JumpBar / CustomTick / fraction-tile
                        // labels) are FULLY exempt from this focusout-driven close — the
                        // dedicated isFocused cleanup effect (see useEffect([isFocused]))
                        // closes the keypad only when selectedTool no longer references the
                        // editor, i.e. the user genuinely exits the math context. Closing
                        // here instead dropped the keypad after every keypad button press:
                        // the desktop mouse path (onMouseDown) preventDefaults but never
                        // refreshes keypadTouchRef, so the time-gate below is always stale
                        // on click and the keypad vanished between every keystroke on a
                        // tick-mark / jump-bar / fraction-tile label. Non-sticky editors
                        // keep the original time-based auto-close.
                        if (!stickyKeypadAfterInsertOnTouch &&
                            mathKeyboardVisibleRef.current &&
                            Date.now() - keypadTouchRef.current >= 500) {
                            // Defer one frame before closing. Clicking from one expression to
                            // another inside the same editor (a table cell / canvas text box
                            // holding two math-fields) fires this focusout with
                            // relatedTarget = null BEFORE the new field's focus handler runs,
                            // so a synchronous close unmounted the keypad for 3-5 frames — a
                            // visible flash mid-edit, and if focus ended up back on a field
                            // that never re-fires 'focus', nothing brought the keypad back at
                            // all. By the next frame document.activeElement tells the truth:
                            // still inside this editor (or in the keypad) means the user never
                            // left the math context, so keep the keypad up. Mirrors the
                            // deferred close already used in the math-field internal-blur
                            // branch above.
                            //
                            // The skip is deliberately narrow — a math-field in THIS editor, or
                            // the keypad. Focus landing on the editorDiv itself means the caret
                            // moved to the cell's plain text, which must still close the keypad
                            // and keep it closed (see docs/manipulatives/Table.md).
                            requestAnimationFrame(function () {
                                var _a;
                                if (!mathKeyboardVisibleRef.current)
                                    return;
                                var ed = editorRef.current;
                                // Torn down between the blur and this frame (row deleted, page
                                // switched, popup closed) — a rAF can be parked indefinitely in a
                                // hidden tab, so bail rather than clear state that now belongs to
                                // a different editor.
                                if (!(ed === null || ed === void 0 ? void 0 : ed.isConnected))
                                    return;
                                var active = document.activeElement;
                                var activeIsMf = (active === null || active === void 0 ? void 0 : active.nodeName) === 'MATH-FIELD';
                                if (active && activeIsMf && ed.contains(active))
                                    return;
                                if (active && ((_a = keypadWrapperRef.current) === null || _a === void 0 ? void 0 : _a.contains(active)))
                                    return;
                                // Clear the ref synchronously — getMathData bails on it, and a
                                // throwChangeOnBlur consumer's setTimeout(…, 100) save can land
                                // before React commits the state update.
                                mathKeyboardVisibleRef.current = false;
                                setmathKeyboardVisible(false);
                                // Focus moved into ANOTHER editor's math-field: its focus handler
                                // has already claimed the shared activeMathText (popup faces /
                                // card labels share one parent state), so clearing it here would
                                // undo that and leave the new field's keypad button reading
                                // aria-expanded="false".
                                if (!activeIsMf)
                                    setActiveMathText && setActiveMathText(-1);
                            });
                        }
                        // For text-only editors (no math-field), clear selection so .focused
                        // outline is removed. Math-field editors handle this in their own
                        // focusout handler via data-MFDeselect.
                        // When keepSelectedOnInternalBlur is true, skip deselection when
                        // focus merely moved to another element within the same manipulative
                        // or to a tool panel (e.g. clicking a fraction tile body to exit
                        // label editing — the tile should stay selected).
                        if (!isTextUpdate) {
                            var editor = editorRef.current;
                            var skipDeselect = false;
                            // Sticky-keypad consumers (JumpBar / CustomTick / fraction-tile
                            // labels) own the selection lifecycle via their own click-outside
                            // handlers. Clearing selectedTool here on iPad's spurious blurs
                            // (relatedTarget=null after a keypad tap on a still-empty label)
                            // drops isFocused → the keypad unmounts → user has to re-tap
                            // the label between every keystroke. Skip the clear unconditionally
                            // for these consumers; the parent widget closes us when the user
                            // actually taps outside.
                            if (stickyKeypadAfterInsertOnTouch) {
                                skipDeselect = true;
                            }
                            if (keepSelectedOnInternalBlur) {
                                // On touch devices, blur with null relatedTarget typically means
                                // the iPad keyboard was dismissed — not that the user tapped
                                // outside.  Actual "tap outside" deselection is handled by the
                                // tapped component's own event handlers (e.g. WhiteBoard sets
                                // selectedTool directly), so we can safely skip here to preserve
                                // the text selection.
                                if (!relatedTarget && isTouchDevice) {
                                    skipDeselect = true;
                                }
                                var sameManipulative = relatedTarget &&
                                    (((_a = editor === null || editor === void 0 ? void 0 : editor.closest('[data-manipulative-id]')) === null || _a === void 0 ? void 0 : _a.contains(relatedTarget)) ||
                                        ((_b = editor === null || editor === void 0 ? void 0 : editor.closest('.tableManipContainer')) === null || _b === void 0 ? void 0 : _b.contains(relatedTarget)));
                                var isToolPanel = relatedTarget instanceof HTMLElement &&
                                    !!(relatedTarget.closest('#rightSideToolPanel') ||
                                        relatedTarget.closest('.toolBottomPanelContainer') ||
                                        relatedTarget.closest('.bottomPanelToolContainer') ||
                                        relatedTarget.closest('.collapsibleOptionHolder') ||
                                        relatedTarget.closest('.manipulativePopup') ||
                                        relatedTarget.closest('.sidebar'));
                                skipDeselect = skipDeselect || !!(sameManipulative || isToolPanel);
                            }
                            if (editor && !editor.querySelector('math-field') && !skipDeselect) {
                                setSelectedTool([]);
                            }
                        }
                        // throwChangeOnBlur: save data on every blur (used by some callers)
                        if (throwChangeOnBlur) {
                            timeoutRef.current = setTimeout(function () {
                                timeoutRef.current = null;
                                getMathData();
                            }, 100);
                        }
                    }, onCopy: function (e) {
                        e.preventDefault();
                        var sel = window.getSelection();
                        var text = sel && sel.rangeCount > 0 ? sel.toString() : '';
                        if (text)
                            e.clipboardData.setData('text/plain', text);
                    }, onPaste: function (e) {
                        e.preventDefault();
                        var text = e.clipboardData.getData('text/plain');
                        if (!text)
                            return;
                        var lines = text.split(/\r\n|\r|\n/);
                        for (var i = 0; i < lines.length; i++) {
                            if (maxCharacters && getTotalCharacterCount() >= maxCharacters)
                                break;
                            if (i > 0) {
                                if (blockNewLine) {
                                    insertTextIntoEditor(' ');
                                }
                                else {
                                    insertLineBreak();
                                }
                            }
                            if (lines[i])
                                insertTextIntoEditor(lines[i]);
                        }
                        getMathData();
                    }, onCut: function (e) { return e.preventDefault(); }, onKeyDown: function (e) {
                        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
                        onKeyDown === null || onKeyDown === void 0 ? void 0 : onKeyDown(e);
                        // Events from inside an mf bubble up through the contenteditable
                        // — skip the navigation handlers below (Tab, arrows, mf-adjacent
                        // Backspace) so MathLive's own arrow/tab handling isn't
                        // double-intercepted. Other handlers below (Alt+M / Alt+K /
                        // Alt+F/E/R, Escape, Enter) intentionally still run because they
                        // are shortcuts that should work from inside an mf too.
                        //
                        // Use BOTH `e.target` (the React-retargeted source element) and
                        // `document.activeElement` (the actual focused element). React's
                        // shadow-DOM retargeting can be inconsistent across browsers, so
                        // either signal alone is unreliable; together they reliably
                        // catch every event originating from a focused mf.
                        var evtTarget = e.target;
                        var active = document.activeElement;
                        var fromInsideMf = (!!evtTarget &&
                            (evtTarget.tagName === 'MATH-FIELD' || !!((_a = evtTarget.closest) === null || _a === void 0 ? void 0 : _a.call(evtTarget, 'math-field')))) ||
                            (!!active && (active.tagName === 'MATH-FIELD' || !!((_b = active.closest) === null || _b === void 0 ? void 0 : _b.call(active, 'math-field'))));
                        // Escape — deselect the editor and blur
                        if (e.key === 'Escape') {
                            e.preventDefault();
                            e.stopPropagation();
                            setSelectedTool([]);
                            return;
                        }
                        // Alt+M — insert a new math block at the cursor
                        if (e.altKey && (e.key === 'm' || e.key === 'M') && !viewonly) {
                            e.preventDefault();
                            e.stopPropagation();
                            insertMath();
                            announce('Math expression inserted. Keypad open.');
                            return;
                        }
                        // Alt+K — toggle keypad open/closed (works whether math-field is focused or not)
                        if (e.altKey && (e.key === 'k' || e.key === 'K') && !viewonly) {
                            e.preventDefault();
                            e.stopPropagation();
                            var willOpen = !mathKeyboardVisibleRef.current;
                            setmathKeyboardVisible(willOpen);
                            announce(willOpen ? 'Math keypad opened.' : 'Math keypad closed.');
                            return;
                        }
                        // Alt+F / Alt+E / Alt+R — insert math structures into the focused math-field
                        var mfFocused = lastFocusedMathField.current;
                        if (e.altKey && mfFocused && !viewonly) {
                            var k = e.key.toLowerCase();
                            if (k === 'f') {
                                e.preventDefault();
                                e.stopPropagation();
                                (_d = (_c = mfFocused).executeCommand) === null || _d === void 0 ? void 0 : _d.call(_c, 'insert', '\\frac{#?}{#?}');
                                announce('Fraction inserted');
                                return;
                            }
                            if (k === 'e') {
                                e.preventDefault();
                                e.stopPropagation();
                                (_f = (_e = mfFocused).executeCommand) === null || _f === void 0 ? void 0 : _f.call(_e, 'insert', '#@^{#?}');
                                announce('Exponent inserted');
                                return;
                            }
                            if (k === 'r') {
                                e.preventDefault();
                                e.stopPropagation();
                                (_h = (_g = mfFocused).executeCommand) === null || _h === void 0 ? void 0 : _h.call(_g, 'insert', '\\sqrt{#?}');
                                announce('Square root inserted');
                                return;
                            }
                        }
                        // Backspace on an empty blank FIRST line: delete the line itself —
                        // spacer and `<br>` together — since the browser cannot (see
                        // blankFirstLineSpacerAtCaret). Re-running ensureBlankLineCarets
                        // afterwards covers consecutive blank lines at the top: the line
                        // that moves up into first place gets its own spacer.
                        if (e.key === 'Backspace' && !viewonly) {
                            var blankFirstLine = blankFirstLineSpacerAtCaret();
                            if (blankFirstLine) {
                                e.preventDefault();
                                var editor = editorRef.current;
                                blankFirstLine.spacer.remove();
                                blankFirstLine.br.remove();
                                ensureBlankLineCarets(editor);
                                // Anchor the caret in a text node, never on the editor itself:
                                // an element-anchored position is exactly what paints no caret.
                                var textWalker = document.createTreeWalker(editor, NodeFilter.SHOW_TEXT);
                                var firstText = null;
                                for (var n = textWalker.nextNode(); n; n = textWalker.nextNode()) {
                                    // Skip zero-length nodes: the editor carries a leading empty
                                    // text node, and a caret anchored in one has a 0x0 rect —
                                    // the very thing this whole helper exists to avoid.
                                    if (((_j = n.textContent) !== null && _j !== void 0 ? _j : '').length > 0) {
                                        firstText = n;
                                        break;
                                    }
                                }
                                var range = document.createRange();
                                if (firstText) {
                                    // After the spacer when it is one, so the caret sits on the
                                    // blank line rather than before it; otherwise at the start of
                                    // the text that just moved up.
                                    range.setStart(firstText, isSpacerText(firstText) ? firstText.length : 0);
                                }
                                else {
                                    range.setStart(editor, 0);
                                }
                                range.collapse(true);
                                var selection = window.getSelection();
                                selection === null || selection === void 0 ? void 0 : selection.removeAllRanges();
                                selection === null || selection === void 0 ? void 0 : selection.addRange(range);
                                editor.dispatchEvent(new Event('input', { bubbles: true }));
                                return;
                            }
                        }
                        // Backspace when the cursor sits immediately to the right of a math-field
                        // (or its spacer) — focus into the math-field at the end so the next
                        // backspace deletes content from within it.
                        if (e.key === 'Backspace' && !viewonly) {
                            var mfBefore = findMathFieldBeforeCursor();
                            if (mfBefore) {
                                e.preventDefault();
                                safeFocusMathField(mfBefore);
                                mfBefore.position = mfBefore.lastOffset;
                                lastFocusedMathField.current = mfBefore;
                            }
                        }
                        // ArrowLeft when the cursor sits immediately to the right of a
                        // math-field — enter the mf at lastOffset so arrow navigation
                        // flows naturally between editor text and inline math.
                        // Symmetric counterpart to the Backspace handler above.
                        if (e.key === 'ArrowLeft' &&
                            !viewonly &&
                            !e.shiftKey &&
                            !e.altKey &&
                            !e.metaKey &&
                            !e.ctrlKey &&
                            !fromInsideMf) {
                            var mfBefore = findMathFieldBeforeCursor();
                            if (mfBefore) {
                                e.preventDefault();
                                safeFocusMathField(mfBefore);
                                mfBefore.position = mfBefore.lastOffset;
                                lastFocusedMathField.current = mfBefore;
                                announce('Entering math expression');
                                return;
                            }
                        }
                        // ArrowRight when the cursor sits immediately to the left of a
                        // math-field — enter the mf at offset 0.
                        if (e.key === 'ArrowRight' &&
                            !viewonly &&
                            !e.shiftKey &&
                            !e.altKey &&
                            !e.metaKey &&
                            !e.ctrlKey &&
                            !fromInsideMf) {
                            var mfAfter = findMathFieldAfterCursor();
                            if (mfAfter) {
                                e.preventDefault();
                                safeFocusMathField(mfAfter);
                                mfAfter.position = 0;
                                lastFocusedMathField.current = mfAfter;
                                announce('Entering math expression');
                                return;
                            }
                        }
                        // Save/restore outer scroll position to prevent the browser's native
                        // "keep focused element in view" from jumping the scaled whiteboard.
                        var scrollEl = (_k = editorRef.current) === null || _k === void 0 ? void 0 : _k.closest('.customScrollbarContent');
                        if (scrollEl) {
                            var savedLeft_1 = scrollEl.scrollLeft;
                            var savedTop_1 = scrollEl.scrollTop;
                            requestAnimationFrame(function () {
                                scrollEl.scrollLeft = savedLeft_1;
                                scrollEl.scrollTop = savedTop_1;
                            });
                        }
                        // Enter key — insert a <br> line break (unless blocked)
                        if (e.key === 'Enter' || e.keyCode === 13) {
                            e.preventDefault();
                            e.stopPropagation();
                            if (!blockNewLine) {
                                insertLineBreak();
                            }
                            return false;
                        }
                        // Prevent cut shortcut (copy/paste are allowed — see onCopy/onPaste above)
                        if ((e.ctrlKey || e.metaKey) && ['x'].includes(e.key.toLowerCase())) {
                            e.preventDefault();
                            e.stopPropagation();
                            return false;
                        }
                    }, onBeforeInput: function (e) {
                        var _a, _b, _c, _d;
                        var inputEvent = e.nativeEvent;
                        // MathLive dispatches 'insertText' InputEvents on the math-field
                        // element that bubble to this contenteditable handler. Letting them
                        // through causes the color-wrap logic below to fire with a stale
                        // window.getSelection() and insert raw LaTeX text (e.g. \placeholder{})
                        // at the wrong location. Bail out whenever the event path passes
                        // through a math-field so we only handle genuine editorDiv input.
                        if (inputEvent.composedPath().some(function (el) { return (el === null || el === void 0 ? void 0 : el.tagName) === 'MATH-FIELD'; })) {
                            return;
                        }
                        // Continue the preceding resized run when the caret has fallen
                        // outside it. Runs BEFORE the color enforcement below because it
                        // is the more specific case: at this boundary the browser would
                        // give the new character the editor's base size, and the color
                        // branch would then wrap it in a fresh editor-level span that has
                        // no size either — the "text added at the end is smaller" report.
                        // Only the first character needs this; it lands inside the sized
                        // span, so every character after it inherits normally and takes
                        // the untouched paths below.
                        if (isTextUpdate &&
                            !viewonly &&
                            !isManipulative &&
                            inputEvent.inputType === 'insertText' &&
                            inputEvent.data) {
                            var sel = window.getSelection();
                            var range = (sel === null || sel === void 0 ? void 0 : sel.isCollapsed) && sel.rangeCount > 0 ? sel.getRangeAt(0) : null;
                            var sizeSpan = range ? sizedSpanBeforeCaret(range) : null;
                            // This branch returns before the maxCharacters gate at the very
                            // bottom of the handler, so honour the limit here rather than
                            // leaving a hole for whoever first puts a limit on a text box.
                            var atLimit = !!maxCharacters && getTotalCharacterCount() >= maxCharacters;
                            if (sel && range && sizeSpan && !atLimit) {
                                e.preventDefault();
                                range.deleteContents();
                                // Keep the picker's color as the color branch below would,
                                // nested inside the sized span so both survive.
                                var span = document.createElement('span');
                                span.style.color = ColorEnum[color] || ColorEnum['BLACK'];
                                span.textContent = inputEvent.data;
                                sizeSpan.appendChild(span);
                                var newRange = document.createRange();
                                newRange.setStart(span.firstChild, span.firstChild.textContent.length);
                                newRange.collapse(true);
                                sel.removeAllRanges();
                                sel.addRange(newRange);
                                // Trigger input handler so data syncs
                                (_a = editorRef.current) === null || _a === void 0 ? void 0 : _a.dispatchEvent(new Event('input', { bubbles: true }));
                                return;
                            }
                        }
                        // Enforce color picker color for newly typed text.
                        // When the caret is collapsed and a character is being inserted,
                        // check if the caret is already inside a span with the correct
                        // picker color. If not, preventDefault the native insert and
                        // manually place the character inside a new colored span.
                        // We must preventDefault because the browser's native handler
                        // ignores DOM changes made in onBeforeInput — it inserts the
                        // character at its own tracked position, bypassing our span.
                        if (isTextUpdate &&
                            !viewonly &&
                            !isManipulative &&
                            inputEvent.inputType === 'insertText' &&
                            inputEvent.data) {
                            var sel = window.getSelection();
                            if ((sel === null || sel === void 0 ? void 0 : sel.isCollapsed) && sel.rangeCount > 0) {
                                var pickerColor = ColorEnum[color] || ColorEnum['BLACK'];
                                // Normalize any CSS color string to "r,g,b" for reliable comparison.
                                // style.color returns rgb() while ColorEnum uses hex — direct === fails.
                                var toRGB = function (c) {
                                    var _a, _b, _c, _d;
                                    var s = c.trim().toLowerCase();
                                    // hex → r,g,b
                                    var hex = (_b = (_a = s.match(/^#([0-9a-f]{6})$/i)) === null || _a === void 0 ? void 0 : _a[1]) !== null && _b !== void 0 ? _b : (_d = (_c = s.match(/^#([0-9a-f]{3})$/i)) === null || _c === void 0 ? void 0 : _c[1]) === null || _d === void 0 ? void 0 : _d.replace(/./g, '$&$&');
                                    if (hex) {
                                        return "".concat(parseInt(hex.slice(0, 2), 16), ",").concat(parseInt(hex.slice(2, 4), 16), ",").concat(parseInt(hex.slice(4, 6), 16));
                                    }
                                    // rgb(r, g, b) → r,g,b
                                    var m = s.match(/rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)/);
                                    return m ? "".concat(m[1], ",").concat(m[2], ",").concat(m[3]) : s;
                                };
                                var normalizedPicker = toRGB(pickerColor);
                                // Check if caret is already inside a correctly-colored span
                                var node = sel.anchorNode;
                                var alreadyCorrect = false;
                                var coloredAncestorTagDbg = null;
                                var coloredAncestorColorDbg = null;
                                while (node && node !== editorRef.current) {
                                    if (node instanceof HTMLElement && node.style.color) {
                                        coloredAncestorTagDbg = node.tagName;
                                        coloredAncestorColorDbg = node.style.color;
                                        alreadyCorrect = toRGB(node.style.color) === normalizedPicker;
                                        break;
                                    }
                                    node = node.parentNode;
                                }
                                if (!alreadyCorrect) {
                                    e.preventDefault();
                                    var range = sel.getRangeAt(0);
                                    range.deleteContents();
                                    // Try to append to an adjacent color-A span instead of creating
                                    // a new one for every character. This keeps the DOM clean and
                                    // ensures subsequent characters stay in the correct color.
                                    var prevNode = range.startContainer.nodeType === Node.TEXT_NODE
                                        ? range.startContainer.parentElement
                                        : range.startContainer.childNodes[range.startOffset - 1];
                                    var prevSpan = prevNode instanceof HTMLSpanElement &&
                                        prevNode.style.color &&
                                        toRGB(prevNode.style.color) === normalizedPicker
                                        ? prevNode
                                        : null;
                                    if (prevSpan) {
                                        // Append into the span's existing trailing text node so
                                        // consecutive same-color chars share a single text node
                                        // and a single shaping run. Separate text nodes break
                                        // shaping and the browser renders duplicated letters
                                        // ("ee", "aa", etc.) with visibly different glyph
                                        // widths / metrics — reads as "the font style changed".
                                        var last = prevSpan.lastChild;
                                        var target = void 0;
                                        if (last && last.nodeType === Node.TEXT_NODE) {
                                            last.data = ((_b = last.data) !== null && _b !== void 0 ? _b : '') + inputEvent.data;
                                            target = last;
                                        }
                                        else {
                                            target = document.createTextNode(inputEvent.data);
                                            prevSpan.appendChild(target);
                                        }
                                        var newRange = document.createRange();
                                        newRange.setStart(target, ((_c = target.data) !== null && _c !== void 0 ? _c : '').length);
                                        newRange.collapse(true);
                                        sel.removeAllRanges();
                                        sel.addRange(newRange);
                                    }
                                    else {
                                        // Create a new colored span with the typed character
                                        var span = document.createElement('span');
                                        span.style.color = pickerColor;
                                        span.textContent = inputEvent.data;
                                        range.insertNode(span);
                                        // Place caret inside the span after the text
                                        var newRange = document.createRange();
                                        newRange.setStart(span.firstChild, span.firstChild.textContent.length);
                                        newRange.collapse(true);
                                        sel.removeAllRanges();
                                        sel.addRange(newRange);
                                    }
                                    // Trigger input handler so data syncs
                                    (_d = editorRef.current) === null || _d === void 0 ? void 0 : _d.dispatchEvent(new Event('input', { bubbles: true }));
                                    return;
                                }
                            }
                        }
                        // iPad virtual keyboard fires beforeinput with deleteContentBackward
                        // instead of a keydown Backspace event. Intercept it here so the
                        // browser doesn't natively delete/break the math-field element.
                        if (inputEvent.inputType === 'deleteContentBackward' && !viewonly) {
                            var mfBefore = findMathFieldBeforeCursor();
                            if (mfBefore) {
                                e.preventDefault();
                                safeFocusMathField(mfBefore);
                                mfBefore.position = mfBefore.lastOffset;
                                lastFocusedMathField.current = mfBefore;
                                return;
                            }
                        }
                        // iPad virtual keyboard fires beforeinput with insertParagraph
                        // or insertLineBreak instead of a keydown Enter event.
                        // Handle it the same way as the keydown Enter handler.
                        if ((inputEvent.inputType === 'insertParagraph' ||
                            inputEvent.inputType === 'insertLineBreak') &&
                            !viewonly) {
                            e.preventDefault();
                            if (!blockNewLine) {
                                insertLineBreak();
                            }
                            return;
                        }
                        if (viewonly || !maxCharacters)
                            return;
                        var currentCount = getTotalCharacterCount();
                        if (currentCount >= maxCharacters) {
                            e.preventDefault();
                            return false;
                        }
                    }, onInput: onChangeHandler, onClick: function (e) {
                        var target = e.target;
                        // When editing is locked, open links in a new tab directly
                        if (viewonly) {
                            var anchor = target.tagName === 'A' ? target : target.closest('a');
                            if (anchor) {
                                e.preventDefault();
                                var href = anchor.getAttribute('href');
                                if (href) {
                                    var url = href.startsWith('http://') || href.startsWith('https://')
                                        ? href
                                        : "https://".concat(href);
                                    window.open(url, '_blank', 'noopener,noreferrer');
                                }
                            }
                            else {
                                onClickWhenLocked === null || onClickWhenLocked === void 0 ? void 0 : onClickWhenLocked(index);
                            }
                            return;
                        }
                        var clickedAnchor = target.tagName === 'A' ? target : target.closest('a');
                        if (clickedAnchor) {
                            clickedAnchorRef.current = clickedAnchor;
                            setActiveLink(clickedAnchor.getAttribute('href'));
                        }
                        else {
                            clickedAnchorRef.current = null;
                            setActiveLink(null);
                        }
                    }, onSelect: function () {
                        if (viewonly)
                            return;
                        var sel = window.getSelection();
                        if (!sel || sel.rangeCount === 0)
                            return;
                        if (!sel.isCollapsed) {
                            lastSelection.current = sel.getRangeAt(0).cloneRange();
                        }
                        emitFormattingState(null);
                    }, style: {
                        fontSize: effectiveFontSize,
                        color: ((_p = data === null || data === void 0 ? void 0 : data.data) === null || _p === void 0 ? void 0 : _p.color) || '#000000',
                        textAlign: "".concat((_r = (_q = data === null || data === void 0 ? void 0 : data.data) === null || _q === void 0 ? void 0 : _q.alignment) !== null && _r !== void 0 ? _r : 'left'),
                        cursor: viewonly ? 'inherit' : 'text',
                        userSelect: viewonly ? 'text' : 'auto',
                        pointerEvents: viewonly ? 'none' : 'auto',
                        transform: disableTransform ? undefined : "rotate(".concat(((_s = data === null || data === void 0 ? void 0 : data.data) === null || _s === void 0 ? void 0 : _s.rotation) || 0, "deg)"),
                        transformOrigin: disableTransform ? undefined : 'center center',
                    }, autoFocus: isFocused && !viewonly }), mathKeyboardVisible && !viewonly && createMathKeyboard(), enableFocusPanel && isFocused && (_jsxs("div", { ref: focusOptionsRef, className: "editorFocusOptions", role: "dialog", "aria-modal": "true", "aria-label": t('text.mathEditor.focusOptionsLabel'), onClick: function (e) { return e.stopPropagation(); }, onKeyDown: function (e) {
                        if (e.key === 'Escape') {
                            e.preventDefault();
                            e.stopPropagation();
                            setSelectedTool([]);
                        }
                    }, style: {
                        position: 'absolute',
                        top: '0',
                        left: '0',
                        transform: focusPanelRight
                            ? "translate(calc(".concat(((_t = data === null || data === void 0 ? void 0 : data.data) === null || _t === void 0 ? void 0 : _t.lineWidth) || 300, "px + 12px), 0)")
                            : 'translate(calc(-100% - 12px), 0)',
                        background: '#fff',
                        border: '2px solid #293273',
                        borderRadius: 8,
                        padding: 6,
                        display: 'flex',
                        gap: 6,
                        zIndex: 10002,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.18)',
                    }, children: [!viewonly && (_jsx(ButtonComponent, { imgSrc: {
                                normal: './assets/images/icons/editPencilN.svg',
                                hover: './assets/images/icons/editPencilH.svg',
                                disable: './assets/images/icons/editPencilDN.svg',
                            }, defaultCSS: "editorFocusOption editorFocusOption-edit", normalCSS: "editorFocusOptionBtn", ariaLabel: t('text.mathEditor.enterEditing'), title: t('text.mathEditor.enterEditing'), clickHandler: function () {
                                editingAllowedRef.current = true;
                                setTimeout(function () {
                                    var el = editorRef.current;
                                    if (!el)
                                        return;
                                    el.focus({ preventScroll: true });
                                    var sel = window.getSelection();
                                    var range = document.createRange();
                                    range.selectNodeContents(el);
                                    range.collapse(false);
                                    sel === null || sel === void 0 ? void 0 : sel.removeAllRanges();
                                    sel === null || sel === void 0 ? void 0 : sel.addRange(range);
                                    announce(t('text.mathEditor.editingMode'));
                                }, 0);
                            } })), _jsx(ButtonComponent, { imgSrc: {
                                normal: './assets/images/icons/kebabN.svg',
                                hover: './assets/images/icons/kebabH.svg',
                                disable: './assets/images/icons/kebabN.svg',
                            }, defaultCSS: "editorFocusOption editorFocusOption-menu", normalCSS: "editorFocusOptionBtn", ariaLabel: t('text.mathEditor.openOptions'), ariaHaspopup: "menu", title: t('text.mathEditor.openOptions'), clickHandler: function () {
                                focusFromKeyboardRef.current = false;
                                if (typeof index !== 'number')
                                    return;
                                var el = editorRef.current;
                                if (!el)
                                    return;
                                var rect = el.getBoundingClientRect();
                                var manipId = "".concat(idPreffix, "_").concat(index);
                                var x = rect.left + rect.width / 2;
                                var y = rect.top + rect.height / 2;
                                setSelectedTool([manipId]);
                                setContextMenu({ x: x, y: y, manipulativeId: manipId });
                                announce(t('text.mathEditor.optionsOpened'));
                            } })] }))] }) }));
});
export default MathEditor;
